// ═══════════════════════════════════════════════════════════════
// Universal AI-Powered Document Intake
// Drop ANY files — invoices, photos, spreadsheets, PDFs, certs —
// AI classifies, extracts data, and routes everything.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Stack, Divider, Progress, SimpleGrid,
} from '../../components/gab/GabComponents';
import {
  IconUpload, IconCheck, IconX,
  IconAlertTriangle, IconLoader2, IconTruck,
  IconFileSpreadsheet, IconFileInvoice, IconPhoto,
  IconArchive, IconScale, IconChevronRight, IconChevronLeft,
  IconPlayerPlay, IconRefresh, IconUsers,
  IconFile, IconFileText, IconFileCheck, IconEye, IconPaperclip,
} from '@tabler/icons-react';
import * as XLSX from 'xlsx';
import * as pdfjsLib from 'pdfjs-dist';
import { notifications } from '../../lib/notifications';
import { useDebrisStore } from '../../store/debris-store';
import { useTampaDebrisStore } from '../../store/tampaDebrisStore';
import { useAuthStore } from '../../store/authStore';
import { createRecord, queryRecords, updateRecord, fetchRecord } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';
import { TDMS_COLUMN_MAP, TDMS_DEBRIS_CLASS_MAP, decodeFaDocs, encodeFaDocs } from '../../types/debris';
import type { DebrisType } from '../../types/debris';
import { invalidateTicketCache } from '../../hooks/useAllTickets';
import { callClaude } from '../../api/worker';
import { useNavigate } from 'react-router-dom';
import { uploadFileToV2, getV2FileDownloadUrl } from '../../lib/uploadFile';
import { detectSpreadsheetType, parseContractorSpreadsheet, parseInvoiceSpreadsheet } from '../../lib/bulkImport';

const FT = FIELDS.debrisLoadTickets;
const FTr = FIELDS.debrisTrucks;
const FDoc = FIELDS.debrisDocuments;
const FL = FIELDS.debrisForceAccountLabor;
const FE = FIELDS.debrisForceAccountEquipment;


// ═══════════════════════════════════════════════════════════
// CLASSIFICATION TYPES
// ═══════════════════════════════════════════════════════════

type DocType =
  | 'load_tickets' | 'transaction_detail' | 'truck_cert'
  | 'contractor_invoice' | 'sub_invoice'
  | 'employee_roster' | 'force_account'
  | 'contractor_register' | 'invoice_register'
  | 'sow_work_order' | 'inspection_report'
  | 'supporting_evidence'
  | 'unknown';

interface DocTypeConfig {
  label: string;
  icon: string;
  color: string;
  action: string;
}

const DOC_TYPES: Record<DocType, DocTypeConfig> = {
  load_tickets:         { label: 'TDMS Load Tickets',        icon: 'spreadsheet', color: '#0285C4',             action: 'Import as load tickets' },
  transaction_detail:   { label: 'Transaction Detail',       icon: 'spreadsheet', color: '#A627FF',             action: 'Parse billing data + enrich tickets' },
  truck_cert:           { label: 'Truck Certification',      icon: 'truck',       color: 'var(--color-success)', action: 'Register truck + store cert' },
  contractor_invoice:   { label: 'Contractor Invoice',       icon: 'invoice',     color: '#C68700',             action: 'AI extract + create invoice record' },
  sub_invoice:          { label: 'Sub-Contractor Invoice',   icon: 'invoice',     color: '#D97706',             action: 'AI extract + link to prime invoice' },
  employee_roster:      { label: 'Employee Roster',          icon: 'users',       color: '#4B3FFF',             action: 'Parse + store employee roster data' },
  force_account:        { label: 'Force Account (FA)',       icon: 'spreadsheet', color: '#0E7490',             action: 'AI extract + import FA records' },
  contractor_register:  { label: 'Contractor Register',     icon: 'users',       color: '#4B3FFF',             action: 'Bulk import contractors' },
  invoice_register:     { label: 'Invoice Register',        icon: 'invoice',     color: '#C68700',             action: 'Bulk import invoices' },
  sow_work_order:       { label: 'Scope of Work / Work Order', icon: 'file',      color: '#7C3AED',             action: 'Auto-link to contractor invoice' },
  inspection_report:    { label: 'Inspection / Sign-Off',    icon: 'file',        color: '#059669',             action: 'Auto-link to contractor invoice' },
  supporting_evidence:  { label: 'Supporting Docs',           icon: 'file',        color: '#03AC9E',             action: 'AI summarize + link to invoice' },
  unknown:              { label: 'Unknown',                  icon: 'unknown',     color: '#94a3b8',             action: 'Needs classification' },
};

const DOC_TYPE_OPTIONS = Object.entries(DOC_TYPES).map(([value, cfg]) => ({ value, label: cfg.label }));

// Confidence levels
type Confidence = 'high' | 'medium' | 'low';

const CONFIDENCE_COLORS: Record<Confidence, string> = {
  high: 'var(--color-success)',
  medium: '#C68700',
  low: '#94a3b8',
};

// ── localStorage helpers ──────────────────────────────────────
const reviewKey = (iid: string) => `debris-review-pending-${iid}`;

function persistQueue(iid: string, items: ReviewItem[]) {
  if (!iid) return;
  const pending = items.filter(i => i.status === 'pending');
  if (pending.length === 0) { localStorage.removeItem(reviewKey(iid)); return; }
  try {
    const data = pending.map(i => ({
      ...i,
      file: undefined as any,
      commit: undefined as any,
      fileName: (i as any).fileName ?? i.file?.name ?? '',
    }));
    localStorage.setItem(reviewKey(iid), JSON.stringify(data));
  } catch {}
}

function loadQueue(iid: string): any[] {
  try { const raw = localStorage.getItem(reviewKey(iid)); return raw ? JSON.parse(raw) : []; }
  catch { return []; }
}

function clearQueue(iid: string) { localStorage.removeItem(reviewKey(iid)); }

async function withConcurrency<T>(items: T[], limit: number, fn: (item: T, idx: number) => Promise<void>): Promise<void> {
  let cursor = 0;
  const next = async (): Promise<void> => {
    const i = cursor++;
    if (i >= items.length) return;
    await fn(items[i], i);
    await next();
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, next));
}

/**
 * Try to extract a date from a filename (e.g., "Ashbritt May 2025 inspection.pdf" → "2025-05").
 * Returns ISO date string or empty string.
 */
function extractDateFromFilename(filename: string): string {
  const name = filename.replace(/\.(pdf|docx?|xlsx?|png|jpe?g)$/i, '').replace(/[-_]/g, ' ');
  // Try MM/DD/YYYY or MM-DD-YYYY
  const mdy = name.match(/(\d{1,2})[/\-](\d{1,2})[/\-](\d{4})/);
  if (mdy) return `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`;
  // Try YYYY-MM-DD
  const iso = name.match(/(\d{4})[/\-](\d{1,2})[/\-](\d{1,2})/);
  if (iso) return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`;
  // Try "Month YYYY" (e.g., "May 2025")
  const months: Record<string, string> = { jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06', jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12' };
  const monthYear = name.toLowerCase().match(/\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s*(\d{4})\b/);
  if (monthYear) {
    const mon = months[monthYear[1].slice(0, 3)] || '01';
    return `${monthYear[2]}-${mon}-15`; // mid-month as representative date
  }
  return '';
}

/**
 * Find the best invoice match for a document by contractor + date range.
 * Returns the invoice whose period contains the document date, or undefined.
 */
function matchInvoiceByPeriod(
  invoices: Array<{ id: string; periodStart: string; periodEnd: string }>,
  docDate: string,
): typeof invoices[0] | undefined {
  if (!docDate) return undefined;
  return invoices.find(inv => {
    if (!inv.periodStart || !inv.periodEnd) return false;
    return docDate >= inv.periodStart && docDate <= inv.periodEnd;
  });
}

/**
 * Normalize a contractor/vendor name for fuzzy matching.
 * Strips punctuation, common corporate suffixes, and extra whitespace
 * so "Ashbritt Environmental" matches "Ashbritt, Inc." and "AshBritt LLC".
 */
function normalizeContractorName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, ' ')
    .replace(/\b(inc|llc|lc|corp|corporation|ltd|co|company|incorporated|environmental|env|services|service|construction|contractors?|contracting|group|enterprises?|solutions|management|systems|industries|industry|international|national|associates?|partners?|holdings?|resources?)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Find a contractor in the existing list by fuzzy name match.
 * Returns the best match or undefined.
 */
function findContractorByName(name: string, contractors: Array<{ id: string | number; companyName: string }>): typeof contractors[0] | undefined {
  if (!name) return undefined;
  const normInput = normalizeContractorName(name);
  // 1. Exact normalized match
  const exact = contractors.find(c => normalizeContractorName(c.companyName) === normInput);
  if (exact) return exact;
  // 2. One normalized name contains the other (handles "Ashbritt" ↔ "Ashbritt Environmental")
  if (normInput) {
    const contains = contractors.find(c => {
      const normStored = normalizeContractorName(c.companyName);
      return normStored && (normStored.includes(normInput) || normInput.includes(normStored));
    });
    if (contains) return contains;
  }
  // 3. Fallback: original bidirectional substring (handles edge cases like abbreviations)
  const lower = name.toLowerCase();
  return contractors.find(c =>
    c.companyName.toLowerCase().includes(lower) || lower.includes(c.companyName.toLowerCase())
  );
}

function computeOcrConfidence(method: 'pdf-text' | 'claude-vision', textLen: number): 'high' | 'medium' | 'low' {
  if (method === 'pdf-text') return textLen >= 800 ? 'high' : textLen >= 200 ? 'medium' : 'low';
  return 'medium';
}

// ═══════════════════════════════════════════════════════════
// FILE CLASSIFICATION
// ═══════════════════════════════════════════════════════════

interface ClassifiedFile {
  file: File;
  detectedType: DocType;
  overrideType?: DocType;
  confidence: Confidence;
  reason: string;
  preview?: string; // First few rows for spreadsheets
  status: 'pending' | 'processing' | 'done' | 'error';
  result?: string;
}

/** Classify a spreadsheet by inspecting column headers */
async function classifySpreadsheet(file: File): Promise<{ type: DocType; confidence: Confidence; reason: string; preview: string }> {
  const data = await readFileAsArray(file);
  const wb = XLSX.read(data, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const json = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { raw: false, defval: '', header: 1 });

  // Build preview (first 3 rows)
  const previewRows = json.slice(0, 4);
  const preview = previewRows.map((row: any) =>
    Array.isArray(row) ? row.slice(0, 8).join(' | ') : Object.values(row).slice(0, 8).join(' | ')
  ).join('\n');

  // Get headers from first row
  const headers = json[0];
  const headerStr = Array.isArray(headers) ? headers.join(',').toLowerCase() : '';

  // Transaction detail: has "TicketNo" or "Service" + "Rate"/"Total" — check BEFORE load_tickets
  // because billing files also have Ticket # and CYD columns
  if ((headerStr.includes('ticketno') || headerStr.includes('service')) && (headerStr.includes('rate') || headerStr.includes('total')))
    return { type: 'transaction_detail', confidence: 'high', reason: 'Headers match transaction detail format (Service, Rate, Total)', preview };

  // TDMS ticket data: must have "Debris Class" — distinguishes from billing exports
  if (headerStr.includes('ticket') && headerStr.includes('debris class'))
    return { type: 'load_tickets', confidence: 'high', reason: 'Headers match TDMS ticket export (Ticket #, Debris Class)', preview };

  // Contractor register / Invoice register — delegate to shared bulkImport detector
  const rawHeaders = Array.isArray(headers) ? (headers as unknown as string[]) : [];
  const detected = detectSpreadsheetType(rawHeaders);
  if (detected === 'contractor_register')
    return { type: 'contractor_register', confidence: 'high', reason: 'Headers match contractor register pattern', preview };
  if (detected === 'invoice_register')
    return { type: 'invoice_register', confidence: 'high', reason: 'Headers match invoice register pattern', preview };

  // Employee roster: has name + (hourly rate OR ERC district) — check BEFORE force_account
  // since force_account also has name+id+hours
  if (
    (headerStr.includes('employee name') || headerStr.includes('full name') || (headerStr.includes('name') && !headerStr.includes('contractor'))) &&
    (headerStr.includes('hourly rate') || headerStr.includes('erc') || headerStr.includes('base rate') || headerStr.includes('rate ($/hr)') || headerStr.includes('hr rate'))
  )
    return { type: 'employee_roster', confidence: 'high', reason: 'Headers match employee roster (Name, Hourly Rate / ERC District)', preview };
  // Roster by classification+shift pattern
  if (headerStr.includes('name') && headerStr.includes('classification') && (headerStr.includes('shift') || headerStr.includes('erc')))
    return { type: 'employee_roster', confidence: 'high', reason: 'Headers match employee roster (Name, Classification, Shift)', preview };

  // Force Account Excel: check all sheets for FA labor/equipment headers
  for (const sheetName of wb.SheetNames) {
    const wsN = wb.Sheets[sheetName];
    if (!wsN['!ref']) continue;
    // Only decode first row for speed
    const firstRow = XLSX.utils.sheet_to_json<any[]>(wsN, { raw: false, defval: '', header: 1 })[0] || [];
    const hStr = Array.isArray(firstRow) ? firstRow.join(',').toLowerCase() : '';
    if (
      (hStr.includes('employee name') || (hStr.includes('name') && hStr.includes('id'))) &&
      (hStr.includes('service date') || hStr.includes('date')) &&
      (hStr.includes('hours') || hStr.includes('hrs'))
    ) {
      return { type: 'force_account', confidence: 'high', reason: `Sheet "${sheetName}" has Force Account labor headers (Employee Name, Date, Hours)`, preview };
    }
  }

  // Filename-based fallback for spreadsheets
  const name = file.name.toLowerCase();
  if (name.includes('fa labor') || name.includes('force account') || name.includes('fal-fae') || name.includes('fae_claim') || name.includes('fa_labor') || name.includes('timesheet'))
    return { type: 'force_account', confidence: 'medium', reason: `Filename matches Force Account pattern: ${file.name}`, preview };
  if (name.includes('tic1') || name.includes('loadticket') || name.includes('ticket data'))
    return { type: 'load_tickets', confidence: 'medium', reason: `Filename suggests ticket data: ${file.name}`, preview };
  if (name.includes('det') || name.includes('transaction'))
    return { type: 'transaction_detail', confidence: 'medium', reason: `Filename suggests transaction detail: ${file.name}`, preview };
  if (name.includes('roster') || name.includes('personnel') || name.includes('employee'))
    return { type: 'employee_roster', confidence: 'medium', reason: `Filename suggests employee roster: ${file.name}`, preview };

  // Generic spreadsheet — supporting evidence (archive with description)
  return { type: 'supporting_evidence', confidence: 'low', reason: 'Unrecognized spreadsheet format — will archive as supporting documentation', preview };
}

/** Classify a PDF by filename patterns + size heuristics */
function classifyPDF(file: File): { type: DocType; confidence: Confidence; reason: string } {
  const name = file.name.toLowerCase();
  const size = file.size;

  // Force Account ANNEX E-3 forms: ERC logs, department forms
  if (/\berc\b/.test(name) || name.startsWith('erc') || name.includes('erc1') || name.includes('erc2') || name.includes('erc '))
    return { type: 'force_account', confidence: 'high', reason: 'Filename matches ERC log pattern (ANNEX E-3 form)' };
  if (name.includes('annex e') || name.includes('annex-e') || name.includes('annexe'))
    return { type: 'force_account', confidence: 'high', reason: 'Filename indicates ANNEX E-3 Force Account form' };
  // Tampa department FA forms: P&R, NCA, HR, Misc — common in Tampa Milton/Helene packages
  if ((name === 'p&r.pdf' || name === 'nca.pdf' || /^hr\d*\.pdf$/.test(name) || name === 'misc.pdf' || name === 'ia.pdf') && size > 100_000)
    return { type: 'force_account', confidence: 'medium', reason: `Filename matches Tampa department FA form pattern: ${file.name}` };

  // Truck certs: Tampa{Contractor}{Storm}_{TruckNo}_{Capacity}.pdf
  if (name.includes('tampa') && (name.includes('helene') || name.includes('milton') || name.includes('city')) && /\d+[_-][\d.]+\.pdf$/.test(name))
    return { type: 'truck_cert', confidence: 'high', reason: 'Filename matches truck cert pattern (Tampa*_{TruckNo}_{CY})' };

  // TDMS web reports contain load ticket DATA — import as load_tickets.
  if (name.includes('tdmswebreport') || name.includes('webreport') || (name.includes('tdms') && name.includes('report')))
    return { type: 'load_tickets', confidence: 'high', reason: 'Filename matches TDMS web report — contains load ticket data' };
  // Load ticket PDFs: various naming patterns including spaces
  if (name.includes('load ticket') || name.includes('loadticket') || name.includes('load_ticket') || name.includes('load-ticket') || /tic\d+/.test(name) || name.includes('debris load') || name.includes('debris ticket'))
    return { type: 'load_tickets', confidence: 'high', reason: `Filename matches load ticket data pattern: ${file.name}` };
  // Other TDMS-named PDFs
  if (name.includes('tdms'))
    return { type: 'supporting_evidence', confidence: 'medium', reason: 'Filename contains TDMS — archived as supporting documentation' };

  // Force Account PDFs identified by content keywords in filename
  if (name.includes('forcelabor') || name.includes('force-labor') || name.includes('laborsummary') ||
      name.includes('labor-summary') || name.includes('forceaccount') || name.includes('force-account') ||
      name.includes('labordetail') || name.includes('labor-detail') || name.includes('equipmentsummary') ||
      name.includes('equipment-summary'))
    return { type: 'force_account', confidence: 'medium', reason: `Filename matches Force Account content pattern: ${file.name}` };

  // Scope of Work / Work Order documents
  if (/\bsow\b|scope.?of.?work|work.?order|statement.?of.?work|\bproposal\b/.test(name))
    return { type: 'sow_work_order', confidence: 'high', reason: `Filename matches Scope of Work pattern: ${file.name}` };

  // Inspection / Completion / Sign-off documents
  if (/inspection|sign.?off|completion|closeout|punch.?list|final.?report|acceptance/.test(name))
    return { type: 'inspection_report', confidence: 'high', reason: `Filename matches inspection/completion pattern: ${file.name}` };

  // Sub-invoice: filename indicates subcontractor
  if (/sub.{0,4}inv|subinv|subcontract|sub-inv/.test(name))
    return { type: 'sub_invoice', confidence: 'high', reason: `Filename matches sub-contractor invoice pattern: ${file.name}` };

  // Contractor invoice: common invoice/billing filename patterns
  if (name.includes('inv') || name.includes('invoice'))
    return { type: 'contractor_invoice', confidence: 'high', reason: `Filename contains invoice keyword: ${file.name}` };
  if (name.includes('bill') || name.includes('billing') || name.includes('statement') || name.includes('stmt'))
    return { type: 'contractor_invoice', confidence: 'medium', reason: `Filename matches billing pattern: ${file.name}` };
  if (name.includes('contract') && !name.includes('subcontract'))
    return { type: 'contractor_invoice', confidence: 'medium', reason: `Filename contains "contract" — likely contractor billing document: ${file.name}` };
  if (name.includes('payment') || name.includes('pay-app') || name.includes('payapp') || name.includes('requisition'))
    return { type: 'contractor_invoice', confidence: 'medium', reason: `Filename matches payment application pattern: ${file.name}` };

  // Everything else → supporting evidence
  return { type: 'supporting_evidence', confidence: 'low', reason: 'Unrecognized PDF — archived as supporting documentation' };
}

/** Classify an image file */
function classifyImage(file: File): { type: DocType; confidence: Confidence; reason: string } {
  return { type: 'supporting_evidence', confidence: 'medium', reason: `Image file: ${file.type || 'unknown format'} — archived as supporting documentation` };
}

/** Master classifier — dispatches by file extension */
async function classifyFile(file: File): Promise<Omit<ClassifiedFile, 'status' | 'result'>> {
  const name = file.name.toLowerCase();
  const isSpreadsheet = name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.csv');
  const isPDF = name.endsWith('.pdf');
  const isImage = /\.(jpg|jpeg|png|gif|webp|bmp|tiff?)$/i.test(name);

  if (isSpreadsheet) {
    try {
      const result = await classifySpreadsheet(file);
      return { file, detectedType: result.type, confidence: result.confidence, reason: result.reason, preview: result.preview };
    } catch (err) {
      return { file, detectedType: 'unknown', confidence: 'low', reason: `Error parsing spreadsheet: ${err instanceof Error ? err.message : 'unknown'}` };
    }
  }

  if (isPDF) {
    const result = classifyPDF(file);
    return { file, detectedType: result.type, confidence: result.confidence, reason: result.reason };
  }

  if (isImage) {
    const result = classifyImage(file);
    return { file, detectedType: result.type, confidence: result.confidence, reason: result.reason };
  }

  // Unknown file type
  return { file, detectedType: 'unknown', confidence: 'low', reason: `Unrecognized file type: ${file.name}` };
}

// ═══════════════════════════════════════════════════════════
// ICONS + HELPERS
// ═══════════════════════════════════════════════════════════

function getTypeIcon(type: DocType, size = 16) {
  switch (type) {
    case 'load_tickets':        return <IconFileSpreadsheet size={size} color="#0285C4" />;
    case 'transaction_detail':  return <IconFileSpreadsheet size={size} color="#A627FF" />;
    case 'truck_cert':          return <IconTruck size={size} color="var(--color-success)" />;
    case 'contractor_invoice':  return <IconFileInvoice size={size} color="#C68700" />;
    case 'sub_invoice':         return <IconFileInvoice size={size} color="#D97706" />;
    case 'employee_roster':     return <IconUsers size={size} color="#4B3FFF" />;
    case 'force_account':       return <IconFileSpreadsheet size={size} color="#0E7490" />;
    case 'contractor_register': return <IconUsers size={size} color="#4B3FFF" />;
    case 'invoice_register':    return <IconFileInvoice size={size} color="#C68700" />;
    case 'sow_work_order':      return <IconFileText size={size} color="#7C3AED" />;
    case 'inspection_report':   return <IconFileCheck size={size} color="#059669" />;
    case 'supporting_evidence': return <IconArchive size={size} color="#03AC9E" />;
    default:                    return <IconAlertTriangle size={size} color="#94a3b8" />;
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const fmtMoney = (v: number) =>
  v > 0 ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00';

// Tampa ArcGIS geocoder → Nominatim fallback
// Returns [lat, lng] or null if unresolvable
async function geocodeAddress(query: string): Promise<[number, number] | null> {
  // Clamp Tampa area bounding box
  const TAMPA_BBOX = { west: -82.9, south: 27.7, east: -82.1, north: 28.3 };

  // Primary: Tampa ArcGIS locator (most accurate for Tampa streets)
  try {
    const params = new URLSearchParams({
      SingleLine: query.includes('Tampa') || query.includes('FL') ? query : `${query}, Tampa, FL`,
      maxLocations: '1',
      outFields: '',
      f: 'json',
    });
    const url = `https://arcgis.tampagov.net/arcgis/rest/services/GeocodeServices/TampaLocator/GeocodeServer/findAddressCandidates?${params}`;
    const res = await fetch(url, { signal: AbortSignal.timeout(5000) });
    if (res.ok) {
      const data = await res.json();
      const c = data?.candidates?.[0];
      if (c && c.score >= 70) {
        const lat = c.location?.y;
        const lng = c.location?.x;
        if (lat && lng && lat >= TAMPA_BBOX.south && lat <= TAMPA_BBOX.north && lng >= TAMPA_BBOX.west && lng <= TAMPA_BBOX.east) {
          return [lat, lng];
        }
      }
    }
  } catch { /* fall through to Nominatim */ }

  // Fallback: Nominatim (rate-limited — only hit if Tampa ArcGIS fails)
  try {
    const params = new URLSearchParams({
      q: query.includes('Tampa') || query.includes('FL') ? query : `${query}, Tampa, FL`,
      format: 'json',
      limit: '1',
      bounded: '1',
      viewbox: `${TAMPA_BBOX.west},${TAMPA_BBOX.north},${TAMPA_BBOX.east},${TAMPA_BBOX.south}`,
      countrycodes: 'us',
    });
    const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
      signal: AbortSignal.timeout(5000),
      headers: { 'Accept-Language': 'en', 'User-Agent': 'OGEMC-Tampa/1.0' },
    });
    if (res.ok) {
      const data = await res.json();
      if (data?.[0]) {
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        if (lat && lng) return [lat, lng];
      }
    }
  } catch { /* give up */ }

  return null;
}

// Road ownership match — hits the same Tampa ArcGIS endpoint the debris map uses.
// Queries a 15m buffer around the ticket GPS point and returns the nearest road's
// name, owner code, owner name, and road class.
const ARCGIS_ROAD_URL = 'https://arcgis.tampagov.net/arcgis/rest/services/OpenData/StreetsandAddresses/MapServer/2/query';
const ROAD_OWNER_NAMES: Record<string, string> = {
  '1': 'City of Tampa',
  '2': 'State (FDOT)',
  '-3': 'Hillsborough County',
  '-1': 'Federal/Private',
};

interface RoadMatch {
  roadName: string;
  roadOwner: string;     // raw OWNEDBY value e.g. "1"
  roadOwnerName: string; // human label e.g. "City of Tampa"
  roadClass: string;
}

async function matchRoadForCoords(lat: number, lng: number): Promise<RoadMatch | null> {
  try {
    const params = new URLSearchParams({
      geometry: JSON.stringify({ x: lng, y: lat, spatialReference: { wkid: 4326 } }),
      geometryType: 'esriGeometryPoint',
      inSR: '4326',
      spatialRel: 'esriSpatialRelIntersects',
      distance: '50',
      units: 'esriSRUnit_Meter',
      outFields: 'FULLNAME,OWNEDBY,ROADCLASS',
      outSR: '4326',
      f: 'json',
      resultRecordCount: '3',
    });
    const res = await fetch(`${ARCGIS_ROAD_URL}?${params}`, { signal: AbortSignal.timeout(6000) });
    if (!res.ok) return null;
    const data = await res.json();
    const features: any[] = data?.features ?? [];
    if (!features.length) return null;
    // Pick first result (ArcGIS returns them ordered by proximity when using point+distance)
    const attrs = features[0].attributes ?? features[0].properties ?? {};
    const ownerKey = String(attrs.OWNEDBY ?? '');
    return {
      roadName: attrs.FULLNAME || '',
      roadOwner: ownerKey,
      roadOwnerName: ROAD_OWNER_NAMES[ownerKey] || '',
      roadClass: attrs.ROADCLASS || '',
    };
  } catch {
    return null;
  }
}

// ── Styling ──────────────────────────────────────────────
const thStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: '#64748b', textAlign: 'left',
  padding: '8px 10px', whiteSpace: 'nowrap',
  position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc',
};
const tdStyle: React.CSSProperties = { fontSize: 12, padding: '6px 10px', textAlign: 'left', verticalAlign: 'middle' };

// ═══════════════════════════════════════════════════════════
// PROCESSING RESULTS
// ═══════════════════════════════════════════════════════════

interface ProcessResult {
  file: string;
  type: DocType;
  action: string;
  recordsCreated: number;
  error?: string;
}

// ── Employee roster record (parsed from employee_roster spreadsheet) ──
interface RosterEmployee {
  name: string;
  id: string;
  ercDistrict: string;
  department: string;
  classification: string;
  shift: string;
  hourlyRate: number;
  fleetNumber?: string;
}

// ── Review queue item (AI-extracted docs held for human review before DB write) ──

interface ReviewItem {
  id: string;
  file: File;
  docType: 'contractor_invoice' | 'sub_invoice' | 'force_account' | 'supporting_evidence' | 'sow_work_order' | 'inspection_report' | 'contractor_register' | 'invoice_register';
  status: 'pending' | 'approved' | 'rejected';
  // Editable fields
  vendor: string;
  invoiceNumber: string;
  invoiceDate: string;
  totalAmount: string;
  lineItems: any[];
  qualityScore: string;
  missingItems: string[];
  summary: string;
  laborEntries: any[];
  equipEntries: any[];
  // Metadata
  warnings: string[];
  extractionMethod: 'pdf-text' | 'claude-vision';
  extractionScore?: number;        // 0-100 numeric confidence from Claude
  ocrConfidence?: 'high' | 'medium' | 'low';
  // Serializable commit params for localStorage restoration:
  capturedContractorId?: string;
  capturedQualityMeta?: string;
  capturedDisasterNum?: string;
  capturedFemaCat?: string;
  fileName?: string;
  previewDocId?: string;  // V2 document UUID for PDF preview on session restore
  // sub_invoice
  primeInvoiceId?: string;
  // supporting_evidence
  selectedInvoiceId?: string;
  selectedSubType?: string;
  evidenceSummary?: string;
  flaggedAmount?: number;
  // force_account roster matching
  rosterUnmatched?: string[];
  // DB write — called with current field values when user approves
  commit: (item: ReviewItem) => Promise<void>;
}

// ── pdfjs text extraction (for digital PDFs — skip Vision for cleaner text) ──

/** Extract text from a PDF, returning per-page text with page numbers */
async function extractPdfPages(file: File): Promise<Array<{ page: number; text: string }>> {
  if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url,
    ).href;
  }
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const pages: Array<{ page: number; text: string }> = [];
    for (let i = 1; i <= pdfDoc.numPages; i++) {
      const page = await pdfDoc.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = (textContent.items as any[])
        .filter((item: any) => item.str)
        .map((item: any) => item.str)
        .join(' ');
      if (pageText.trim()) pages.push({ page: i, text: pageText });
    }
    return pages;
  } catch { return []; }
}

async function extractPdfText(file: File): Promise<string> {
  if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url,
    ).href;
  }
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const parts: string[] = [];
    for (let i = 1; i <= pdfDoc.numPages; i++) {
      const page = await pdfDoc.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = (textContent.items as any[])
        .filter((item: any) => item.str)
        .map((item: any) => item.str)
        .join(' ');
      if (pageText.trim()) parts.push(pageText);
    }
    return parts.join('\n\n').trim();
  } catch { return ''; }
}

// ── Normalize raw Claude invoice response into a clean object ──

function normalizeInvoiceAI(raw: string): any {
  const jsonMatch = raw.match(/\{[\s\S]*\}/);
  if (!jsonMatch) return null;
  let result: any;
  try {
    result = JSON.parse(jsonMatch[0]);
  } catch {
    let s = jsonMatch[0];
    s = s.replace(/,\s*"[^"]*"?\s*$/, '');
    s = s.replace(/,\s*\{[^}]*$/, '');
    s = s.replace(/,\s*"[^"]*":\s*[^,}\]]*$/, '');
    const op = (s.match(/\[/g) || []).length - (s.match(/\]/g) || []).length;
    for (let i = 0; i < op; i++) s += ']';
    const ob = (s.match(/\{/g) || []).length - (s.match(/\}/g) || []).length;
    for (let i = 0; i < ob; i++) s += '}';
    try { result = JSON.parse(s); } catch { return null; }
  }
  if (!result) return null;
  if (result.vendor && typeof result.vendor === 'object')
    result.vendor = result.vendor.name || result.vendor.company || JSON.stringify(result.vendor);
  result.vendor = String(result.vendor || '').trim();
  if (typeof result.totalAmount === 'string')
    result.totalAmount = parseFloat(result.totalAmount.replace(/[$,]/g, '')) || 0;
  result.totalAmount = Number(result.totalAmount) || 0;
  if (result.invoiceDate) {
    const mdy = String(result.invoiceDate).match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (mdy) result.invoiceDate = `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`;
  }
  if (typeof result.qualityScore === 'number')
    result.qualityScore = result.qualityScore >= 90 ? 'complete' : result.qualityScore >= 60 ? 'needs_revision' : 'reject';
  else if (typeof result.qualityScore === 'object') {
    const sc = result.qualityScore.score || result.qualityScore.value || 0;
    result.qualityScore = sc >= 90 ? 'complete' : sc >= 60 ? 'needs_revision' : 'reject';
  }
  if (!['complete', 'needs_revision', 'reject'].includes(result.qualityScore)) result.qualityScore = 'needs_revision';
  if (!Array.isArray(result.missingItems)) result.missingItems = [];
  if (typeof result.summary === 'object') result.summary = JSON.stringify(result.summary);
  if (typeof result.confidenceScore !== 'number') {
    // Infer from qualityScore if not provided
    result.confidenceScore = result.qualityScore === 'complete' ? 90 : result.qualityScore === 'needs_revision' ? 60 : 30;
  }
  result.confidenceScore = Math.max(0, Math.min(100, Math.round(result.confidenceScore)));
  return result;
}

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════

export default function PackageProcessor() {
  const navigate = useNavigate();
  const { activeIncident, user } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const { contractors, fetchContractors, createContractor, trucks, fetchTrucks, createTruck, updateTruck, createDocument, uploadDocumentFile, fetchDisposalSites } = useDebrisStore();
  const { createImportLog, createReconciliation } = useTampaDebrisStore();

  // ── State ─────────────────────────────────────────────
  const [step, setStep] = useState<'upload' | 'classify' | 'process' | 'review' | 'results'>('upload');
  const [files, setFiles] = useState<ClassifiedFile[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<'individual' | 'bulk'>('individual');
  const [pendingCount, setPendingCount] = useState(0);

  // Configuration (shown inline in classify step)
  const [selectedContractor, setSelectedContractor] = useState<string | null>(null);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');

  // Processing
  const [processProgress, setProcessProgress] = useState(0);
  const [processMessage, setProcessMessage] = useState('');
  const [results, setResults] = useState<ProcessResult[]>([]);
  const abortRef = useRef(false);

  // Review queue
  const [reviewQueue, setReviewQueue] = useState<ReviewItem[]>([]);
  const [reviewIdx, setReviewIdx] = useState(0);
  const [reviewPdfUrl, setReviewPdfUrl] = useState<string | null>(null);
  const [faLaborDocSaving, setFaLaborDocSaving] = useState<string | null>(null); // "rowIdx:slot"

  // Incident invoices (for sub_invoice + supporting_evidence selectors)
  const [incidentInvoices, setIncidentInvoices] = useState<Array<{id: string; number: string; vendor: string; amount: number; contractorId: string; periodStart: string; periodEnd: string}>>([]);

  // Load contractors + trucks
  useEffect(() => {
    fetchContractors();
    fetchTrucks();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load contractor invoices for this incident (used in review queue selectors)
  useEffect(() => {
    if (!incidentId) return;
    const FI = FIELDS.debrisInvoices;
    const load = async () => {
      try {
        const res = await queryRecords<Record<string, unknown>>(TABLES.debrisInvoices, {
          limit: 500, offset: 0,
          filters: [{ field: FI.incidentId, operator: 'eq' as const, value: incidentId }],
        });
        const invoices = res.records
          .filter(r => {
            if (String(r[FI.incidentId] || '') !== incidentId) return false;
            // Exclude sub-invoices from the prime invoice selector
            return String(r[FI.status] || '') !== 'sub_invoice';
          })
          .map(r => ({
            id: String((r as any).record_id || ''),
            number: String(r[FI.invoiceNumber] || ''),
            vendor: String(r[FI.reviewerNotes] || '').match(/"vendor":"([^"]+)"/)?.[1] || '',
            amount: parseFloat(String(r[FI.totalAmount] || '0')) || 0,
            contractorId: String(r[FI.contractorId] || ''),
            periodStart: String(r[FI.periodStart] || ''),
            periodEnd: String(r[FI.periodEnd] || ''),
          }));
        setIncidentInvoices(invoices);
      } catch { /* non-critical */ }
    };
    load();
  }, [incidentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for persisted pending items from last session
  useEffect(() => {
    if (!incidentId) return;
    const persisted = loadQueue(incidentId);
    setPendingCount(persisted.filter((i: any) => i.status === 'pending').length);
  }, [incidentId]);

  // Warn before browser unload/navigation when review queue has uncommitted items
  useEffect(() => {
    const hasUnsaved = step === 'review' && reviewQueue.length > 0;
    if (!hasUnsaved) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [step, reviewQueue.length]);

  // PDF object URL for review viewer — revoke on change to avoid memory leak
  useEffect(() => {
    if (step !== 'review' || reviewQueue.length === 0) return;
    const item = reviewQueue[reviewIdx];
    if (!item) return;
    if (!(item.file instanceof File)) {
      if (item.previewDocId) {
        getV2FileDownloadUrl(item.previewDocId).then(url => setReviewPdfUrl(url)).catch(() => setReviewPdfUrl(null));
      } else {
        setReviewPdfUrl(null);
      }
      return;
    }
    const url = URL.createObjectURL(item.file);
    setReviewPdfUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [reviewIdx, step]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateReviewItem = (id: string, updates: Partial<ReviewItem>) =>
    setReviewQueue(prev => {
      const next = prev.map(item => item.id === id ? { ...item, ...updates } : item);
      persistQueue(incidentId, next);
      return next;
    });

  // Upload a supporting doc for a pre-commit FA entry and store the doc ID in the entry object
  async function saveFaDocToEntry(
    itemId: string, entries: any[], rowIdx: number, slot: string, file: File,
    updateFn: (entries: any[]) => void
  ) {
    const { uploadFileToV2 } = await import('../../lib/uploadFile');
    const result = await uploadFileToV2(file, `fa-doc-${itemId}-${rowIdx}-${slot}`);
    const docId = (result as any).documentId || (result as any).attachmentId || '';
    const updated = [...entries];
    updated[rowIdx] = { ...updated[rowIdx], [slot]: docId };
    updateFn(updated);
  }

  const contractorOptions = useMemo(() =>
    contractors.map(c => ({ value: String(c.id), label: c.companyName || `Contractor #${c.id}` })),
    [contractors],
  );

  // ── Derived ───────────────────────────────────────────
  const getEffType = (cf: ClassifiedFile): DocType => cf.overrideType || cf.detectedType;

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    files.forEach(cf => {
      const t = getEffType(cf);
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }, [files]);

  const needsContractor = false; // Contractor is optional — AI extracts from docs, and records work without it

  // ── File handling ─────────────────────────────────────
  const handleFiles = useCallback(async (fileList: FileList) => {
    const incoming: ClassifiedFile[] = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      if (file.name.startsWith('.')) continue; // skip hidden files
      incoming.push({ file, detectedType: 'unknown', confidence: 'low', reason: 'Classifying...', status: 'pending' });
    }
    setFiles(incoming);
    setStep('classify');

    // Classify each file (spreadsheets are async due to XLSX parsing)
    const classified = await Promise.all(
      incoming.map(async (cf) => {
        const result = await classifyFile(cf.file);
        return { ...cf, ...result, status: 'pending' as const };
      })
    );
    setFiles(classified);
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fl = e.target.files;
    if (fl && fl.length > 0) handleFiles(fl);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const fl = e.dataTransfer.files;
    if (fl && fl.length > 0) handleFiles(fl);
  };

  const handleOverride = (index: number, newType: string) => {
    setFiles(prev => {
      const next = [...prev];
      next[index] = { ...next[index], overrideType: newType as DocType };
      return next;
    });
  };

  // ═══════════════════════════════════════════════════════
  // PROCESSING PIPELINE
  // ═══════════════════════════════════════════════════════

  // Helper: create document record AND upload the actual file
  const createAndUploadDocument = async (file: File, meta: { title: string; documentType: string; notes: string }) => {
    const docId = await createDocument({
      title: meta.title,
      documentType: meta.documentType,
      uploadedBy: user?.email || 'unknown',
      notes: meta.notes,
      incidentId,
    });
    if (docId && file) {
      try {
        await uploadDocumentFile(docId, file);
      } catch (e) {
        console.warn('[Processor] File upload failed for', meta.title, e);
      }
    }
    return docId;
  };

  const resumeSession = () => {
    const persisted = loadQueue(incidentId);
    if (persisted.length === 0) return;
    const restored: ReviewItem[] = persisted.map((data: any) => ({
      ...data,
      file: { name: data.fileName || 'unknown' } as File,
      commit: async (item: ReviewItem) => {
        if (item.docType === 'contractor_invoice' || item.docType === 'sub_invoice') {
          const FI = FIELDS.debrisInvoices;
          const payload: Record<string, unknown> = {
            [FI.incidentId]: incidentId,
            [FI.invoiceNumber]: item.invoiceNumber,
            [FI.totalAmount]: parseFloat(item.totalAmount) || 0,
            [FI.status]: 'draft',
            [FI.lineItems]: JSON.stringify(item.lineItems || []),
            [FI.approvedAmount]: 0,
            [FI.contractorId]: data.capturedContractorId || '',
          };
          if (item.invoiceDate) { payload[FI.periodStart] = item.invoiceDate; payload[FI.periodEnd] = item.invoiceDate; }
          if (item.docType === 'sub_invoice') {
            payload[FI.status] = 'sub_invoice';
            const subMarker = `|||SUBINVOICE|||${JSON.stringify({ primeInvoiceId: item.primeInvoiceId || '' })}|||ENDSUBINVOICE|||`;
            payload[FI.reviewerNotes] = data.capturedQualityMeta
              ? `|||TAMPA|||${data.capturedQualityMeta}|||ENDTAMPA|||\n${subMarker}`
              : subMarker;
          } else {
            payload[FI.reviewerNotes] = data.capturedQualityMeta
              ? `|||TAMPA|||${data.capturedQualityMeta}|||ENDTAMPA|||\n|||IMETA|||${JSON.stringify({ approvedBy: '', ticketIds: '[]', totalCubicYards: '' })}|||ENDIMETA|||`
              : '';
          }
          await createRecord(TABLES.debrisInvoices, payload);
          // Note: original file not available — document upload skipped for restored sessions
        } else if (item.docType === 'supporting_evidence') {
          const marker = item.selectedInvoiceId
            ? `|||INVOICELINK|||${JSON.stringify({ invoiceId: item.selectedInvoiceId })}|||ENDINVOICELINK|||`
            : '';
          await createRecord(TABLES.debrisDocuments, {
            [FDoc.incidentId]: incidentId,
            [FDoc.title]: data.fileName || 'Restored document',
            [FDoc.documentType]: item.selectedSubType || 'other',
            [FDoc.uploadedBy]: 'restored-session',
            [FDoc.notes]: `${marker}${item.evidenceSummary ? `\n${item.evidenceSummary}` : ''}`,
          });
        } else if (item.docType === 'sow_work_order' || item.docType === 'inspection_report') {
          const checklistItemId = item.docType === 'sow_work_order' ? 'ndc-003' : 'ndc-004';
          const docLabel = item.docType === 'sow_work_order' ? 'SOW / Work Order' : 'Inspection / Sign-Off';
          const marker = item.selectedInvoiceId
            ? `|||INVOICELINK|||${JSON.stringify({ invoiceId: item.selectedInvoiceId })}|||ENDINVOICELINK|||`
            : '';
          await createRecord(TABLES.debrisDocuments, {
            [FDoc.incidentId]: incidentId,
            [FDoc.title]: data.fileName || `Restored ${docLabel}`,
            [FDoc.documentType]: item.docType,
            [FDoc.uploadedBy]: 'restored-session',
            [FDoc.notes]: `${marker}${item.evidenceSummary ? `\n${item.evidenceSummary}` : ''}`,
          });
          // Auto-satisfy checklist item if invoice linked
          if (item.selectedInvoiceId) {
            try {
              const FCI = FIELDS.debrisChecklistItems;
              await createRecord(TABLES.debrisChecklistItems, {
                [FCI.invoiceId]: item.selectedInvoiceId,
                [FCI.invoiceType]: 'non_debris_contractor',
                [FCI.templateItemId]: checklistItemId,
                [FCI.label]: docLabel,
                [FCI.evidenceType]: 'file',
                [FCI.status]: 'satisfied',
                [FCI.checkedBy]: 'restored-session',
                [FCI.checkedAt]: new Date().toISOString(),
                [FCI.notes]: `Auto-linked via restored session from ${data.fileName || 'unknown'}`,
              });
            } catch {}
          }
        } else if (item.docType === 'force_account') {
          for (const entry of item.laborEntries) {
            const empName = String(entry.employeeName || '').trim();
            if (!empName) continue;
            try {
              const laborDocs: Record<string, string> = {};
              if (entry.timesheetDocId) laborDocs.timesheetDocId = entry.timesheetDocId;
              if (entry.payrollDocId) laborDocs.payrollDocId = entry.payrollDocId;
              const laborNotes = Object.keys(laborDocs).length > 0 ? encodeFaDocs(laborDocs) : '';
              await createRecord(TABLES.debrisForceAccountLabor, {
                [FL.incidentId]: incidentId,
                [FL.employeeName]: empName,
                [FL.employeeId]: String(entry.employeeId || ''),
                [FL.department]: String(entry.department || ''),
                [FL.classification]: String(entry.classification || ''),
                [FL.dateWorked]: String(entry.dateWorked || ''),
                [FL.hoursRegular]: String(parseFloat(entry.hoursWorked) || 0),
                [FL.hoursOvertime]: String(parseFloat(entry.hoursOT || entry.overtime || '0') || 0),
                [FL.hourlyRate]: '0', [FL.totalCost]: '0',
                [FL.ercDistrict]: String(entry.ercDistrict || ''),
                [FL.disasterNumber]: data.capturedDisasterNum || '',
                [FL.femaCategory]: data.capturedFemaCat || 'Cat B',
                [FL.locationOfWork]: String(entry.locationOfWork || ''),
                [FL.sourceFile]: data.fileName || '',
                [FL.timeIn]: String(entry.timeIn || ''),
                [FL.timeOut]: String(entry.timeOut || ''),
                [FL.notes]: laborNotes,
              });
            } catch {}
          }
          for (const entry of item.equipEntries) {
            const desc = String(entry.equipmentDescription || entry.description || '').trim();
            if (!desc) continue;
            try {
              const equipDocs: Record<string, string> = {};
              if (entry.femaRateDocId) equipDocs.femaRateDocId = entry.femaRateDocId;
              const equipNotes = Object.keys(equipDocs).length > 0 ? encodeFaDocs(equipDocs) : '';
              await createRecord(TABLES.debrisForceAccountEquipment, {
                [FE.incidentId]: incidentId,
                [FE.fleetNumber]: String(entry.fleetNumber || ''),
                [FE.equipmentType]: '', [FE.description]: desc,
                [FE.dateUsed]: String(entry.dateWorked || ''),
                [FE.hoursUsed]: String(parseFloat(entry.hoursUsed) || 0),
                [FE.hourlyRate]: '0', [FE.totalCost]: '0',
                [FE.ercDistrict]: String(entry.ercDistrict || ''),
                [FE.disasterNumber]: data.capturedDisasterNum || '',
                [FE.femaCategory]: data.capturedFemaCat || 'Cat B',
                [FE.locationOfWork]: String(entry.locationOfWork || ''),
                [FE.sourceFile]: data.fileName || '',
                [FE.timeIn]: String(entry.timeIn || ''),
                [FE.timeOut]: String(entry.timeOut || ''),
                [FE.notes]: equipNotes,
              });
            } catch {}
          }
        } else if (item.docType === 'contractor_register') {
          // Restore contractor register — parse rows from evidenceSummary JSON
          try {
            const validRows = JSON.parse(item.evidenceSummary || '[]');
            for (const row of validRows) {
              await createContractor({
                incidentId,
                companyName: row.companyName, contractNumber: row.contractNumber,
                contactName: row.contactName, contactPhone: row.contactPhone,
                contactEmail: row.contactEmail, contractAmount: row.contractAmount,
                contractCeiling: row.contractCeiling, vendorCategory: row.vendorCategory,
                managingDepartment: row.managingDepartment, cityPOC: row.cityPOC,
                cityPOCEmail: row.cityPOCEmail, status: row.status || 'active',
                notes: row.notes,
              });
            }
            fetchContractors();
          } catch {}
        } else if (item.docType === 'invoice_register') {
          // Restore invoice register — parse rows from evidenceSummary JSON
          try {
            const FI = FIELDS.debrisInvoices;
            const validRows = JSON.parse(item.evidenceSummary || '[]');
            for (const row of validRows) {
              const payload: Record<string, unknown> = {
                [FI.incidentId]: incidentId,
                [FI.invoiceNumber]: row.invoiceNumber,
                [FI.totalAmount]: row.totalAmount || 0,
                [FI.status]: 'draft',
                [FI.lineItems]: '[]',
                [FI.approvedAmount]: 0,
                [FI.contractorId]: row.matchedContractorId || '',
              };
              if (row.invoiceDate) { payload[FI.periodStart] = row.invoiceDate; payload[FI.periodEnd] = row.invoiceDate; }
              if (row.periodStart) payload[FI.periodStart] = row.periodStart;
              if (row.periodEnd) payload[FI.periodEnd] = row.periodEnd;
              const metaStr = JSON.stringify({ approvedBy: '', ticketIds: '[]', totalCubicYards: '' });
              payload[FI.reviewerNotes] = `|||IMETA|||${metaStr}|||ENDIMETA|||`;
              await createRecord(TABLES.debrisInvoices, payload);
            }
          } catch {}
        }
      },
    }));
    setReviewQueue(restored);
    setReviewIdx(0);
    setStep('review');
    setMode(restored.length >= 5 ? 'bulk' : 'individual');
    setPendingCount(0);
  };

  const runProcessing = async () => {
    setStep('process');
    abortRef.current = false;
    setProcessProgress(0);
    setProcessMessage('Starting...');

    const allResults: ProcessResult[] = [];
    const reviewItems: ReviewItem[] = [];
    const total = files.length;
    let processed = 0;

    const updateProgress = (msg: string) => {
      processed++;
      setProcessProgress(Math.round((processed / total) * 100));
      setProcessMessage(msg);
    };

    // Group files by effective type
    const groups: Record<string, ClassifiedFile[]> = {};
    files.forEach(cf => {
      const t = getEffType(cf);
      if (!groups[t]) groups[t] = [];
      groups[t].push(cf);
    });

    // ── Process load tickets ──────────────────────────
    if (groups.load_tickets && !abortRef.current) {
      for (const cf of groups.load_tickets) {
        if (abortRef.current) break;
        // Process all load tickets regardless of confidence — user explicitly classified or auto-detected
        setProcessMessage(`Importing tickets from ${cf.file.name}...`);
        try {
          const importResult = await processTicketData(cf.file);
          if (importResult.newRecords > 0 || importResult.skipped > 0) invalidateTicketCache();
          // Store the source file as a document so Ticket360 can find it
          // Include page mapping so individual tickets can link to their specific PDF page
          if (importResult.ticketNumbers.length > 0) {
            try {
              const pageMapStr = Object.keys(importResult.ticketPageMap).length > 0
                ? `\n|||PAGEMAP|||${JSON.stringify(importResult.ticketPageMap)}|||ENDPAGEMAP|||`
                : '';
              await createAndUploadDocument(cf.file, {
                title: cf.file.name,
                documentType: 'load_tickets',
                notes: `Source file for ${importResult.ticketNumbers.length} tickets: ${importResult.ticketNumbers.join(', ')}${pageMapStr}`,
              });
            } catch { /* non-critical */ }
          }
          allResults.push({
            file: cf.file.name, type: 'load_tickets',
            action: [
              `${importResult.newRecords} tickets imported`,
              importResult.skipped > 0 ? `${importResult.skipped} existing updated` : null,
              importResult.geocodedFromAddress > 0 ? `${importResult.geocodedFromAddress} geocoded from address` : null,
              importResult.geocodeMissing > 0 ? `⚠ ${importResult.geocodeMissing} missing location` : null,
            ].filter(Boolean).join(' · '),
            recordsCreated: importResult.newRecords,
            error: importResult.errors > 0 ? `${importResult.errors} errors` : undefined,
          });
        } catch (err) {
          allResults.push({
            file: cf.file.name, type: 'load_tickets', action: 'Failed', recordsCreated: 0,
            error: err instanceof Error ? err.message : 'Unknown error',
          });
        }
        updateProgress(`Processed ${cf.file.name}`);
      }
    }

    // ── Process truck certs ────────────────────────────
    if (groups.truck_cert && !abortRef.current) {
      for (const cf of groups.truck_cert) {
        if (abortRef.current) break;
        if (cf.confidence === 'low') {
          allResults.push({
            file: cf.file.name, type: 'truck_cert',
            action: 'Skipped — low classification confidence. Override the document type and reprocess to import.',
            recordsCreated: 0,
          });
          updateProgress(`Skipped ${cf.file.name} (low confidence)`);
          continue;
        }
        setProcessMessage(`Registering truck from ${cf.file.name}...`);
        try {
          const result = await processTruckCert(cf.file);
          allResults.push({
            file: cf.file.name, type: 'truck_cert',
            action: result.action, recordsCreated: result.created ? 1 : 0,
          });
        } catch (err) {
          allResults.push({
            file: cf.file.name, type: 'truck_cert', action: 'Failed', recordsCreated: 0,
            error: err instanceof Error ? err.message : 'Unknown error',
          });
        }
        updateProgress(`Processed ${cf.file.name}`);
      }
    }

    // ── Process invoices with AI extraction ─────────────
    // Query contractors fresh from the API — do NOT rely on the Zustand `contractors`
    // state, which may be empty if fetchContractors() hasn't completed since mount.
    // (Invoice numbers below use the same pattern for the same reason.)
    const localContractors: Array<{ id: string | number; companyName: string }> = [];
    try {
      const FC2 = FIELDS.debrisContractors;
      const cFilter = incidentId ? [{ field: FC2.incidentId, operator: 'eq' as const, value: incidentId }] : [];
      const cRes = await queryRecords<Record<string, unknown>>(TABLES.debrisContractors, {
        limit: 500, ...(cFilter.length > 0 ? { filters: cFilter } : {}),
      });
      cRes.records
        .filter(r => !incidentId || String(r[FC2.incidentId] || '') === incidentId)
        .forEach(r => localContractors.push({ id: String(r.record_id), companyName: String(r[FC2.companyName] || '') }));
    } catch { localContractors.push(...contractors.map(c => ({ id: c.id, companyName: c.companyName }))); }
    // Maps normalized vendor name → in-flight Promise<id> — prevents two concurrent
    // files from each creating their own contractor for the same vendor.
    const pendingContractorCreates = new Map<string, Promise<string>>();

    const resolveOrCreateContractor = async (vendorName: string): Promise<string> => {
      if (!vendorName) return '';
      const existing = findContractorByName(vendorName, localContractors);
      if (existing) return String(existing.id);
      const normName = normalizeContractorName(vendorName);
      const pending = pendingContractorCreates.get(normName);
      if (pending) return pending;
      const promise = (async () => {
        try {
          const FC = FIELDS.debrisContractors;
          const res = await createRecord(TABLES.debrisContractors, {
            [FC.incidentId]: incidentId,
            [FC.companyName]: vendorName,
            [FC.status]: 'active',
            [FC.notes]: `|||TRMETA|||${JSON.stringify({ autoCreated: 'true' })}|||ENDTRMETA|||`,
          });
          const newId = String((res as any)?.record_id || '');
          if (newId) {
            localContractors.push({ id: newId, companyName: vendorName });
            fetchContractors();
          }
          return newId;
        } catch { return ''; }
      })();
      pendingContractorCreates.set(normName, promise);
      return promise;
    };

    // Pre-load existing invoice numbers + document titles for dedup
    // Map from invoice number (lowercase) → existing record ID (for upsert in commit)
    const existingInvoiceMap = new Map<string, number>();
    const existingDocTitles = new Set<string>();
    try {
      const FI = FIELDS.debrisInvoices;
      const invIncFilter = incidentId ? [{ field: FI.incidentId, operator: 'eq' as const, value: incidentId }] : [];
      let invOff = 0, invTotal = Infinity;
      while (invOff < invTotal) {
        const res = await queryRecords<Record<string, unknown>>(TABLES.debrisInvoices, {
          limit: 500, offset: invOff,
          ...(invIncFilter.length > 0 ? { filters: invIncFilter } : {}),
        });
        invTotal = res.total;
        res.records.forEach(r => {
          // Client-side filter since GAB filter API is unreliable
          if (incidentId && String(r[FI.incidentId] || '') !== incidentId) return;
          const num = String(r[FI.invoiceNumber] || '').trim().toLowerCase();
          if (num) existingInvoiceMap.set(num, Number(r.record_id) || 0);
        });
        invOff += 500;
      }
    } catch (e) { console.warn('[Processor] Invoice dedup query failed', e); }
    try {
      const FD = FIELDS.debrisDocuments;
      const docIncFilter = incidentId ? [{ field: FD.incidentId, operator: 'eq' as const, value: incidentId }] : [];
      let docOff = 0, docTotal = Infinity;
      while (docOff < docTotal) {
        const res = await queryRecords<Record<string, unknown>>(TABLES.debrisDocuments, {
          limit: 500, offset: docOff,
          ...(docIncFilter.length > 0 ? { filters: docIncFilter } : {}),
        });
        docTotal = res.total;
        res.records.forEach(r => {
          // Client-side filter since GAB filter API is unreliable
          if (incidentId && String(r[FD.incidentId] || '') !== incidentId) return;
          const title = String(r[FD.title] || '').trim().toLowerCase();
          if (title) existingDocTitles.add(title);
        });
        docOff += 500;
      }
    } catch (e) { console.warn('[Processor] Document dedup query failed', e); }

    // ── Shared invoice AI prompt ────────────────────────────────────
    const INVOICE_JSON_SCHEMA = `{
  "vendor": "company name as a plain string",
  "vendorAddress": "full address",
  "invoiceNumber": "invoice number",
  "invoiceDate": "MM/DD/YYYY",
  "poNumber": "PO or WO number or empty string",
  "totalAmount": 123.45,
  "disasterReference": "hurricane or disaster reference if mentioned",
  "jobDescription": "description of work performed",
  "jobLocation": "address where work was done",
  "lineItems": [{"description": "item", "quantity": 1, "unit": "each", "rate": 100.00, "amount": 100.00}],
  "laborBreakdown": [{"classification": "Foreman", "hours": 8, "rate": 50.00, "amount": 400.00}],
  "materials": [{"description": "material", "supplierInvoice": "", "amount": 50.00}],
  "hasItemizedMaterials": true,
  "hasLaborBreakdown": true,
  "hasDailyServiceTickets": false,
  "qualityScore": "complete or needs_revision or reject",
  "missingItems": ["list what is missing for FEMA compliance"],
  "summary": "one sentence summary",
  "confidenceScore": 85
}`;
    const INVOICE_SYSTEM = 'You are a FEMA compliance analyst reviewing contractor invoices for disaster debris operations. Extract ALL data. Be thorough but concise — keep descriptions under 80 chars. Return ONLY valid JSON. vendor MUST be a plain string. qualityScore MUST be exactly one of: complete, needs_revision, reject. confidenceScore is 0-100: overall confidence in extraction accuracy.';

    if (groups.contractor_invoice && !abortRef.current) {
      const invoiceConcurrency = mode === 'bulk' ? 3 : 1;
      await withConcurrency(groups.contractor_invoice, invoiceConcurrency, async (cf) => {
        if (abortRef.current) return;
        setProcessMessage(`Extracting data from ${cf.file.name}...`);
        try {
          let aiResult: any = null;
          let extractionMethod: 'pdf-text' | 'claude-vision' = 'claude-vision';
          let pdfTextLen = 0;

          try {
            // Try text extraction first (digital PDFs — faster + cheaper)
            const pdfText = await extractPdfText(cf.file);
            pdfTextLen = pdfText.length;
            if (pdfText.length >= 150) {
              extractionMethod = 'pdf-text';
              setProcessMessage(`Reading invoice ${cf.file.name} (text)...`);
              const resp = await callClaude([{
                role: 'user',
                content: `Extract ALL data from this contractor invoice. Return ONLY valid JSON:\n${INVOICE_JSON_SCHEMA}\n\n[INVOICE TEXT]\n${pdfText}`,
              }], { system: INVOICE_SYSTEM, maxTokens: 4096 });
              aiResult = normalizeInvoiceAI(resp);
              await new Promise(r => setTimeout(r, 2000));
            } else {
              // Scanned PDF — fall back to Claude Vision
              extractionMethod = 'claude-vision';
              setProcessMessage(`Reading invoice ${cf.file.name} (vision)...`);
              const buf = await cf.file.arrayBuffer();
              const bytes = new Uint8Array(buf);
              let binary = '';
              const CHUNK = 32768;
              for (let i = 0; i < bytes.length; i += CHUNK)
                binary += String.fromCharCode(...bytes.subarray(i, Math.min(i + CHUNK, bytes.length)));
              const base64 = btoa(binary);
              const resp = await callClaude([{
                role: 'user',
                content: [
                  { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } } as any,
                  { type: 'text', text: `Extract ALL data from this contractor invoice. Return ONLY valid JSON:\n${INVOICE_JSON_SCHEMA}` },
                ],
              }], { system: INVOICE_SYSTEM, maxTokens: 4096 });
              aiResult = normalizeInvoiceAI(resp);
              await new Promise(r => setTimeout(r, 5000));
            }
          } catch (aiErr) {
            console.error('[Processor] AI invoice extraction failed for', cf.file.name, ':', aiErr);
          }

          const ocrConf = computeOcrConfidence(extractionMethod, pdfTextLen);

          // Check for existing invoice — if found, commit will update instead of create
          const extractedNum = String(aiResult?.invoiceNumber || cf.file.name.replace(/\.[^.]+$/, '')).trim().toLowerCase();
          const existingInvoiceRecordId = extractedNum ? (existingInvoiceMap.get(extractedNum) ?? 0) : 0;
          const isDuplicate = existingInvoiceRecordId > 0;
          if (!isDuplicate) existingInvoiceMap.set(extractedNum, 0); // reserve to prevent double-create in same batch

          // Resolve contractor now (before review) so it's captured in the commit closure
          const resolvedContractorId = aiResult?.vendor
            ? await resolveOrCreateContractor(String(aiResult.vendor))
            : '';

          // Build quality metadata captured in commit closure
          const qualityMeta = aiResult ? JSON.stringify({
            qualityScore: aiResult.qualityScore || 'needs_revision',
            missingItems: aiResult.missingItems || [],
            vendor: aiResult.vendor || '',
            vendorAddress: aiResult.vendorAddress || '',
            poNumber: aiResult.poNumber || '',
            disasterReference: aiResult.disasterReference || '',
            jobDescription: aiResult.jobDescription || '',
            jobLocation: aiResult.jobLocation || '',
            hasItemizedMaterials: aiResult.hasItemizedMaterials || false,
            hasLaborBreakdown: aiResult.hasLaborBreakdown || false,
            hasDailyServiceTickets: aiResult.hasDailyServiceTickets || false,
            laborBreakdown: aiResult.laborBreakdown || [],
            materials: aiResult.materials || [],
            aiSummary: aiResult.summary || '',
            extractionMethod,
          }) : '';

          // Capture loop-local values for the commit closure
          const capturedContractorId = resolvedContractorId;
          const capturedQualityMeta = qualityMeta;
          const capturedFile = cf.file;
          const capturedExistingRecordId = existingInvoiceRecordId;

          const warnings: string[] = [];
          if (isDuplicate) warnings.push(`Invoice #${extractedNum} already exists — approving will update the existing record`);
          if (aiResult?.missingItems?.length) warnings.push(...(aiResult.missingItems as string[]).slice(0, 3));
          if (!aiResult) warnings.push('AI extraction failed — review manually');

          // Upload PDF now so resumed sessions can show the preview
          let previewDocId: string | undefined;
          try {
            const uploadResult = await uploadFileToV2(capturedFile, 'invoice-preview');
            previewDocId = uploadResult.documentId;
          } catch { /* non-critical — preview just won't work on restore */ }

          reviewItems.push({
            id: `inv-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            file: cf.file,
            docType: 'contractor_invoice',
            status: 'pending',
            vendor: aiResult?.vendor || '',
            invoiceNumber: aiResult?.invoiceNumber || cf.file.name.replace(/\.[^.]+$/, ''),
            invoiceDate: aiResult?.invoiceDate || '',
            totalAmount: String(aiResult?.totalAmount || '0'),
            lineItems: aiResult?.lineItems || [],
            qualityScore: aiResult?.qualityScore || 'needs_revision',
            missingItems: aiResult?.missingItems || [],
            summary: aiResult?.summary || '',
            laborEntries: [],
            equipEntries: [],
            warnings,
            extractionMethod,
            extractionScore: typeof aiResult?.confidenceScore === 'number' ? aiResult.confidenceScore : undefined,
            ocrConfidence: ocrConf,
            capturedContractorId,
            capturedQualityMeta,
            fileName: cf.file.name,
            previewDocId,
            commit: async (item: ReviewItem) => {
              const FI = FIELDS.debrisInvoices;
              const reviewerNotes = capturedQualityMeta
                ? `|||TAMPA|||${capturedQualityMeta}|||ENDTAMPA|||\n|||IMETA|||${JSON.stringify({ approvedBy: '', ticketIds: '[]', totalCubicYards: '' })}|||ENDIMETA|||`
                : '';
              if (capturedExistingRecordId > 0) {
                // Invoice already exists — update instead of create
                const updatePayload: Record<string, unknown> = {
                  [FI.invoiceNumber]: item.invoiceNumber,
                  [FI.totalAmount]: parseFloat(item.totalAmount) || 0,
                  [FI.lineItems]: JSON.stringify(item.lineItems || []),
                  [FI.contractorId]: capturedContractorId,
                  [FI.reviewerNotes]: reviewerNotes,
                };
                if (item.invoiceDate) {
                  updatePayload[FI.periodStart] = item.invoiceDate;
                  updatePayload[FI.periodEnd] = item.invoiceDate;
                }
                await updateRecord(TABLES.debrisInvoices, capturedExistingRecordId, updatePayload);
              } else {
                const payload: Record<string, unknown> = {
                  [FI.incidentId]: incidentId,
                  [FI.invoiceNumber]: item.invoiceNumber,
                  [FI.totalAmount]: parseFloat(item.totalAmount) || 0,
                  [FI.status]: 'draft',
                  [FI.lineItems]: JSON.stringify(item.lineItems || []),
                  [FI.approvedAmount]: 0,
                  [FI.contractorId]: capturedContractorId,
                  [FI.reviewerNotes]: reviewerNotes,
                };
                if (item.invoiceDate) {
                  payload[FI.periodStart] = item.invoiceDate;
                  payload[FI.periodEnd] = item.invoiceDate;
                }
                await createRecord(TABLES.debrisInvoices, payload);
              }
              if (!existingDocTitles.has(capturedFile.name.toLowerCase())) {
                await createAndUploadDocument(capturedFile, {
                  title: capturedFile.name,
                  documentType: 'contractor_invoice',
                  notes: `Uploaded via Package Processor. AI extracted: ${item.summary || item.vendor || ''}`,
                });
                existingDocTitles.add(capturedFile.name.toLowerCase());
              }
            },
          });
        } catch (err) {
          allResults.push({
            file: cf.file.name, type: 'contractor_invoice', action: 'Extraction failed', recordsCreated: 0,
            error: err instanceof Error ? err.message : 'Unknown error',
          });
        }
        updateProgress(`Extracted ${cf.file.name}`);
      });
    }

    // ── Process sub-contractor invoices ────────────────
    if (groups.sub_invoice && !abortRef.current) {
      const subInvoiceConcurrency = mode === 'bulk' ? 3 : 1;
      await withConcurrency(groups.sub_invoice, subInvoiceConcurrency, async (cf) => {
        if (abortRef.current) return;
        setProcessMessage(`Extracting sub-invoice data from ${cf.file.name}...`);
        try {
          let aiResult: any = null;
          let extractionMethod: 'pdf-text' | 'claude-vision' = 'claude-vision';
          let pdfTextLen = 0;
          try {
            const pdfText = await extractPdfText(cf.file);
            pdfTextLen = pdfText.length;
            if (pdfText.length >= 150) {
              extractionMethod = 'pdf-text';
              const resp = await callClaude([{
                role: 'user',
                content: `Extract ALL data from this sub-contractor invoice. Return ONLY valid JSON:\n${INVOICE_JSON_SCHEMA}\n\n[INVOICE TEXT]\n${pdfText}`,
              }], { system: INVOICE_SYSTEM, maxTokens: 4096 });
              aiResult = normalizeInvoiceAI(resp);
              await new Promise(r => setTimeout(r, 2000));
            } else {
              const buf = await cf.file.arrayBuffer();
              const bytes = new Uint8Array(buf);
              let binary = ''; const CHUNK = 32768;
              for (let i = 0; i < bytes.length; i += CHUNK)
                binary += String.fromCharCode(...bytes.subarray(i, Math.min(i + CHUNK, bytes.length)));
              const base64 = btoa(binary);
              const resp = await callClaude([{
                role: 'user',
                content: [
                  { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } } as any,
                  { type: 'text', text: `Extract ALL data from this sub-contractor invoice. Return ONLY valid JSON:\n${INVOICE_JSON_SCHEMA}` },
                ],
              }], { system: INVOICE_SYSTEM, maxTokens: 4096 });
              aiResult = normalizeInvoiceAI(resp);
              await new Promise(r => setTimeout(r, 5000));
            }
          } catch { /* AI failed */ }

          const ocrConf = computeOcrConfidence(extractionMethod, pdfTextLen);
          const resolvedContractorId = aiResult?.vendor
            ? await resolveOrCreateContractor(String(aiResult.vendor))
            : '';
          const qualityMeta = aiResult ? JSON.stringify({
            qualityScore: aiResult.qualityScore || 'needs_revision',
            missingItems: aiResult.missingItems || [],
            vendor: aiResult.vendor || '',
            vendorAddress: aiResult.vendorAddress || '',
            extractionMethod,
          }) : '';
          const capturedContractorId = resolvedContractorId;
          const capturedQualityMeta = qualityMeta;
          const capturedFile = cf.file;
          const warnings: string[] = [];
          if (!aiResult) warnings.push('AI extraction failed — review manually');
          if (aiResult?.missingItems?.length) warnings.push(...(aiResult.missingItems as string[]).slice(0, 3));

          let previewDocId: string | undefined;
          try { const r = await uploadFileToV2(capturedFile, 'sub-invoice-preview'); previewDocId = r.documentId; } catch {}

          reviewItems.push({
            id: `sub-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            file: cf.file,
            docType: 'sub_invoice',
            status: 'pending',
            vendor: aiResult?.vendor || '',
            invoiceNumber: aiResult?.invoiceNumber || cf.file.name.replace(/\.[^.]+$/, ''),
            invoiceDate: aiResult?.invoiceDate || '',
            totalAmount: String(aiResult?.totalAmount || '0'),
            lineItems: aiResult?.lineItems || [],
            qualityScore: aiResult?.qualityScore || 'needs_revision',
            missingItems: aiResult?.missingItems || [],
            summary: aiResult?.summary || '',
            laborEntries: [], equipEntries: [],
            warnings,
            extractionMethod,
            extractionScore: typeof aiResult?.confidenceScore === 'number' ? aiResult.confidenceScore : undefined,
            ocrConfidence: ocrConf,
            capturedContractorId,
            capturedQualityMeta,
            primeInvoiceId: '',
            fileName: cf.file.name,
            previewDocId,
            commit: async (item: ReviewItem) => {
              const FI = FIELDS.debrisInvoices;
              const subMarker = `|||SUBINVOICE|||${JSON.stringify({ primeInvoiceId: item.primeInvoiceId || '' })}|||ENDSUBINVOICE|||`;
              const payload: Record<string, unknown> = {
                [FI.incidentId]: incidentId,
                [FI.invoiceNumber]: item.invoiceNumber,
                [FI.totalAmount]: parseFloat(item.totalAmount) || 0,
                [FI.status]: 'sub_invoice',
                [FI.lineItems]: JSON.stringify(item.lineItems || []),
                [FI.approvedAmount]: 0,
                [FI.contractorId]: capturedContractorId,
                [FI.reviewerNotes]: capturedQualityMeta
                  ? `|||TAMPA|||${capturedQualityMeta}|||ENDTAMPA|||\n${subMarker}`
                  : subMarker,
              };
              if (item.invoiceDate) { payload[FI.periodStart] = item.invoiceDate; payload[FI.periodEnd] = item.invoiceDate; }
              await createRecord(TABLES.debrisInvoices, payload);
              if (!existingDocTitles.has(capturedFile.name.toLowerCase())) {
                await createAndUploadDocument(capturedFile, {
                  title: capturedFile.name,
                  documentType: 'sub_invoice',
                  notes: `Sub-contractor invoice uploaded via Package Processor. AI extracted: ${item.summary || item.vendor || ''}`,
                });
                existingDocTitles.add(capturedFile.name.toLowerCase());
              }
            },
          });
        } catch (err) {
          allResults.push({
            file: cf.file.name, type: 'sub_invoice', action: 'Extraction failed', recordsCreated: 0,
            error: err instanceof Error ? err.message : 'Unknown error',
          });
        }
        updateProgress(`Extracted ${cf.file.name}`);
      });
    }

    // ── Process transaction detail ─────────────────────
    if (groups.transaction_detail && !abortRef.current) {
      for (const cf of groups.transaction_detail) {
        if (abortRef.current) break;
        if (cf.confidence === 'low') {
          allResults.push({
            file: cf.file.name, type: 'transaction_detail',
            action: 'Skipped — low classification confidence. Override the document type and reprocess.',
            recordsCreated: 0,
          });
          updateProgress(`Skipped ${cf.file.name} (low confidence)`);
          continue;
        }
        setProcessMessage(`Parsing transaction detail ${cf.file.name}...`);
        try {
          const txResult = await processTransactionDetail(cf.file);
          allResults.push({
            file: cf.file.name, type: 'transaction_detail',
            action: [
              `${txResult.clinGroups} CLINs parsed · ${fmtMoney(txResult.totalAmount)} total`,
              txResult.ticketsEnriched > 0 ? `${txResult.ticketsEnriched} tickets enriched with billing data` : null,
            ].filter(Boolean).join(' · '),
            recordsCreated: txResult.ticketsEnriched,
          });
        } catch (err) {
          allResults.push({
            file: cf.file.name, type: 'transaction_detail', action: 'Failed', recordsCreated: 0,
            error: err instanceof Error ? err.message : 'Unknown error',
          });
        }
        updateProgress(`Processed ${cf.file.name}`);
      }
    }

    // ── Process Employee Roster (must run before Force Account so roster is available) ──
    if (groups.employee_roster && !abortRef.current) {
      for (const cf of groups.employee_roster) {
        if (abortRef.current) break;
        setProcessMessage(`Parsing employee roster ${cf.file.name}...`);
        try {
          const rosterResult = await processEmployeeRoster(cf.file);
          allResults.push({
            file: cf.file.name, type: 'employee_roster',
            action: `${rosterResult.employeeCount} employee records stored for FA matching`,
            recordsCreated: rosterResult.employeeCount,
            error: rosterResult.error,
          });
        } catch (err) {
          allResults.push({
            file: cf.file.name, type: 'employee_roster', action: 'Failed', recordsCreated: 0,
            error: err instanceof Error ? err.message : 'Unknown error',
          });
        }
        updateProgress(`Processed ${cf.file.name}`);
      }
    }

    // ── Process Force Account (Excel/CSV direct + ANNEX E-3 PDF → review queue) ──
    if (groups.force_account && !abortRef.current) {
      const roster = await loadRosterForIncident();
      for (const cf of groups.force_account) {
        if (abortRef.current) break;
        const ext = cf.file.name.split('.').pop()?.toLowerCase() ?? '';
        const isSpreadsheet = ['xlsx', 'xls', 'csv'].includes(ext);

        if (isSpreadsheet) {
          if (cf.confidence === 'low') {
            allResults.push({
              file: cf.file.name, type: 'force_account',
              action: 'Skipped — low classification confidence. Override the document type and reprocess to import.',
              recordsCreated: 0,
            });
            updateProgress(`Skipped ${cf.file.name} (low confidence)`);
            continue;
          }
          setProcessMessage(`Importing Force Account data from ${cf.file.name}...`);
          try {
            const faResult = await processForceAccountExcel(cf.file, roster);
            if (faResult.rosterUnmatched && faResult.rosterUnmatched.length > 0) {
              reviewItems.push({
                id: `fa-unmatched-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                file: cf.file,
                docType: 'force_account',
                status: 'pending',
                vendor: '', invoiceNumber: '', invoiceDate: '', totalAmount: '', lineItems: [],
                qualityScore: 'needs_revision',
                missingItems: [],
                summary: `${faResult.laborCreated} labor, ${faResult.equipCreated} equipment imported. ${faResult.rosterUnmatched.length} employee(s) not matched to roster — rates set to $0.`,
                laborEntries: [], equipEntries: [],
                warnings: [`Unmatched employees (hourly rate = $0): ${faResult.rosterUnmatched.join(', ')}`],
                extractionMethod: 'column-map',
                extractionScore: undefined,
                ocrConfidence: 'high' as const,
                capturedDisasterNum: '', capturedFemaCat: 'Cat B', fileName: cf.file.name,
                rosterUnmatched: faResult.rosterUnmatched,
                commit: async () => { /* records already created by processForceAccountExcel */ },
              });
            }
            allResults.push({
              file: cf.file.name, type: 'force_account',
              action: `${faResult.laborCreated} labor records, ${faResult.equipCreated} equipment records imported${faResult.laborErrors > 0 ? ` (${faResult.laborErrors} errors)` : ''}${faResult.rosterUnmatched?.length ? ` — ${faResult.rosterUnmatched.length} unmatched (check review queue)` : ''}`,
              recordsCreated: faResult.laborCreated + faResult.equipCreated,
              error: faResult.laborErrors > 0 && (faResult.laborCreated + faResult.equipCreated) === 0 ? 'All rows failed — check format' : undefined,
            });
          } catch (err) {
            allResults.push({
              file: cf.file.name, type: 'force_account', action: 'Failed', recordsCreated: 0,
              error: err instanceof Error ? err.message : 'Unknown error',
            });
          }
        } else {
          // PDF → Vision extraction → review queue
          setProcessMessage(`Extracting Force Account data from ${cf.file.name}...`);
          try {
            await createAndUploadDocument(cf.file, {
              title: cf.file.name,
              documentType: 'force_account',
              notes: 'ANNEX E-3 Force Account form — pending human review',
            });

            const faResult = await processForceAccountForm(cf.file, true);
            const laborEntries = (faResult.entries || []).filter((e: any) => e.type === 'labor');
            const equipEntries = (faResult.entries || []).filter((e: any) => e.type === 'equipment');
            const capturedDisasterNum = faResult.disasterNumber || '';
            const capturedFemaCat = faResult.femaCategory || 'Cat B';
            const capturedFileName = cf.file.name;

            const rosterUnmatched: string[] = [];
            for (const entry of laborEntries) {
              const match = matchEmployeeToRoster(String(entry.employeeName || ''), String(entry.employeeId || ''), roster);
              if (match) {
                entry.hourlyRate = match.hourlyRate;
                entry.classification = entry.classification || match.classification;
                entry.ercDistrict = entry.ercDistrict || match.ercDistrict;
              } else if (String(entry.employeeName || '').trim()) {
                rosterUnmatched.push(String(entry.employeeName || '').trim());
              }
            }

            const warnings: string[] = [];
            if (faResult.aiError) warnings.push(faResult.aiError);
            if (laborEntries.length === 0 && equipEntries.length === 0)
              warnings.push('No entries found — PDF may be handwritten or low-quality; review carefully');
            if (rosterUnmatched.length > 0)
              warnings.push(`Unmatched employees (hourly rate = $0): ${rosterUnmatched.join(', ')}`);

            reviewItems.push({
              id: `fa-${Date.now()}-${Math.random().toString(36).slice(2)}`,
              file: cf.file,
              docType: 'force_account',
              status: 'pending',
              vendor: '', invoiceNumber: '', invoiceDate: '', totalAmount: '', lineItems: [],
              qualityScore: laborEntries.length > 0 || equipEntries.length > 0 ? 'complete' : 'needs_revision',
              missingItems: [],
              summary: `${laborEntries.length} labor + ${equipEntries.length} equipment entries from ${faResult.pagesProcessed || 0}/${faResult.totalPages || 0} pages · ${capturedDisasterNum || 'DR unknown'} · ${capturedFemaCat}`,
              laborEntries,
              equipEntries,
              warnings,
              rosterUnmatched,
              extractionMethod: 'claude-vision',
              extractionScore: undefined,
              ocrConfidence: 'medium' as const,
              capturedDisasterNum,
              capturedFemaCat,
              fileName: cf.file.name,
              commit: async (item: ReviewItem) => {
                for (const entry of item.laborEntries) {
                  const empName = String(entry.employeeName || '').trim();
                  if (!empName) continue;
                  const laborDocs: Record<string, string> = {};
                  if (entry.timesheetDocId) laborDocs.timesheetDocId = entry.timesheetDocId;
                  if (entry.payrollDocId) laborDocs.payrollDocId = entry.payrollDocId;
                  const laborNotes = Object.keys(laborDocs).length > 0 ? encodeFaDocs(laborDocs) : '';
                  await createRecord(TABLES.debrisForceAccountLabor, {
                    [FL.incidentId]: incidentId, [FL.employeeName]: empName,
                    [FL.employeeId]: String(entry.employeeId || ''),
                    [FL.department]: String(entry.department || ''),
                    [FL.classification]: String(entry.classification || ''),
                    [FL.dateWorked]: String(entry.dateWorked || ''),
                    [FL.hoursRegular]: String(parseFloat(entry.hoursWorked) || 0),
                    [FL.hoursOvertime]: String(parseFloat(entry.hoursOT || entry.overtime || '0') || 0),
                    [FL.hourlyRate]: String(entry.hourlyRate || 0), [FL.totalCost]: '0',
                    [FL.ercDistrict]: String(entry.ercDistrict || ''),
                    [FL.disasterNumber]: capturedDisasterNum, [FL.femaCategory]: capturedFemaCat,
                    [FL.locationOfWork]: String(entry.locationOfWork || ''),
                    [FL.sourceFile]: capturedFileName,
                    [FL.timeIn]: String(entry.timeIn || ''), [FL.timeOut]: String(entry.timeOut || ''),
                    [FL.notes]: laborNotes,
                  });
                }
                for (const entry of item.equipEntries) {
                  const desc = String(entry.equipmentDescription || entry.description || '').trim();
                  if (!desc) continue;
                  const equipDocs: Record<string, string> = {};
                  if (entry.femaRateDocId) equipDocs.femaRateDocId = entry.femaRateDocId;
                  const equipNotes = Object.keys(equipDocs).length > 0 ? encodeFaDocs(equipDocs) : '';
                  await createRecord(TABLES.debrisForceAccountEquipment, {
                    [FE.incidentId]: incidentId, [FE.fleetNumber]: String(entry.fleetNumber || ''),
                    [FE.equipmentType]: '', [FE.description]: desc,
                    [FE.dateUsed]: String(entry.dateWorked || ''),
                    [FE.hoursUsed]: String(parseFloat(entry.hoursUsed) || 0),
                    [FE.hourlyRate]: '0', [FE.totalCost]: '0',
                    [FE.ercDistrict]: String(entry.ercDistrict || ''),
                    [FE.disasterNumber]: capturedDisasterNum, [FE.femaCategory]: capturedFemaCat,
                    [FE.locationOfWork]: String(entry.locationOfWork || ''),
                    [FE.sourceFile]: capturedFileName,
                    [FE.timeIn]: String(entry.timeIn || ''), [FE.timeOut]: String(entry.timeOut || ''),
                    [FE.notes]: equipNotes,
                  });
                }
              },
            });
          } catch (err) {
            allResults.push({
              file: cf.file.name, type: 'force_account', action: 'Extraction failed', recordsCreated: 0,
              error: err instanceof Error ? err.message : 'Unknown error',
            });
          }
        }
        updateProgress(`Processed ${cf.file.name}`);
      }
    }

    // ── Process Contractor Register → bulk create contractors ──
    if (groups.contractor_register && !abortRef.current) {
      for (const cf of groups.contractor_register) {
        if (abortRef.current) break;
        setProcessMessage(`Parsing contractor register ${cf.file.name}...`);
        try {
          const { rows, errors } = await parseContractorSpreadsheet(cf.file, localContractors as any);
          const validCount = rows.filter(r => r.valid).length;
          reviewItems.push({
            id: `cr-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            file: cf.file,
            docType: 'contractor_register',
            status: 'pending',
            vendor: '', invoiceNumber: '', invoiceDate: '', totalAmount: '', lineItems: [],
            qualityScore: 'complete', missingItems: [],
            summary: `${validCount} valid contractor${validCount !== 1 ? 's' : ''} found (${rows.length} total rows, ${errors.length} errors)`,
            laborEntries: [], equipEntries: [],
            warnings: errors.length > 0 ? [`${errors.length} validation error(s)`] : [],
            extractionMethod: 'pdf-text', ocrConfidence: 'high',
            fileName: cf.file.name,
            evidenceSummary: JSON.stringify(rows.filter(r => r.valid)),
            commit: async () => {
              const validRows = rows.filter(r => r.valid);
              for (const row of validRows) {
                await createContractor({
                  incidentId,
                  companyName: row.companyName, contractNumber: row.contractNumber,
                  contactName: row.contactName, contactPhone: row.contactPhone,
                  contactEmail: row.contactEmail, contractAmount: row.contractAmount,
                  contractCeiling: row.contractCeiling, vendorCategory: row.vendorCategory,
                  managingDepartment: row.managingDepartment, cityPOC: row.cityPOC,
                  cityPOCEmail: row.cityPOCEmail, status: row.status || 'active',
                  notes: row.notes,
                });
              }
              fetchContractors();
              allResults.push({ file: cf.file.name, type: 'contractor_register', action: `Imported ${validRows.length} contractors`, recordsCreated: validRows.length });
            },
          });
        } catch (err) {
          allResults.push({
            file: cf.file.name, type: 'contractor_register', action: 'Failed', recordsCreated: 0,
            error: err instanceof Error ? err.message : 'Unknown error',
          });
        }
        updateProgress(`Queued ${cf.file.name} for review`);
      }
    }

    // ── Process Invoice Register → bulk create invoices ──
    if (groups.invoice_register && !abortRef.current) {
      for (const cf of groups.invoice_register) {
        if (abortRef.current) break;
        setProcessMessage(`Parsing invoice register ${cf.file.name}...`);
        try {
          const { rows, errors } = await parseInvoiceSpreadsheet(cf.file, localContractors as any, Array.from(existingInvoiceMap.keys()).map(n => ({ invoiceNumber: n })));
          const validCount = rows.filter(r => r.valid).length;
          reviewItems.push({
            id: `ir-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            file: cf.file,
            docType: 'invoice_register',
            status: 'pending',
            vendor: '', invoiceNumber: '', invoiceDate: '', totalAmount: '', lineItems: [],
            qualityScore: 'complete', missingItems: [],
            summary: `${validCount} valid invoice${validCount !== 1 ? 's' : ''} found (${rows.length} total rows, ${errors.length} errors)`,
            laborEntries: [], equipEntries: [],
            warnings: errors.length > 0 ? [`${errors.length} validation error(s)`] : [],
            extractionMethod: 'pdf-text', ocrConfidence: 'high',
            fileName: cf.file.name,
            evidenceSummary: JSON.stringify(rows.filter(r => r.valid)),
            commit: async () => {
              const FI = FIELDS.debrisInvoices;
              const FC = FIELDS.debrisContractors;
              const validRows = rows.filter(r => r.valid);
              let created = 0;
              let skipped = 0;
              // Dedup: check existing invoices at commit time
              const existingRes = await queryRecords<Record<string, unknown>>(TABLES.debrisInvoices, {
                limit: 500,
                filters: [{ field: FI.incidentId, operator: 'eq' as const, value: incidentId }],
              });
              const existingInvNums = new Set(
                existingRes.records.map(r => String(r[FI.invoiceNumber] || '').toLowerCase().trim())
              );
              // First pass: auto-create ALL missing contractors from ALL rows (including invalid ones)
              const createdContractors = new Map<string, string>();
              for (const row of rows) {
                if (!row.matchedContractorId && row.contractorName) {
                  const normName = row.contractorName.toLowerCase().trim();
                  if (!createdContractors.has(normName)) {
                    // Check if another row already matched this contractor
                    const alreadyExists = localContractors.some(c =>
                      c.companyName.toLowerCase().trim() === normName ||
                      normalizeContractorName(c.companyName) === normalizeContractorName(row.contractorName)
                    );
                    if (!alreadyExists) {
                      try {
                        const newCont = await createRecord<Record<string, unknown>>(TABLES.debrisContractors, {
                          [FC.incidentId]: incidentId,
                          [FC.companyName]: row.contractorName,
                          [FC.status]: 'active',
                        });
                        const newId = String((newCont as any)?.record_id || '');
                        if (newId) {
                          createdContractors.set(normName, newId);
                          localContractors.push({ id: newId, companyName: row.contractorName });
                        }
                      } catch {}
                    }
                  }
                }
              }
              if (createdContractors.size > 0) fetchContractors();

              // Second pass: create invoices for valid rows
              for (const row of validRows) {
                // Resolve contractor ID (may have been auto-created in first pass)
                let contractorId = row.matchedContractorId || '';
                if (!contractorId && row.contractorName) {
                  const normName = row.contractorName.toLowerCase().trim();
                  contractorId = createdContractors.get(normName) || '';
                }
                // Skip duplicate invoices
                const invKey = (row.invoiceNumber || '').toLowerCase().trim();
                if (existingInvNums.has(invKey)) { skipped++; continue; }
                const payload: Record<string, unknown> = {
                  [FI.incidentId]: incidentId,
                  [FI.invoiceNumber]: row.invoiceNumber,
                  [FI.totalAmount]: row.totalAmount || 0,
                  [FI.status]: 'draft',
                  [FI.lineItems]: '[]',
                  [FI.approvedAmount]: 0,
                  [FI.contractorId]: contractorId,
                };
                if (row.invoiceDate) { payload[FI.periodStart] = row.invoiceDate; payload[FI.periodEnd] = row.invoiceDate; }
                if (row.periodStart) payload[FI.periodStart] = row.periodStart;
                if (row.periodEnd) payload[FI.periodEnd] = row.periodEnd;
                if (row.invoiceType) payload[FI.invoiceType] = row.invoiceType;
                const metaStr = JSON.stringify({ approvedBy: '', ticketIds: '[]', totalCubicYards: '' });
                payload[FI.reviewerNotes] = `|||IMETA|||${metaStr}|||ENDIMETA|||`;
                await createRecord(TABLES.debrisInvoices, payload);
                existingInvNums.add(invKey); // prevent dups within batch
                created++;
              }
              allResults.push({ file: cf.file.name, type: 'invoice_register', action: `Imported ${created} invoices${skipped > 0 ? `, ${skipped} skipped (already exist)` : ''}${createdContractors.size > 0 ? `, ${createdContractors.size} contractor(s) auto-created` : ''}`, recordsCreated: created });
            },
          });
        } catch (err) {
          allResults.push({
            file: cf.file.name, type: 'invoice_register', action: 'Failed', recordsCreated: 0,
            error: err instanceof Error ? err.message : 'Unknown error',
          });
        }
        updateProgress(`Queued ${cf.file.name} for review`);
      }
    }

    // ── Process Supporting Evidence → AI summary → review queue ──
    if (groups.supporting_evidence && !abortRef.current) {
      for (const cf of groups.supporting_evidence) {
        if (abortRef.current) break;
        setProcessMessage(`Summarizing ${cf.file.name}...`);
        try {
          let evidenceSummary = '';
          let flaggedAmount: number | undefined;
          try {
            const summaryResult = await summarizeSupportingEvidence(cf.file);
            evidenceSummary = summaryResult.summary;
            flaggedAmount = summaryResult.flaggedAmount;
          } catch {
            evidenceSummary = cf.file.name;
          }

          reviewItems.push({
            id: `ev-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            file: cf.file,
            docType: 'supporting_evidence',
            status: 'pending',
            vendor: '', invoiceNumber: '', invoiceDate: '', totalAmount: '', lineItems: [],
            qualityScore: 'complete',
            missingItems: [],
            summary: evidenceSummary,
            laborEntries: [], equipEntries: [],
            warnings: flaggedAmount ? [`Dollar amount detected ($${flaggedAmount.toLocaleString()}) — consider reclassifying as a cost document`] : [],
            extractionMethod: 'claude-text',
            extractionScore: undefined,
            ocrConfidence: 'high' as const,
            capturedDisasterNum: '', capturedFemaCat: '', fileName: cf.file.name,
            evidenceSummary,
            flaggedAmount,
            commit: async (item: ReviewItem) => {
              const invoiceLinkNote = item.selectedInvoiceId
                ? `|||INVOICELINK|||{"invoiceId":"${item.selectedInvoiceId}"}|||ENDINVOICELINK|||`
                : '';
              await createAndUploadDocument(cf.file, {
                title: cf.file.name,
                documentType: item.selectedSubType || 'other',
                notes: [invoiceLinkNote, evidenceSummary].filter(Boolean).join('\n'),
              });
            },
          });
        } catch (err) {
          allResults.push({
            file: cf.file.name, type: 'supporting_evidence', action: 'Failed', recordsCreated: 0,
            error: err instanceof Error ? err.message : 'Unknown error',
          });
        }
        updateProgress(`Queued ${cf.file.name} for review`);
      }
    }

    // ── Process SOW / Work Order and Inspection / Sign-Off documents ──
    for (const sowType of ['sow_work_order', 'inspection_report'] as const) {
      if (groups[sowType] && !abortRef.current) {
        for (const cf of groups[sowType]) {
          if (abortRef.current) break;
          const checklistItemId = sowType === 'sow_work_order' ? 'ndc-003' : 'ndc-004';
          const docLabel = sowType === 'sow_work_order' ? 'SOW / Work Order' : 'Inspection / Sign-Off';
          setProcessMessage(`Processing ${docLabel}: ${cf.file.name}...`);

          try {
            // Try to extract contractor name from filename (e.g., "Himes Electric SOW.pdf")
            const namePart = cf.file.name.replace(/\.(pdf|docx?|xlsx?)$/i, '')
              .replace(/[-_]/g, ' ')
              .replace(/\b(sow|scope|work.?order|inspection|sign.?off|completion|closeout|punch|final|report|proposal|acceptance|statement)\b/gi, '')
              .trim();

            // Fuzzy-match to a contractor
            const matchedContractor = namePart
              ? findContractorByName(namePart, contractors.map(c => ({ id: c.id, companyName: c.companyName })))
              : undefined;

            // Find invoices for this contractor (by contractorId or vendor name)
            const matchedInvoices = matchedContractor
              ? incidentInvoices.filter(inv =>
                  inv.contractorId === String(matchedContractor.id) ||
                  String(inv.vendor).toLowerCase().includes(String(matchedContractor.companyName).toLowerCase()) ||
                  String(matchedContractor.companyName).toLowerCase().includes(String(inv.vendor).toLowerCase()))
              : [];

            // Date-range matching: extract date from filename and match to invoice period
            const docDate = extractDateFromFilename(cf.file.name);
            const periodMatch = docDate ? matchInvoiceByPeriod(matchedInvoices, docDate) : undefined;

            // Auto-select: period match > single invoice > manual pick
            const autoSelectedId = periodMatch?.id
              ?? (matchedInvoices.length === 1 ? matchedInvoices[0].id : undefined);

            const periodNote = docDate
              ? (periodMatch
                ? `Date ${docDate} matches invoice period ${periodMatch.periodStart} — ${periodMatch.periodEnd}`
                : `Date ${docDate} extracted but no invoice period match`)
              : '';

            reviewItems.push({
              id: `${sowType}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
              file: cf.file,
              docType: sowType,
              status: 'pending',
              vendor: matchedContractor?.companyName || namePart || '',
              invoiceNumber: '', invoiceDate: docDate, totalAmount: '', lineItems: [],
              qualityScore: 'complete', missingItems: [],
              summary: `${docLabel} document${matchedContractor ? ` for ${matchedContractor.companyName}` : ''}${periodNote ? ` — ${periodNote}` : ''}`,
              laborEntries: [], equipEntries: [],
              warnings: matchedInvoices.length === 0
                ? [`No invoice found for "${matchedContractor?.companyName || namePart}" — select one manually`]
                : matchedInvoices.length > 1 && !periodMatch
                  ? [`${matchedInvoices.length} invoices found for ${matchedContractor?.companyName} — select the correct period`]
                  : [],
              extractionMethod: 'pdf-text',
              ocrConfidence: 'high' as const,
              capturedContractorId: matchedContractor ? String(matchedContractor.id) : '',
              fileName: cf.file.name,
              // Pre-select invoice by period match or single invoice
              selectedInvoiceId: autoSelectedId,
              // Store the checklist item ID for commit
              selectedSubType: checklistItemId,
              evidenceSummary: `${docLabel} — ${cf.file.name}`,
              commit: async (item: ReviewItem) => {
                // 1. Create document record
                const docId = await createAndUploadDocument(cf.file, {
                  title: cf.file.name,
                  documentType: sowType,
                  notes: item.selectedInvoiceId
                    ? `|||INVOICELINK|||{"invoiceId":"${item.selectedInvoiceId}"}|||ENDINVOICELINK|||`
                    : '',
                });
                // 2. Auto-satisfy the checklist item if an invoice is linked
                if (item.selectedInvoiceId && docId) {
                  try {
                    const FCI = FIELDS.debrisChecklistItems;
                    await createRecord(TABLES.debrisChecklistItems, {
                      [FCI.invoiceId]: item.selectedInvoiceId,
                      [FCI.invoiceType]: 'non_debris_contractor',
                      [FCI.templateItemId]: item.selectedSubType || checklistItemId,
                      [FCI.label]: docLabel,
                      [FCI.evidenceType]: 'file',
                      [FCI.status]: 'satisfied',
                      [FCI.fileDocId]: String(docId),
                      [FCI.fileName]: cf.file.name,
                      [FCI.checkedBy]: user?.email || 'data-intake',
                      [FCI.checkedAt]: new Date().toISOString(),
                      [FCI.notes]: `Auto-linked via Data Intake from ${cf.file.name}`,
                    });
                  } catch (e) {
                    console.warn(`[Processor] Failed to auto-satisfy checklist ${checklistItemId}:`, e);
                  }
                }
              },
            });
          } catch (err) {
            allResults.push({
              file: cf.file.name, type: sowType,
              action: `${docLabel} processing failed`, recordsCreated: 0,
              error: err instanceof Error ? err.message : 'Unknown error',
            });
          }
          updateProgress(`Queued ${cf.file.name} for review`);
        }
      }
    }

    // ── Flag unknown-type files ────────────────────────
    if (groups.unknown && !abortRef.current) {
      for (const cf of groups.unknown) {
        allResults.push({
          file: cf.file.name, type: 'unknown',
          action: 'Not processed — document type could not be determined. Return to classify step, override the type, and reprocess.',
          recordsCreated: 0,
          error: 'Unknown document type',
        });
        updateProgress(`Skipped ${cf.file.name} (unknown type)`);
      }
    }

    setProcessProgress(100);
    setProcessMessage('Complete');
    setResults(allResults);

    if (reviewItems.length > 0) {
      setReviewQueue(reviewItems);
      persistQueue(incidentId, reviewItems);
      setPendingCount(0);
      setReviewIdx(0);
      setStep('review');
      notifications.show({
        title: `${reviewItems.length} document${reviewItems.length !== 1 ? 's' : ''} ready for review`,
        message: 'Verify extracted data before saving to the database',
        color: 'blue',
      });
    } else {
      setStep('results');
      try {
        const totalRecords = allResults.reduce((s, r) => s + r.recordsCreated, 0);
        const totalErrors = allResults.filter(r => r.error).length;
        await createImportLog({
          incidentId,
          fileName: files.map(f => f.file.name).join(', '),
          fileType: 'package_processor',
          contractorId: selectedContractor || '',
          recordCount: files.length,
          newRecords: totalRecords,
          updatedRecords: 0,
          skippedRecords: allResults.filter(r => r.type === 'unknown').length,
          errorCount: totalErrors,
          errors: allResults.filter(r => r.error).map((r, i) => ({ row: i, message: `${r.file}: ${r.error}` })),
          status: 'completed',
          importedBy: user?.email || 'unknown',
          notes: `Package Processor: ${files.length} files, ${totalRecords} records created`,
        });
      } catch { /* non-critical */ }
      notifications.show({
        title: 'Processing Complete',
        message: `${allResults.filter(r => r.recordsCreated > 0).length} files processed successfully`,
        color: 'green',
      });
    }
  };

  // ── Ticket data processor ────────────────────────────
  async function processTicketData(file: File) {
    // ── PDF path: extract text per page → build ticket-to-page mapping ──
    let json: Record<string, string>[];
    const ticketPageMap: Record<string, number> = {}; // ticketNumber → PDF page number
    const isPdf = file.name.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf';
    if (isPdf) {
      setProcessMessage(`Extracting ticket data from PDF ${file.name}...`);
      const pages = await extractPdfPages(file);
      if (pages.length === 0) {
        throw new Error('Could not extract text from PDF. Please export from TDMS as Excel or CSV format.');
      }

      // First pass: scan each page for ticket numbers to build page mapping
      // ADMS format: "DEBRIS LOAD TICKET" header followed by ticket number on same/next line
      const ticketNumRe = /\b(\d{6}-\d{5}-\d{5})\b/;
      for (const { page, text } of pages) {
        const m = ticketNumRe.exec(text);
        if (m) ticketPageMap[m[1]] = page;
      }

      // Chunk all page text for AI parsing
      const fullText = pages.map(p => p.text).join('\n\n');
      const CHUNK_SIZE = 15000;
      const chunks: string[] = [];
      for (let i = 0; i < fullText.length; i += CHUNK_SIZE) {
        chunks.push(fullText.slice(i, i + CHUNK_SIZE));
      }

      const allRecords: Record<string, string>[] = [];
      const seenTicketNums = new Set<string>();

      for (let ci = 0; ci < chunks.length; ci++) {
        setProcessMessage(`AI parsing PDF chunk ${ci + 1}/${chunks.length} (${file.name})...`);
        try {
          const resp = await callClaude([{
            role: 'user',
            content: `Extract all complete load ticket records from this TDMS debris report text. Return ONLY a valid JSON array — no markdown, no explanation. Use EXACTLY these JSON key names regardless of what the report calls the column:\n"Ticket #" (ticket/load number), "Truck #" (truck ID), "Debris Class" (debris type), "CYD" (cubic yards), "Load Call" (load percentage 0-100), "Load Date" (pickup date), "Load Time" (pickup time), "Street Address" (pickup address or road name), "Load Latitude" (GPS latitude decimal), "Load Longitude" (GPS longitude decimal), "Load Monitor" (loading monitor name), "Contractor" (contractor name), "Subcontractor" (subcontractor name), "Disposal Site" (dump site), "Disposal Date", "Disposal Time", "Disposal Monitor", "Mileage" (distance miles), "Rate Code" (CLIN/billing code), "Rate Description", "Transaction Rate" (unit rate $), "Extended Cost" (total $), "Invoice #", "ROENo", "Truck Capacity"\nOmit a key if the value is blank or not present. Return [] if no complete records found in this section.\n\n[SECTION ${ci + 1}/${chunks.length}]\n${chunks[ci]}`,
          }], { system: 'Extract TDMS debris load ticket records. Return ONLY a valid JSON array. Use the specified key names exactly as given.', maxTokens: 8192 });
          const cleaned = (resp ?? '').replace(/```(?:json)?\n?|\n?```/g, '').trim();
          const parsed = JSON.parse(cleaned);
          if (Array.isArray(parsed)) {
            for (const r of parsed) {
              const tn = String((r as any)['Ticket #'] || (r as any)['ticket #'] || '').trim();
              if (tn && seenTicketNums.has(tn)) continue;
              if (tn) seenTicketNums.add(tn);
              const out: Record<string, string> = {};
              for (const [k, v] of Object.entries(r as object)) out[k] = String(v ?? '');
              allRecords.push(out);
            }
          }
        } catch { /* continue to next chunk */ }
      }

      json = allRecords;
      if (json.length === 0) throw new Error('No ticket records found in PDF. Please export from TDMS as Excel or CSV format.');
    } else {
      // ── Excel/CSV path ──────────────────────────────
      const data = await readFileAsArray(file);
      const wb = XLSX.read(data, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      json = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { raw: false, defval: '' });
    }

    // Fetch existing ticket numbers + record IDs + key fields for smart upsert
    const existingTicketMap = new Map<string, number>(); // ticketNumber → record_id
    const existingTicketData = new Map<string, Record<string, string>>(); // ticketNumber → {clinCode, ...}
    try {
      const PAGE = 500;
      let offset = 0;
      let total = Infinity;
      const dedupeFilters: { field: string; operator: 'eq'; value: string }[] = [];
      if (incidentId) dedupeFilters.push({ field: FT.incidentId, operator: 'eq', value: incidentId });
      if (selectedContractor) dedupeFilters.push({ field: FT.contractorId, operator: 'eq', value: selectedContractor });
      while (offset < total) {
        const res = await queryRecords<Record<string, unknown>>(TABLES.debrisLoadTickets, {
          limit: PAGE, offset,
          ...(dedupeFilters.length > 0 ? { filters: dedupeFilters } : {}),
        });
        total = res.total;
        res.records.forEach(r => {
          if (incidentId && String(r[FT.incidentId] || '') !== incidentId) return;
          const tn = String(r[FT.ticketNumber] || '');
          if (tn) {
            existingTicketMap.set(tn, r.record_id as number);
            existingTicketData.set(tn, {
              clinCode: String(r[FT.clinCode] || ''),
              clinDescription: String(r[FT.clinDescription] || ''),
              transactionRate: String(r[FT.transactionRate] || ''),
              extendedCost: String(r[FT.extendedCost] || ''),
              disposalSiteName: String(r[FT.disposalSiteName] || ''),
              contractorId: String(r[FT.contractorId] || ''),
            });
          }
        });
        offset += PAGE;
      }
    } catch (e) { console.warn('[Processor] Dedup query failed', e); }

    let newRecords = 0;
    let skipped = 0;
    let errors = 0;
    let geocodedFromAddress = 0;
    let geocodeMissing = 0;
    const processedTicketNums: string[] = [];

    // ── Auto-populate disposal sites + rate schedules ──
    // Reads from BOTH the uploaded spreadsheet AND existing ticket records in the DB
    try {
      setProcessMessage('Scanning for disposal sites and rate schedules...');
      const uniqueSites = new Set<string>();
      const clinRates = new Map<string, { desc: string; rate: string; contractorId: string }>();

      // Source 1: the uploaded spreadsheet
      json.forEach(raw => {
        const mapped: Record<string, string> = {};
        for (const [tdmsCol, internalName] of Object.entries(TDMS_COLUMN_MAP)) {
          if (raw[tdmsCol] !== undefined) mapped[internalName] = String(raw[tdmsCol]).trim();
        }
        if (mapped.disposalSiteName) uniqueSites.add(mapped.disposalSiteName);
        if (mapped.clinCode && !clinRates.has(mapped.clinCode)) {
          clinRates.set(mapped.clinCode, { desc: mapped.clinDescription || '', rate: mapped.transactionRate || '', contractorId: selectedContractor || '' });
        }
      });

      // Source 2: existing ticket records in the database
      for (const [, existingData] of existingTicketData) {
        if (existingData.disposalSiteName) uniqueSites.add(existingData.disposalSiteName);
      }
      // Also scan existing tickets for CLIN codes (may have been set by previous import)
      for (const [, existingData] of existingTicketData) {
        if (existingData.clinCode && !clinRates.has(existingData.clinCode)) {
          clinRates.set(existingData.clinCode, {
            desc: existingData.clinDescription || '',
            rate: existingData.transactionRate || '',
            contractorId: existingData.contractorId || selectedContractor || '',
          });
        }
      }

      // Fetch more ticket data if existingTicketData didn't have disposal sites
      if (uniqueSites.size === 0 && incidentId) {
        try {
          let offset = 0;
          const PAGE = 500;
          let total = Infinity;
          while (offset < total) {
            const res = await queryRecords<Record<string, unknown>>(TABLES.debrisLoadTickets, {
              limit: PAGE, offset,
              filters: [{ field: FT.incidentId, operator: 'eq' as const, value: incidentId }],
            });
            total = res.total;
            res.records.forEach(r => {
              if (incidentId && String(r[FT.incidentId] || '') !== incidentId) return;
              const dsn = String(r[FT.disposalSiteName] || '').trim();
              if (dsn) uniqueSites.add(dsn);
              const clin = String(r[FT.clinCode] || '').trim();
              const cId = String(r[FT.contractorId] || selectedContractor || '');
              if (clin && !clinRates.has(clin)) {
                clinRates.set(clin, { desc: String(r[FT.clinDescription] || ''), rate: String(r[FT.transactionRate] || ''), contractorId: cId });
              }
            });
            offset += PAGE;
          }
        } catch {}
      }

      // Auto-create disposal sites
      if (uniqueSites.size > 0) {
        setProcessMessage(`Creating disposal sites from ${uniqueSites.size} unique names...`);
        const FS = FIELDS.debrisDisposalSites;
        const existingSitesRes = await queryRecords<Record<string, unknown>>(TABLES.debrisDisposalSites, {
          limit: 200, ...(incidentId ? { filters: [{ field: FS.incidentId, operator: 'eq' as const, value: incidentId }] } : {}),
        });
        const existingSiteNames = new Set(existingSitesRes.records.map(r => String(r[FS.siteName] || '').toLowerCase().trim()));
        let sitesCreated = 0;
        for (const siteName of uniqueSites) {
          if (existingSiteNames.has(siteName.toLowerCase().trim())) continue;
          try {
            const siteType = /dms|staging/i.test(siteName) ? 'dms' : /fds|final|landfill/i.test(siteName) ? 'final_disposal' : /recycle/i.test(siteName) ? 'recycler' : 'dms';
            await createRecord(TABLES.debrisDisposalSites, { [FS.incidentId]: incidentId, [FS.siteName]: siteName, [FS.siteType]: siteType, [FS.status]: 'active' });
            existingSiteNames.add(siteName.toLowerCase().trim());
            sitesCreated++;
          } catch {}
        }
        if (sitesCreated > 0) fetchDisposalSites();
      }

      // Auto-create rate schedules — use contractorId from tickets if not selected
      if (clinRates.size > 0) {
        const FRS = FIELDS.debrisRateSchedules;
        // Group by contractor
        const byContractor = new Map<string, Map<string, { desc: string; rate: string }>>();
        for (const [clinCode, data] of clinRates) {
          const cId = data.contractorId || selectedContractor || '';
          if (!cId) continue;
          if (!byContractor.has(cId)) byContractor.set(cId, new Map());
          byContractor.get(cId)!.set(clinCode, { desc: data.desc, rate: data.rate });
        }
        for (const [contId, clins] of byContractor) {
          setProcessMessage(`Creating rate schedules for contractor ${contId}...`);
          const existingRatesRes = await queryRecords<Record<string, unknown>>(TABLES.debrisRateSchedules, {
            limit: 200, filters: [{ field: FRS.contractorId, operator: 'eq' as const, value: contId }],
          });
          const existingClinCodes = new Set(existingRatesRes.records.map(r => String(r[FRS.lineItemCode] || '').toLowerCase().trim()));
          for (const [clinCode, data] of clins) {
            if (existingClinCodes.has(clinCode.toLowerCase().trim())) continue;
            try {
              await createRecord(TABLES.debrisRateSchedules, {
                [FRS.contractorId]: contId, [FRS.lineItemCode]: clinCode,
                [FRS.description]: data.desc, [FRS.unit]: 'CY',
                [FRS.rateTier1]: parseFloat(data.rate) || 0,
                [FRS.notes]: 'Auto-created from ticket data',
              });
            } catch {}
          }
        }
      }
    } catch {}

    const BATCH_SIZE = 10; // reduced from 50 to avoid overwhelming GAB API
    for (let i = 0; i < json.length; i += BATCH_SIZE) {
      if (abortRef.current) break;
      const batch = json.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map(async (raw) => {
        try {
          const mapped: Record<string, string> = {};
          for (const [tdmsCol, internalName] of Object.entries(TDMS_COLUMN_MAP)) {
            if (raw[tdmsCol] !== undefined) mapped[internalName] = String(raw[tdmsCol]).trim();
          }

          const existingRecordId = mapped.ticketNumber ? existingTicketMap.get(mapped.ticketNumber) : undefined;

          const rawDebrisClass = mapped.debrisType || '';
          const debrisType: DebrisType = TDMS_DEBRIS_CLASS_MAP[rawDebrisClass] ?? 'mixed';

          // ── Geocoding ────────────────────────────────────────────
          let resolvedLat = mapped.pickupLat || '';
          let resolvedLng = mapped.pickupLng || '';
          let resolvedAddress = mapped.pickupAddress || '';

          if (!resolvedLat || !resolvedLng) {
            // Try to geocode from address or road name
            const geocodeQuery = mapped.pickupAddress || mapped.roadName || '';
            if (geocodeQuery) {
              const coords = await geocodeAddress(geocodeQuery);
              if (coords) {
                resolvedLat = String(coords[0]);
                resolvedLng = String(coords[1]);
                resolvedAddress = geocodeQuery; // preserve original in pickupAddress
                geocodedFromAddress++;
              }
              // else: address present but geocode failed — leave coords blank, address stored
            } else {
              // No address and no coords
              resolvedAddress = '[NO_LOCATION]';
              geocodeMissing++;
            }
          }

          // ── Road matching ─────────────────────────────────────────
          let roadMatch: RoadMatch | null = null;
          const latN = parseFloat(resolvedLat);
          const lngN = parseFloat(resolvedLng);
          if (latN && lngN) {
            roadMatch = await matchRoadForCoords(latN, lngN);
          }

          const payload: Record<string, unknown> = {
            [FT.incidentId]:          incidentId,
            [FT.ticketNumber]:        mapped.ticketNumber || '',
            [FT.contractorId]:        selectedContractor || '',
            [FT.debrisType]:          debrisType,
            [FT.calculatedCubicYards]:mapped.calculatedCubicYards || '0',
            [FT.status]:              'verified',
            [FT.loadingMonitorName]:  mapped.loadMonitorName || '',
            [FT.towerMonitorName]:    mapped.disposalMonitorName || '',
            [FT.loadingLoadPct]:      mapped.loadPercentage || '',
            [FT.towerLoadPct]:        mapped.loadPercentage || '',
            [FT.measurementMode]:     'volumetric',
            [FT.notes]:               '',
            // Financial / identity columns
            [FT.subcontractorName]:   mapped.subcontractorName || '',
            [FT.clinCode]:            mapped.clinCode || '',
            [FT.clinDescription]:     mapped.clinDescription || '',
            [FT.distanceMiles]:       mapped.distanceMiles || '',
            [FT.transactionRate]:     mapped.transactionRate || '',
            [FT.extendedCost]:        mapped.extendedCost || '',
            [FT.truckNumber]:         mapped.truckNumber || '',
            [FT.pickupLat]:           resolvedLat,
            [FT.pickupLng]:           resolvedLng,
            [FT.pickupAddress]:       resolvedAddress,
            // Road — prefer ArcGIS match; fall back to whatever the PDF had
            [FT.roadName]:            roadMatch?.roadName || mapped.roadName || '',
            [FT.roadOwner]:           roadMatch?.roadOwner || '',
            [FT.roadOwnerName]:       roadMatch?.roadOwnerName || '',
            [FT.roadClass]:           roadMatch?.roadClass || '',
            [FT.disposalSiteName]:    mapped.disposalSiteName || '',
            [FT.loadDate]:            mapped.loadDate || '',
            [FT.pickupTime]:          mapped.loadTime || '',
            // Formerly TMETA
            [FT.monitorFirmName]:     'Thompson Consulting',
            [FT.loadMonitorId]:       mapped.loadMonitorId || '',
            [FT.disposalMonitorId]:   mapped.disposalMonitorId || '',
            [FT.disposalTime]:        mapped.disposalTime || '',
            [FT.loadPercentage]:      mapped.loadPercentage || '',
            [FT.contractorName]:      mapped.contractorName || '',
            [FT.roeNumber]:           mapped.roeNumber || '',
            [FT.truckCapacity]:       mapped.truckCapacity || '',
            [FT.disposalMonitorName]: mapped.disposalMonitorName || '',
            [FT.transactionQuantity]: mapped.transactionQuantity || '',
            [FT.invoiceNumber]:       mapped.invoiceNumber || '',
            [FT.ticketType]:          mapped.ticketType || '',
            [FT.disposalDate]:        mapped.disposalDate || '',
          };
          if (mapped.tonnage) payload[FT.weight] = mapped.tonnage;

          if (mapped.ticketNumber) processedTicketNums.push(mapped.ticketNumber);
          if (existingRecordId) {
            // Smart upsert: only PATCH if new data fills previously-empty fields
            const existing = existingTicketData.get(mapped.ticketNumber || '') || {};
            const updatePayload: Record<string, unknown> = {};
            // Only add fields that are new (mapped has value, existing is empty)
            if (mapped.clinCode && !existing.clinCode) updatePayload[FT.clinCode] = mapped.clinCode;
            if (mapped.transactionRate && !existing.transactionRate) updatePayload[FT.transactionRate] = mapped.transactionRate;
            if (mapped.extendedCost && !existing.extendedCost) updatePayload[FT.extendedCost] = mapped.extendedCost;
            if (mapped.disposalSiteName && !existing.disposalSiteName) updatePayload[FT.disposalSiteName] = mapped.disposalSiteName;
            if (mapped.clinDescription) updatePayload[FT.clinDescription] = mapped.clinDescription;
            // Skip entirely if nothing new to add
            if (Object.keys(updatePayload).length === 0) {
              skipped++;
              return;
            }
            await updateRecord(TABLES.debrisLoadTickets, existingRecordId, updatePayload);
            skipped++;
          } else {
            await createRecord(TABLES.debrisLoadTickets, payload);
            newRecords++;
          }
        } catch {
          errors++;
        }
      }));

      setProcessMessage(`Importing tickets... ${Math.round(((i + batch.length) / json.length) * 100)}% (${newRecords.toLocaleString()} new)`);
    }

    // Apply any billing data stored from previously-uploaded transaction detail files
    if (newRecords > 0) await applyStoredBillingEnrichment();

    return { total: json.length, newRecords, skipped, errors, geocodedFromAddress, geocodeMissing, ticketNumbers: processedTicketNums, ticketPageMap };
  }

  // ── Truck cert processor ─────────────────────────────
  async function processTruckCert(file: File) {
    const match = file.name.match(/[_-](\d+)[_-]([\d.]+)\.pdf$/i);
    if (!match) return { action: 'Could not parse truck# from filename', created: false };

    const truckNo = match[1];
    const capacityCY = String(Math.round(parseFloat(match[2])));
    const existingNums = new Set(trucks.map(t => t.truckNumber).filter(Boolean));
    const isNew = !existingNums.has(truckNo);

    if (isNew) {
      await createTruck({
        truckNumber: truckNo,
        cubicYardCapacity: capacityCY,
        contractorId: selectedContractor || '',
        certStatus: 'certified',
        certifiedDate: new Date().toISOString().slice(0, 10),
        truckType: 'trailer',
        notes: `Imported from cert PDF: ${file.name}`,
      });
      // Refresh truck list so we can find the new record below
      await fetchTrucks();
    }

    // Upload cert PDF to evidence archive and link back to truck record
    let docRecordId: number | undefined;
    try {
      docRecordId = await createAndUploadDocument(file, {
        title: file.name,
        documentType: 'truck_certification',
        notes: `Truck cert for #${truckNo} (${capacityCY} CY)`,
      });
    } catch { /* upload failed — continue without doc link */ }

    // Link document UUID to truck record TRMETA (need the V2 UUID, not the table record ID)
    if (docRecordId) {
      const { trucks: freshTrucks } = useDebrisStore.getState();
      // Fetch the document record directly to get the V2 UUID from DOCID marker
      let v2DocId = String(docRecordId);
      try {
        const docRec = await fetchRecord<Record<string, unknown>>(TABLES.debrisDocuments, docRecordId);
        const docNotes = String(docRec?.[FIELDS.debrisDocuments.notes] || '');
        const docIdMatch = docNotes.match(/\|\|\|DOCID\|\|\|([^|]+)\|\|\|/);
        if (docIdMatch) v2DocId = docIdMatch[1];
      } catch {}

      const truckRec = freshTrucks.find(t => t.truckNumber === truckNo);
      if (truckRec) {
        // Write TRMETA directly to API — bypass store's updateTruck which
        // re-encodes TRMETA and drops documentId
        const FTrNotes = FIELDS.debrisTrucks.notes;
        const rawNotes = (truckRec as any).notes || '';
        const TRMETA = /\|\|\|TRMETA\|\|\|([\s\S]*?)\|\|\|ENDTRMETA\|\|\|/;
        const mm = rawNotes.match(TRMETA);
        let meta: Record<string, string> = {};
        if (mm) { try { meta = JSON.parse(mm[1]); } catch {} }
        meta.documentId = v2DocId;
        meta.certFileName = file.name;
        meta.certIncidentId = incidentId;
        const cleanNotes = rawNotes.replace(TRMETA, '').trim();
        const newNotes = cleanNotes
          ? `${cleanNotes}\n|||TRMETA|||${JSON.stringify(meta)}|||ENDTRMETA|||`
          : `|||TRMETA|||${JSON.stringify(meta)}|||ENDTRMETA|||`;
        await updateRecord(TABLES.debrisTrucks, truckRec.id, { [FTrNotes]: newNotes });
        await fetchTrucks();
      }
    }

    return {
      action: isNew
        ? `Truck #${truckNo} registered (${capacityCY} CY)${docRecordId ? ' + cert uploaded' : ''}`
        : `Truck #${truckNo} already registered — cert ${docRecordId ? 'uploaded' : 'already on file'}`,
      created: isNew,
    };
  }

  // ── Stored billing enrichment ────────────────────────
  // Reads all BILLMAP markers stored in transaction_detail documents for this
  // incident, then updates any tickets that don't yet have billing data.
  // Called at the end of both processTicketData and processTransactionDetail so
  // uploads can arrive in any order.
  async function applyStoredBillingEnrichment(): Promise<number> {
    if (!incidentId) return 0;

    // 1. Load all BILLMAP docs stored for this incident
    const FD = FIELDS.debrisDocuments;
    const billingMap = new Map<string, { c: string; r: number; e: number; q: number }>();
    try {
      let off = 0, total = Infinity;
      while (off < total) {
        const res = await queryRecords<Record<string, unknown>>(TABLES.debrisDocuments, {
          filters: [{ field: FD.incidentId, operator: 'eq' as const, value: incidentId }],
          limit: 200, offset: off,
        });
        total = res.total;
        if (res.records.length === 0) break;
        res.records.forEach(r => {
          if (String(r[FD.incidentId] || '') !== incidentId) return;
          const notes = String(r[FD.notes] || '');
          const m = notes.match(/\|\|\|BILLMAP\|\|\|([\s\S]*?)\|\|\|ENDBILLMAP\|\|\|/);
          if (!m) return;
          try {
            const map: Record<string, any> = JSON.parse(m[1]);
            Object.entries(map).forEach(([tn, v]) => {
              if (!billingMap.has(tn)) billingMap.set(tn, v as any);
            });
          } catch {}
        });
        off += 200;
      }
    } catch {}

    if (billingMap.size === 0) return 0;

    // 2. Fetch all unenriched tickets for this incident (no clinCode yet)
    const updates: Array<{ rid: number; payload: Record<string, unknown> }> = [];
    try {
      let off = 0, total = Infinity;
      while (off < total) {
        const res = await queryRecords<Record<string, unknown>>(TABLES.debrisLoadTickets, {
          filters: [{ field: FT.incidentId, operator: 'eq' as const, value: incidentId }],
          limit: 500, offset: off,
        });
        total = res.total;
        if (res.records.length === 0) break;
        res.records.forEach(r => {
          if (String(r[FT.incidentId] || '') !== incidentId) return;
          if (r[FT.clinCode]) return; // already has billing data
          const tn = String(r[FT.ticketNumber] || '').trim();
          const b = billingMap.get(tn);
          if (!b) return;
          const rid = (r as any).record_id;
          if (!rid) return;
          const payload: Record<string, unknown> = {};
          if (b.c) payload[FT.clinCode] = b.c;
          if (b.r > 0) payload[FT.transactionRate] = b.r;
          if (b.e > 0) payload[FT.extendedCost] = b.e;
          if (b.q > 0) payload[FT.calculatedCubicYards] = String(b.q);
          if (Object.keys(payload).length > 0) updates.push({ rid, payload });
        });
        off += 500;
      }
    } catch {}

    if (updates.length === 0) return 0;

    // 3. Apply in batches of 20
    let enriched = 0;
    const BATCH = 20;
    for (let i = 0; i < updates.length; i += BATCH) {
      if (abortRef.current) break;
      setProcessMessage(`Applying billing data to tickets... ${Math.min(i + BATCH, updates.length)}/${updates.length}`);
      await Promise.all(updates.slice(i, i + BATCH).map(async ({ rid, payload }) => {
        try { await updateRecord(TABLES.debrisLoadTickets, rid, payload); enriched++; } catch {}
      }));
    }
    return enriched;
  }

  // ── Transaction detail processor ─────────────────────
  async function processTransactionDetail(file: File) {
    const data = await readFileAsArray(file);
    const wb = XLSX.read(data, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { raw: false, defval: '' });

    // Detect per-ticket key column — used for both BILLMAP and inline enrichment
    const ticketNoCol = Object.keys(json[0] || {}).find(k =>
      ['TicketNo', 'Ticket #', 'Ticket No', 'TicketNumber'].includes(k)
    );

    // ── CLIN summary (for invoice reconciliation) ──────
    const clinGroups: Record<string, { qty: number; rate: number; amount: number; count: number; desc: string }> = {};
    json.forEach(row => {
      const clin = row['Service'] || row['Rate Code'] || row['CLIN'] || row['CLIN Code'] || row['clinCode'] || '';
      if (!clin) return;
      const qty = parseFloat(row['Quantity'] || row['CYD'] || row['Qty'] || '0') || 0;
      const rate = parseFloat(row['Rate'] || row['Transaction Rate'] || row['Unit Rate'] || '0') || 0;
      const totalRaw = row[' Total '] || row['Total'] || row['Extended Cost'] || row['Amount'] || '0';
      const amount = parseFloat(String(totalRaw).replace(/[$,\s]/g, '')) || 0;
      const desc = row['Description'] || row['Rate Description'] || '';

      if (!clinGroups[clin]) clinGroups[clin] = { qty: 0, rate: 0, amount: 0, count: 0, desc: '' };
      clinGroups[clin].qty += qty;
      clinGroups[clin].amount += amount;
      if (rate > 0) clinGroups[clin].rate = rate;
      clinGroups[clin].count++;
      if (desc && !clinGroups[clin].desc) clinGroups[clin].desc = desc;
    });

    const totalAmount = Object.values(clinGroups).reduce((s, g) => s + g.amount, 0);

    // ── Build per-ticket billing map for future imports ──
    // Stored alongside TXDATA so applyStoredBillingEnrichment can find it later.
    const billMapRaw: Record<string, { c: string; r: number; e: number; q: number }> = {};
    if (ticketNoCol) {
      json.forEach(row => {
        const tn = String(row[ticketNoCol] || '').trim();
        if (!tn) return;
        const clin = row['Service'] || row['Rate Code'] || row['CLIN'] || '';
        const rate = parseFloat(row['Rate'] || row['Transaction Rate'] || '0') || 0;
        const totalRaw = row[' Total '] || row['Total'] || row['Extended Cost'] || '0';
        const cost = parseFloat(String(totalRaw).replace(/[$,\s]/g, '')) || 0;
        const qty = parseFloat(row['Quantity'] || row['CYD'] || '0') || 0;
        billMapRaw[tn] = { c: clin, r: rate, e: cost, q: qty };
      });
    }

    // Persist parsed data as an evidence document so Reconciliation can use it
    const txData = {
      fileName: file.name,
      contractorId: selectedContractor || '',
      periodStart: periodStart || '',
      periodEnd: periodEnd || '',
      parsedAt: new Date().toISOString(),
      totalRows: json.length,
      totalAmount,
      clinGroups,
    };
    const billMapMarker = Object.keys(billMapRaw).length > 0
      ? `|||BILLMAP|||${JSON.stringify(billMapRaw)}|||ENDBILLMAP|||`
      : '';
    await createAndUploadDocument(file, {
      title: file.name,
      documentType: 'transaction_detail',
      notes: `|||TXDATA|||${JSON.stringify(txData)}|||ENDTXDATA|||${billMapMarker}`,
    });

    // ── Per-ticket billing enrichment ─────────────────
    // If the file has a TicketNo column, match against existing tickets in this
    // incident and update billing fields (CLIN, rate, cost, CY) on each match.
    let ticketsEnriched = 0;
    if (ticketNoCol && incidentId) {
      setProcessMessage(`Enriching tickets with billing data from ${file.name}...`);

      // Fetch existing tickets for this incident (ticketNumber → record_id map)
      const ticketIdMap = new Map<string, number>();
      try {
        let off = 0, total = Infinity;
        while (off < total) {
          const res = await queryRecords<Record<string, unknown>>(TABLES.debrisLoadTickets, {
            filters: [{ field: FT.incidentId, operator: 'eq' as const, value: incidentId }],
            limit: 500, offset: off,
          });
          total = res.total;
          res.records.forEach(r => {
            if (String(r[FT.incidentId] || '') !== incidentId) return;
            const tn = String(r[FT.ticketNumber] || '').trim();
            const rid = (r as any).record_id;
            if (tn && rid) ticketIdMap.set(tn, rid);
          });
          off += 500;
        }
      } catch (e) { console.warn('[Processor] Ticket fetch for enrichment failed', e); }

      if (ticketIdMap.size > 0) {
        // Collect updates: one per unique ticket number (last row wins if dupes)
        const updates = new Map<number, Record<string, unknown>>();
        json.forEach(row => {
          const tn = String(row[ticketNoCol] || '').trim();
          if (!tn) return;
          const rid = ticketIdMap.get(tn);
          if (!rid) return;
          const clin = row['Service'] || row['Rate Code'] || row['CLIN'] || '';
          const rate = parseFloat(row['Rate'] || row['Transaction Rate'] || '0') || 0;
          const totalRaw = row[' Total '] || row['Total'] || row['Extended Cost'] || '0';
          const cost = parseFloat(String(totalRaw).replace(/[$,\s]/g, '')) || 0;
          const qty = parseFloat(row['Quantity'] || row['CYD'] || '0') || 0;
          const desc = row['Description'] || row['Rate Description'] || '';
          const update: Record<string, unknown> = {};
          if (clin) update[FT.clinCode] = clin;
          if (rate > 0) update[FT.transactionRate] = rate;
          if (cost > 0) update[FT.extendedCost] = cost;
          if (qty > 0) update[FT.calculatedCubicYards] = String(qty);
          if (desc) update[FT.clinDescription] = desc;
          if (Object.keys(update).length > 0) updates.set(rid, update);
        });

        // Apply updates in batches of 20
        const updateEntries = [...updates.entries()];
        const BATCH = 20;
        for (let i = 0; i < updateEntries.length; i += BATCH) {
          if (abortRef.current) break;
          const batch = updateEntries.slice(i, i + BATCH);
          await Promise.all(batch.map(async ([rid, payload]) => {
            try {
              await updateRecord(TABLES.debrisLoadTickets, rid, payload);
              ticketsEnriched++;
            } catch {}
          }));
          setProcessMessage(`Enriching tickets... ${Math.min(i + BATCH, updateEntries.length)}/${updateEntries.length}`);
        }
      }
    }

    // Also run the durable enrichment path in case tickets already exist
    // that the inline enrichment above missed (e.g. pagination gaps)
    const additionalEnriched = await applyStoredBillingEnrichment();

    return {
      rows: json.length,
      clinGroups: Object.keys(clinGroups).length,
      totalAmount,
      ticketsEnriched: ticketsEnriched + additionalEnriched,
    };
  }

  // ── Employee roster helpers ───────────────────────────

  /** Parse an XLSX/CSV employee roster and store it as a debrisDocuments record. */
  async function processEmployeeRoster(file: File): Promise<{ employeeCount: number; error?: string }> {
    const data = await readFileAsArray(file);
    const wb = XLSX.read(data, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    if (!ws) return { employeeCount: 0, error: 'No worksheets found in file' };

    const rows = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { raw: false, defval: '' });
    const employees: RosterEmployee[] = [];

    for (const row of rows) {
      const name = String(
        row['Employee Name'] || row['Name'] || row['Full Name'] || ''
      ).trim();
      if (!name || name === 'Employee Name' || name === 'Name') continue;

      const hourlyRateRaw = String(
        row['Hourly Rate'] || row['Rate'] || row['Base Rate'] || row['Pay Rate'] || '0'
      ).replace(/[$,]/g, '');

      employees.push({
        name,
        id: String(row['Employee ID'] || row['Employee Number'] || row['ID'] || '').trim(),
        ercDistrict: String(row['ERC District'] || row['District'] || row['ERC'] || '').trim(),
        department: String(row['Department'] || row['Dept'] || '').trim(),
        classification: String(row['Classification'] || row['Job Title'] || row['Pay Title'] || row['Work Category'] || '').trim(),
        shift: String(row['Shift'] || '').trim(),
        hourlyRate: parseFloat(hourlyRateRaw) || 0,
        fleetNumber: String(row['Fleet Number'] || row['Fleet #'] || '').trim() || undefined,
      });
    }

    if (employees.length === 0) {
      return { employeeCount: 0, error: 'No valid employee rows found — check column headers' };
    }

    const FD = FIELDS.debrisDocuments;
    await createRecord(TABLES.debrisDocuments, {
      [FD.incidentId]: incidentId,
      [FD.title]: `Employee Roster — ${file.name}`,
      [FD.documentType]: 'roster_data',
      [FD.uploadedBy]: 'Package Processor',
      [FD.notes]: JSON.stringify(employees),
    });

    return { employeeCount: employees.length };
  }

  /** Load the most recent employee roster stored for this incident. Returns [] if none found. */
  async function loadRosterForIncident(): Promise<RosterEmployee[]> {
    try {
      const FD = FIELDS.debrisDocuments;
      const res = await queryRecords<Record<string, unknown>>(TABLES.debrisDocuments, {
        limit: 10, offset: 0,
        filters: [
          { field: FD.incidentId, operator: 'eq' as const, value: incidentId },
          { field: FD.documentType, operator: 'eq' as const, value: 'roster_data' },
        ],
      });
      const rec = res?.records?.[0];
      if (!rec) return [];
      const raw = String(rec[FD.notes] || '');
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /** Match an employee name/id against the roster. ID match takes precedence over name. */
  function matchEmployeeToRoster(name: string, id: string, roster: RosterEmployee[]): RosterEmployee | null {
    if (roster.length === 0) return null;
    // ID match first (exact)
    if (id) {
      const byId = roster.find(r => r.id && r.id === id);
      if (byId) return byId;
    }
    // Normalized name match
    const normalize = (s: string) => s.toLowerCase().replace(/[^a-z]/g, '');
    const normName = normalize(name);
    if (!normName) return null;
    return roster.find(r => normalize(r.name) === normName) ?? null;
  }

  /** Call Claude to generate a one-line summary of a supporting evidence document. */
  async function summarizeSupportingEvidence(file: File): Promise<{ summary: string; flaggedAmount?: number }> {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    let text = '';
    if (ext === 'pdf') {
      text = await extractPdfText(file);
    } else if (['xlsx', 'xls', 'csv'].includes(ext)) {
      const data = await readFileAsArray(file);
      const wb = XLSX.read(data, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<any[]>(ws, { raw: false, defval: '', header: 1 });
      text = rows.slice(0, 20).map(r => (r as string[]).join('\t')).join('\n');
    } else {
      // Images / unknown — use filename as fallback
      return { summary: `Supporting document: ${file.name}` };
    }

    if (!text || text.trim().length < 20) {
      return { summary: `Supporting document: ${file.name}` };
    }

    const excerpt = text.slice(0, 3000);
    const response = await callClaude([{
      role: 'user',
      content: `Read this document excerpt and return ONLY valid JSON with two keys: "summary" (one-line description ≤120 chars) and "flaggedAmount" (number or null — only set if a clear total dollar amount is present).\n\n${excerpt}`,
    }], {
      system: 'You are a FEMA debris documentation assistant.',
      maxTokens: 256,
      model: 'claude-haiku-4-5-20251001',
    });

    try {
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return {
          summary: String(parsed.summary || file.name),
          flaggedAmount: parsed.flaggedAmount ? parseFloat(String(parsed.flaggedAmount).replace(/[$,]/g, '')) || undefined : undefined,
        };
      }
    } catch { /* fall through */ }

    return { summary: response.slice(0, 120) || file.name };
  }

  // ── Force Account Excel processor ────────────────────
  async function processForceAccountExcel(file: File, roster: RosterEmployee[] = []) {
    const data = await readFileAsArray(file);
    const wb = XLSX.read(data, { type: 'array' });

    // Detect disaster number from filename
    const drMatch = file.name.match(/DR[-_\s]?(\d{4})/i);
    const disasterNumber = drMatch ? `DR-${drMatch[1]}` : (file.name.toLowerCase().includes('milton') ? 'DR-4834' : file.name.toLowerCase().includes('helene') ? 'DR-4828' : '');
    const femaCategory = file.name.toLowerCase().includes('cat a') ? 'Cat A' : 'Cat B';

    // Find labor and equipment sheets by inspecting headers in each sheet
    let laborSheet: XLSX.WorkSheet | null = null;
    let laborSheetName = '';
    let equipSheet: XLSX.WorkSheet | null = null;

    for (const sheetName of wb.SheetNames) {
      const ws = wb.Sheets[sheetName];
      if (!ws['!ref']) continue;
      const firstRow = XLSX.utils.sheet_to_json<any[]>(ws, { raw: false, defval: '', header: 1 })[0] || [];
      const hStr = Array.isArray(firstRow) ? firstRow.join(',').toLowerCase() : '';

      if (!laborSheet && (hStr.includes('employee name') || (hStr.includes('name') && hStr.includes('id'))) && (hStr.includes('service date') || hStr.includes('date')) && (hStr.includes('hours') || hStr.includes('hrs'))) {
        laborSheet = ws;
        laborSheetName = sheetName;
      }
      if (!equipSheet && (hStr.includes('fleet') || hStr.includes('asset') || hStr.includes('vehicle')) && (hStr.includes('hours') || hStr.includes('hrs')) && (hStr.includes('date') || hStr.includes('service'))) {
        equipSheet = ws;
      }
    }

    let laborCreated = 0;
    let laborErrors = 0;
    let equipCreated = 0;
    const rosterUnmatched: string[] = [];

    // ── Labor records ──────────────────────────────────
    if (laborSheet) {
      const rows = XLSX.utils.sheet_to_json<Record<string, string>>(laborSheet, { raw: false, defval: '' });
      const BATCH_SIZE = 50;

      for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        if (abortRef.current) break;
        const batch = rows.slice(i, i + BATCH_SIZE);

        await Promise.all(batch.map(async (row) => {
          try {
            // Normalize name — support both column header styles
            const empName = String(
              row['Employee Name'] || row['Name'] || ''
            ).trim();
            const rawDate = String(
              row['Service Date'] || row['Date'] || ''
            ).trim();

            // Skip blank, header-repeat, or #N/A rows
            if (!empName || !rawDate) return;
            if (empName === 'Employee Name' || empName === 'Name') return;
            if (empName.includes('#N/A') || rawDate.includes('#N/A')) return;

            const empId = String(row['Employee Number'] || row['Employee ID'] || row['ID'] || '').trim();
            const dept = String(row['Department'] || '').trim();
            const classification = String(row['Work Category'] || row['Pay Code'] || row['Classification'] || '').trim();
            const hoursRegular = parseFloat(row['Regular Hours'] || row['Regular'] || '0') || 0;
            const hoursOT = parseFloat(row['Overtime Hours'] || row['Overtime'] || '0') || 0;
            const totalHours = parseFloat(row['Total Hours Worked'] || row['Total Hours'] || row['Hours'] || '0') || 0;

            // Skip zero-hour rows (corrections, totals, etc.)
            if (hoursRegular === 0 && hoursOT === 0 && totalHours === 0) return;

            const laborCostRaw = row['Labor Cost'] || row['Regular Salary Expense'] || row['Totaled Amount'] || row['Amount'] || '';
            const totalCost = parseFloat(String(laborCostRaw).replace(/[$,]/g, '')) || 0;
            const timeIn = String(row['Time In (Military)'] || row['Time In'] || '').trim();
            const timeOut = String(row['Time Out (Military)'] || row['Time Out'] || '').trim();

            // Roster match — pull authoritative hourly rate and classification
            const rosterMatch = matchEmployeeToRoster(empName, empId, roster);
            const hourlyRate = rosterMatch ? rosterMatch.hourlyRate : 0;
            const resolvedClassification = classification || (rosterMatch ? rosterMatch.classification : '');
            if (!rosterMatch && empName) rosterUnmatched.push(empName);

            // Normalize date: Excel serials → ISO, or parse MM/DD/YYYY etc.
            let dateWorked = rawDate;
            const serial = parseFloat(rawDate);
            if (!isNaN(serial) && serial > 1000) {
              // Excel date serial
              const d = XLSX.SSF.parse_date_code(serial);
              if (d) dateWorked = `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
            } else {
              // Try MM/DD/YYYY → YYYY-MM-DD
              const mdy = rawDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
              if (mdy) dateWorked = `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`;
            }

            await createRecord(TABLES.debrisForceAccountLabor, {
              [FL.incidentId]:    incidentId,
              [FL.employeeName]:  empName,
              [FL.employeeId]:    empId,
              [FL.department]:    dept,
              [FL.classification]:resolvedClassification,
              [FL.dateWorked]:    dateWorked,
              [FL.hoursRegular]:  String(hoursRegular),
              [FL.hoursOvertime]: String(hoursOT),
              [FL.hourlyRate]:    String(hourlyRate),
              [FL.totalCost]:     String(totalCost),
              [FL.disasterNumber]:disasterNumber,
              [FL.femaCategory]:  femaCategory,
              [FL.sourceFile]:    file.name,
              [FL.timeIn]:        timeIn,
              [FL.timeOut]:       timeOut,
              [FL.totalHours]:    String(totalHours),
              [FL.sourceSheet]:   laborSheetName,
            });
            laborCreated++;
          } catch {
            laborErrors++;
          }
        }));

        setProcessMessage(`Importing FA labor from ${file.name}... ${Math.round(((i + batch.length) / rows.length) * 100)}% (${laborCreated.toLocaleString()} records)`);
      }
    }

    // ── Equipment records ──────────────────────────────
    if (equipSheet) {
      const rows = XLSX.utils.sheet_to_json<Record<string, string>>(equipSheet, { raw: false, defval: '' });
      const BATCH_SIZE = 50;

      for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        if (abortRef.current) break;
        const batch = rows.slice(i, i + BATCH_SIZE);

        await Promise.all(batch.map(async (row) => {
          try {
            const fleetNum = String(row['Fleet #'] || row['Asset #'] || row['Vehicle #'] || row['Fleet Number'] || '').trim();
            const description = String(row['Description'] || row['Equipment Description'] || row['Vehicle Description'] || '').trim();
            const rawDate = String(row['Date'] || row['Service Date'] || '').trim();
            if (!rawDate || (!fleetNum && !description)) return;
            if (rawDate.includes('#N/A')) return;

            const hoursUsed = parseFloat(row['Hours'] || row['Hours Used'] || row['Total Hours'] || '0') || 0;
            if (hoursUsed === 0) return;

            let dateUsed = rawDate;
            const serial = parseFloat(rawDate);
            if (!isNaN(serial) && serial > 1000) {
              const d = XLSX.SSF.parse_date_code(serial);
              if (d) dateUsed = `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
            } else {
              const mdy = rawDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
              if (mdy) dateUsed = `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`;
            }

            await createRecord(TABLES.debrisForceAccountEquipment, {
              [FE.incidentId]: incidentId,
              [FE.fleetNumber]: fleetNum,
              [FE.equipmentType]: '',
              [FE.description]: description,
              [FE.dateUsed]: dateUsed,
              [FE.hoursUsed]: String(hoursUsed),
              [FE.hourlyRate]: '0',
              [FE.totalCost]: '0',
              [FE.disasterNumber]: disasterNumber,
              [FE.femaCategory]: femaCategory,
              [FE.sourceFile]: file.name,
            });
            equipCreated++;
          } catch { /* skip */ }
        }));
      }
    }

    return { laborCreated, laborErrors, equipCreated, rosterUnmatched };
  }

  // ── Force Account ANNEX E-3 PDF form processor ───────
  // Renders every page with PDF.js → JPEG → Claude Vision in batches of 15.
  // No file-size limit — works for 1000+ page scanned PDFs.
  async function processForceAccountForm(file: File, extractOnly = false): Promise<{ laborCreated: number; equipCreated: number; pagesProcessed: number; totalPages: number; aiError?: string; entries?: any[]; disasterNumber?: string; femaCategory?: string }> {
    if (!extractOnly) {
      await createAndUploadDocument(file, {
        title: file.name,
        documentType: 'force_account',
        notes: `ANNEX E-3 Force Account form uploaded via Package Processor`,
      });
    }

    // Configure PDF.js worker — Vite resolves this to the bundled asset URL
    if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.min.mjs',
        import.meta.url,
      ).href;
    }

    // Load the PDF
    let pdfDoc: any;
    try {
      const arrayBuffer = await file.arrayBuffer();
      pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    } catch (loadErr) {
      return {
        laborCreated: 0, equipCreated: 0, pagesProcessed: 0, totalPages: 0,
        aiError: `Failed to load PDF: ${loadErr instanceof Error ? loadErr.message : 'unknown'}`,
      };
    }

    const totalPages: number = pdfDoc.numPages;
    const PAGES_PER_BATCH = 15;
    const allEntries: any[] = [];
    let stormEvent = file.name.toLowerCase().includes('milton') ? 'MILTON' : file.name.toLowerCase().includes('helene') ? 'HELENE' : '';
    let femaCategory = 'Cat B';
    let pagesProcessed = 0;

    for (let pageStart = 1; pageStart <= totalPages; pageStart += PAGES_PER_BATCH) {
      if (abortRef.current) break;

      const pageEnd = Math.min(pageStart + PAGES_PER_BATCH - 1, totalPages);
      setProcessMessage(`${file.name}: extracting pages ${pageStart}–${pageEnd} of ${totalPages} (${allEntries.length} records so far)...`);

      // Render pages to JPEG images
      const imageBlocks: any[] = [];
      for (let pageNum = pageStart; pageNum <= pageEnd; pageNum++) {
        try {
          const page = await pdfDoc.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(viewport.width);
          canvas.height = Math.round(viewport.height);
          const ctx = canvas.getContext('2d');
          if (!ctx) continue;
          await page.render({ canvasContext: ctx, viewport }).promise;
          const jpeg = canvas.toDataURL('image/jpeg', 0.75);
          const base64 = jpeg.split(',')[1];
          if (base64) {
            imageBlocks.push({
              type: 'image',
              source: { type: 'base64', media_type: 'image/jpeg', data: base64 },
            });
          }
        } catch { /* skip bad page */ }
      }

      if (imageBlocks.length === 0) { pagesProcessed = pageEnd; continue; }

      // Send batch to Claude Vision
      try {
        const aiResponse = await callClaude([{
          role: 'user',
          content: [
            ...imageBlocks,
            {
              type: 'text',
              text: `These are pages ${pageStart}–${pageEnd} of ${totalPages} from a City of Tampa ANNEX E-3 Storm/Event Work Cost Reporting Form for FEMA disaster reimbursement.

Extract ALL personnel and equipment entries visible on these pages.

Return ONLY valid JSON:
{
  "stormEvent": "storm name seen on form (e.g. MILTON, HELENE)",
  "femaCategory": "Cat A or Cat B",
  "entries": [
    {
      "type": "labor",
      "ercDistrict": "ERC # or division (e.g. ERC 1, ERC-2, HR, EOC)",
      "dateWorked": "YYYY-MM-DD",
      "locationOfWork": "location from form header",
      "supervisorName": "supervisor name",
      "employeeId": "employee ID number",
      "employeeName": "full name",
      "department": "department abbreviation",
      "timeIn": "HH:MM",
      "timeOut": "HH:MM",
      "hoursWorked": 8.0,
      "classification": "job title or class"
    },
    {
      "type": "equipment",
      "ercDistrict": "ERC # or division",
      "dateWorked": "YYYY-MM-DD",
      "locationOfWork": "location",
      "fleetNumber": "asset or fleet number",
      "equipmentDescription": "description of equipment",
      "timeIn": "HH:MM",
      "timeOut": "HH:MM",
      "hoursUsed": 8.0
    }
  ]
}

Rules: Extract EVERY person and EVERY piece of equipment listed. Use the date from the form header for any rows that don't have their own date. If a page has no entries, return an empty entries array.`,
            },
          ],
        }], {
          system: 'You are a FEMA compliance analyst extracting data from scanned ANNEX E-3 government forms. Be thorough — capture every row. Return ONLY valid JSON.',
          maxTokens: 4096,
        });

        // Parse response
        const jsonMatch = aiResponse.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          let extracted: any;
          try {
            extracted = JSON.parse(jsonMatch[0]);
          } catch {
            let s = jsonMatch[0];
            s = s.replace(/,\s*\{[^}]*$/, '');
            const op = (s.match(/\[/g) || []).length - (s.match(/\]/g) || []).length;
            for (let i = 0; i < op; i++) s += ']';
            const ob = (s.match(/\{/g) || []).length - (s.match(/\}/g) || []).length;
            for (let i = 0; i < ob; i++) s += '}';
            try { extracted = JSON.parse(s); } catch { extracted = {}; }
          }
          if (extracted.stormEvent && !stormEvent) stormEvent = String(extracted.stormEvent || '').toLowerCase();
          if (extracted.femaCategory) femaCategory = String(extracted.femaCategory || 'Cat B');
          if (Array.isArray(extracted.entries)) allEntries.push(...extracted.entries);
        }
      } catch { /* batch failed — continue to next */ }

      pagesProcessed = pageEnd;

      // 2s between batches within the same file (5s is reserved for between files)
      if (pageEnd < totalPages && !abortRef.current) {
        await new Promise(r => setTimeout(r, 2000));
      }
    }

    // Resolve disaster number
    const stormLower = stormEvent.toLowerCase();
    const drFromName = file.name.toLowerCase().includes('milton') ? 'DR-4834' : file.name.toLowerCase().includes('helene') ? 'DR-4828' : '';
    const disasterNumber = stormLower.includes('milton') ? 'DR-4834' : stormLower.includes('helene') ? 'DR-4828' : drFromName;

    // In extract-only mode, return entries without writing to DB
    if (extractOnly) {
      return { laborCreated: 0, equipCreated: 0, pagesProcessed, totalPages, entries: allEntries, disasterNumber, femaCategory };
    }

    // Create records from all accumulated entries
    let laborCreated = 0;
    let equipCreated = 0;

    for (const entry of allEntries) {
      if (entry.type === 'labor') {
        const empName = String(entry.employeeName || '').trim();
        if (!empName) continue;
        try {
          await createRecord(TABLES.debrisForceAccountLabor, {
            [FL.incidentId]:    incidentId,
            [FL.employeeName]:  empName,
            [FL.employeeId]:    String(entry.employeeId || ''),
            [FL.department]:    String(entry.department || ''),
            [FL.classification]:String(entry.classification || ''),
            [FL.dateWorked]:    String(entry.dateWorked || ''),
            [FL.hoursRegular]:  String(parseFloat(entry.hoursWorked) || 0),
            [FL.hoursOvertime]: '0',
            [FL.hourlyRate]:    '0',
            [FL.totalCost]:     '0',
            [FL.ercDistrict]:   String(entry.ercDistrict || ''),
            [FL.disasterNumber]:disasterNumber,
            [FL.femaCategory]:  femaCategory,
            [FL.locationOfWork]:String(entry.locationOfWork || ''),
            [FL.sourceFile]:    file.name,
            [FL.timeIn]:        String(entry.timeIn || ''),
            [FL.timeOut]:       String(entry.timeOut || ''),
          });
          laborCreated++;
        } catch { /* skip */ }
      } else if (entry.type === 'equipment') {
        const desc = String(entry.equipmentDescription || '').trim();
        if (!desc) continue;
        try {
          await createRecord(TABLES.debrisForceAccountEquipment, {
            [FE.incidentId]:    incidentId,
            [FE.fleetNumber]:   String(entry.fleetNumber || ''),
            [FE.equipmentType]: '',
            [FE.description]:   desc,
            [FE.dateUsed]:      String(entry.dateWorked || ''),
            [FE.hoursUsed]:     String(parseFloat(entry.hoursUsed) || 0),
            [FE.hourlyRate]:    '0',
            [FE.totalCost]:     '0',
            [FE.ercDistrict]:   String(entry.ercDistrict || ''),
            [FE.disasterNumber]:disasterNumber,
            [FE.femaCategory]:  femaCategory,
            [FE.locationOfWork]:String(entry.locationOfWork || ''),
            [FE.sourceFile]:    file.name,
            [FE.timeIn]:        String(entry.timeIn || ''),
            [FE.timeOut]:       String(entry.timeOut || ''),
          });
          equipCreated++;
        } catch { /* skip */ }
      }
    }

    return { laborCreated, equipCreated, pagesProcessed, totalPages };
  }

  // ── Commit approved review items ──────────────────────
  const commitApproved = async () => {
    const approved = reviewQueue.filter(i => i.status === 'approved');
    if (approved.length === 0) return;
    setStep('process');
    setProcessProgress(0);
    setProcessMessage(`Saving ${approved.length} approved document${approved.length !== 1 ? 's' : ''}...`);

    const commitResults: ProcessResult[] = [...results];
    let done = 0;
    for (const item of approved) {
      try {
        setProcessMessage(`Saving ${item.file.name}...`);
        await item.commit(item);
        commitResults.push({
          file: item.file.name,
          type: item.docType,
          action: `Approved — ${item.vendor || 'No vendor'} | #${item.invoiceNumber} | $${parseFloat(item.totalAmount || '0').toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
          recordsCreated: 2,
        });
      } catch (err) {
        commitResults.push({
          file: item.file.name,
          type: item.docType,
          action: 'Commit failed',
          recordsCreated: 0,
          error: err instanceof Error ? err.message : 'Unknown error',
        });
      }
      done++;
      setProcessProgress(Math.round((done / approved.length) * 100));
    }

    setResults(commitResults);
    setStep('results');
    const remaining = reviewQueue.filter(i => i.status === 'pending');
    if (remaining.length === 0) clearQueue(incidentId);
    else persistQueue(incidentId, remaining);

    try {
      const totalRecords = commitResults.reduce((s, r) => s + r.recordsCreated, 0);
      await createImportLog({
        incidentId,
        fileName: files.map(f => f.file.name).join(', '),
        fileType: 'package_processor',
        contractorId: selectedContractor || '',
        recordCount: files.length,
        newRecords: totalRecords,
        updatedRecords: 0,
        skippedRecords: reviewQueue.filter(i => i.status === 'rejected').length,
        errorCount: commitResults.filter(r => r.error).length,
        errors: commitResults.filter(r => r.error).map((r, i) => ({ row: i, message: `${r.file}: ${r.error}` })),
        status: 'completed',
        importedBy: user?.email || 'unknown',
        notes: `Package Processor: ${approved.length} approved, ${reviewQueue.filter(i => i.status === 'rejected').length} rejected`,
      });
    } catch { /* non-critical */ }

    notifications.show({
      title: 'Committed',
      message: `${approved.length} document${approved.length !== 1 ? 's' : ''} saved successfully`,
      color: 'green',
    });
  };

  // ── Reset ─────────────────────────────────────────────
  const resetAll = () => {
    clearQueue(incidentId);
    setPendingCount(0);
    setStep('upload');
    setFiles([]);
    setSelectedContractor(null);
    setPeriodStart('');
    setPeriodEnd('');
    setProcessProgress(0);
    setProcessMessage('');
    setResults([]);
    setReviewQueue([]);
    setReviewIdx(0);
    setReviewPdfUrl(null);
    abortRef.current = false;
  };

  // ── Backfill road matching for existing tickets ────────
  const [roadBackfillRunning, setRoadBackfillRunning] = useState(false);
  const [roadBackfillMsg, setRoadBackfillMsg] = useState('');

  const runRoadBackfill = async () => {
    if (!incidentId) return;
    setRoadBackfillRunning(true);
    setRoadBackfillMsg('Loading tickets...');

    try {
      // Fetch all tickets for this incident that are missing roadOwnerName
      const PAGE = 500;
      let offset = 0, total = Infinity;
      const unmatched: { id: number; lat: string; lng: string }[] = [];
      while (offset < total) {
        const res = await queryRecords<Record<string, unknown>>(TABLES.debrisLoadTickets, {
          limit: PAGE, offset,
          filters: [{ field: FT.incidentId, operator: 'eq' as const, value: incidentId }],
        });
        total = res.total;
        res.records.forEach((r: any) => {
          if (String(r[FT.incidentId] || '') !== incidentId) return;
          const lat = String(r[FT.pickupLat] || '');
          const lng = String(r[FT.pickupLng] || '');
          const hasOwner = String(r[FT.roadOwnerName] || '').trim();
          if (lat && lng && parseFloat(lat) && parseFloat(lng) && !hasOwner) {
            unmatched.push({ id: r.record_id as number, lat, lng });
          }
        });
        offset += PAGE;
      }

      if (unmatched.length === 0) {
        setRoadBackfillMsg('All tickets already have road match data.');
        setRoadBackfillRunning(false);
        return;
      }

      let matched = 0, failed = 0;
      const CONCURRENCY = 10;
      for (let i = 0; i < unmatched.length; i += CONCURRENCY) {
        const batch = unmatched.slice(i, i + CONCURRENCY);
        setRoadBackfillMsg(`Matching roads… ${i}/${unmatched.length} (${matched} matched, ${failed} no result)`);
        await Promise.all(batch.map(async (t) => {
          const road = await matchRoadForCoords(parseFloat(t.lat), parseFloat(t.lng));
          if (road) {
            try {
              await updateRecord(TABLES.debrisLoadTickets, t.id, {
                [FT.roadName]:      road.roadName,
                [FT.roadOwner]:     road.roadOwner,
                [FT.roadOwnerName]: road.roadOwnerName,
                [FT.roadClass]:     road.roadClass,
              });
              matched++;
            } catch { failed++; }
          } else {
            failed++;
          }
        }));
      }

      invalidateTicketCache();
      setRoadBackfillMsg(`Done — ${matched} tickets matched, ${failed} without a road result.`);
    } catch (e) {
      setRoadBackfillMsg(`Error: ${String(e)}`);
    }
    setRoadBackfillRunning(false);
  };

  // ═══════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={3} c="black">Document Intake</Title>
          <Text size="sm" c="dimmed">
            Drop any files — AI classifies, extracts data, and routes everything
          </Text>
        </div>
        {step !== 'upload' && step !== 'process' && (
          <Button size="xs" variant="subtle" onClick={resetAll} leftSection={<IconRefresh size={14} />}>
            Start Over
          </Button>
        )}
      </Group>

      {/* ═══════════════ STEP 1: UPLOAD ═══════════════ */}
      {step === 'upload' && (
        <Paper p="xl" radius="md" withBorder>
          <Stack gap="lg" style={{ alignItems: 'center' }}>
            <IconUpload size={48} color="#4B3FFF" style={{ opacity: 0.7 }} />
            <div style={{ textAlign: 'center' }}>
              <Title order={4}>Upload Files</Title>
              <Text size="sm" c="dimmed" mt={4} style={{ maxWidth: 520 }}>
                Drop any combination of files. The system classifies each one and routes it
                to the correct handler — invoices, spreadsheets, PDFs, photos, certificates.
              </Text>
            </div>

            {/* Mode toggle */}
            <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              {(['individual', 'bulk'] as const).map(m => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  style={{
                    padding: '7px 22px',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: 'pointer',
                    border: 'none',
                    background: mode === m ? '#4B3FFF' : 'transparent',
                    color: mode === m ? '#fff' : '#64748b',
                    transition: 'all 150ms ease',
                    textTransform: 'capitalize',
                  }}
                >
                  {m === 'individual' ? 'Individual' : 'Bulk'}
                </button>
              ))}
            </div>
            {mode === 'bulk' && (
              <Text size="xs" c="dimmed" style={{ marginTop: -8 }}>
                Concurrent processing · auto-approve high-confidence · table review
              </Text>
            )}

            {/* Resume banner */}
            {pendingCount > 0 && (
              <Paper p="sm" radius="md" withBorder style={{ background: '#fffbeb', borderColor: '#fbbf24', maxWidth: 600, width: '100%' }}>
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="xs">
                    <IconAlertTriangle size={16} color="#d97706" />
                    <Text size="sm" fw={600} style={{ color: '#92400e' }}>
                      {pendingCount} document{pendingCount !== 1 ? 's' : ''} pending review from your last session
                    </Text>
                  </Group>
                  <Button size="xs" onClick={resumeSession}>Resume</Button>
                </Group>
              </Paper>
            )}

            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              style={{
                width: '100%',
                maxWidth: 600,
                padding: '48px 24px',
                border: `2px dashed ${dragOver ? '#4B3FFF' : '#d1d5db'}`,
                borderRadius: 12,
                background: dragOver ? '#f5f3ff' : '#fafafa',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 150ms ease',
              }}
            >
              <IconUpload size={32} color={dragOver ? '#4B3FFF' : '#94a3b8'} />
              <Text size="sm" fw={600} mt="sm">
                {dragOver ? 'Drop files here' : 'Drag & drop files or click to browse'}
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                Any file type — .pdf, .xlsx, .xls, .csv, .jpg, .png, and more
              </Text>
              <input
                ref={fileRef}
                type="file"
                multiple
                onChange={handleFileInput}
                style={{ display: 'none' }}
              />
            </div>

            <Text size="xs" c="dimmed" style={{ maxWidth: 520, textAlign: 'center' }}>
              Supports: TDMS exports, truck certs, invoices, transaction details, ticket images,
              reconciliation reports, personnel rosters, vendor lists, photos, and any other document.
            </Text>
          </Stack>
        </Paper>
      )}

      {/* Data Utilities section removed — road matching not needed for Tampa cost recovery */}
      {false && step === 'upload' && (
        <Paper p="md" radius="md" withBorder style={{ background: '#fafbfc' }} />
      )}

      {/* ═══════════════ STEP 2: CLASSIFY + CONFIGURE ═══════════════ */}
      {step === 'classify' && (
        <>
          {/* Summary badges */}
          <Paper p="sm" radius="md" withBorder style={{ background: '#f0f4ff' }}>
            <Group gap="sm" wrap="wrap">
              <Text size="sm" fw={600}>{files.length} files classified:</Text>
              {Object.entries(typeCounts).sort(([a], [b]) => {
                const order = Object.keys(DOC_TYPES);
                return order.indexOf(a) - order.indexOf(b);
              }).map(([type, count]) => (
                <Badge key={type} size="sm" color={DOC_TYPES[type as DocType]?.color || '#94a3b8'} variant="light">
                  {count} {DOC_TYPES[type as DocType]?.label || type}
                </Badge>
              ))}
            </Group>
          </Paper>

          {/* Warning banner for low-confidence / unknown files */}
          {(() => {
            const lowConfCount = files.filter(f => f.confidence === 'low' && getEffType(f) !== 'unknown').length;
            const unknownCount = files.filter(f => getEffType(f) === 'unknown').length;
            if (lowConfCount === 0 && unknownCount === 0) return null;
            return (
              <Paper p="sm" radius="md" withBorder style={{ background: '#fffbeb', borderColor: '#fbbf24' }}>
                <Group gap="xs">
                  <IconAlertTriangle size={16} color="#d97706" />
                  <div>
                    <Text size="sm" fw={600} style={{ color: '#92400e' }}>
                      {[
                        unknownCount > 0 && `${unknownCount} file${unknownCount !== 1 ? 's' : ''} could not be classified`,
                        lowConfCount > 0 && `${lowConfCount} file${lowConfCount !== 1 ? 's' : ''} have low classification confidence`,
                      ].filter(Boolean).join(' · ')}
                    </Text>
                    <Text size="xs" style={{ color: '#92400e', opacity: 0.8 }}>
                      {unknownCount > 0 && 'Unknown files will be skipped. '}
                      {lowConfCount > 0 && 'Low-confidence auto-import files will be skipped — override the type to confirm before processing. '}
                      Invoices and Force Account forms go to human review regardless.
                    </Text>
                  </div>
                </Group>
              </Paper>
            );
          })()}

          {/* File table — position+zIndex so dropdown paints above subsequent sibling Papers */}
          <Paper withBorder radius="md" style={{ position: 'relative', zIndex: 10 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                    <th style={thStyle}>File</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Size</th>
                    <th style={thStyle}>Detected Type</th>
                    <th style={thStyle}>Confidence</th>
                    <th style={{ ...thStyle, minWidth: 180 }}>Override</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((cf, idx) => {
                    const effType = getEffType(cf);
                    const config = DOC_TYPES[effType] || DOC_TYPES.unknown;
                    const rowNeedsAttention = cf.confidence === 'low' || effType === 'unknown';
                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: rowNeedsAttention ? '#fffbeb' : 'transparent' }}>
                        <td style={tdStyle}>
                          <Group gap={6} wrap="nowrap">
                            {getTypeIcon(effType, 14)}
                            <div>
                              <Text size="xs" fw={500} truncate style={{ maxWidth: 280 }}>{cf.file.name}</Text>
                              {cf.preview && (
                                <Text size="xs" c="dimmed" style={{
                                  maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden',
                                  textOverflow: 'ellipsis', fontSize: 10, fontFamily: 'monospace',
                                }}>
                                  {cf.preview.split('\n')[0]}
                                </Text>
                              )}
                            </div>
                          </Group>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <Text size="xs" c="dimmed">{formatFileSize(cf.file.size)}</Text>
                        </td>
                        <td style={tdStyle}>
                          <Badge size="xs" color={config.color} variant="light">
                            {config.label}
                          </Badge>
                        </td>
                        <td style={tdStyle}>
                          <Badge
                            size="xs"
                            variant="outline"
                            color={CONFIDENCE_COLORS[cf.confidence]}
                          >
                            {cf.confidence.toUpperCase()}
                          </Badge>
                        </td>
                        <td style={tdStyle}>
                          <Select
                            size="xs"
                            data={DOC_TYPE_OPTIONS}
                            value={effType}
                            onChange={(v) => v && handleOverride(idx, v)}
                            w={170}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
          </Paper>

          {/* Configuration — always visible, all optional */}
          <Paper p="md" radius="md" withBorder>
            <Text fw={600} size="sm" mb="sm">Configuration <Text span size="xs" c="dimmed">(optional — AI extracts from docs)</Text></Text>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, maxWidth: 700 }}>
              <Select
                label="Contractor"
                placeholder="Auto-detect from files"
                data={contractorOptions}
                value={selectedContractor}
                onChange={setSelectedContractor}
                size="xs"
                searchable
                clearable
              />
              <TextInput
                label="Period Start"
                type="date"
                value={periodStart}
                onChange={e => {
                  const v = e?.currentTarget?.value ?? e?.target?.value ?? '';
                  setPeriodStart(v);
                }}
                size="xs"
              />
              <TextInput
                label="Period End"
                type="date"
                value={periodEnd}
                onChange={e => {
                  const v = e?.currentTarget?.value ?? e?.target?.value ?? '';
                  setPeriodEnd(v);
                }}
                size="xs"
              />
            </div>
          </Paper>

          {/* Processing plan badges */}
          <Paper p="sm" radius="md" withBorder style={{ background: '#fafbff' }}>
            <Text size="xs" fw={600} c="dimmed" mb={6}>Processing Plan:</Text>
            <Group gap={6} wrap="wrap">
              {Object.entries(typeCounts).sort(([a], [b]) => {
                const order = Object.keys(DOC_TYPES);
                return order.indexOf(a) - order.indexOf(b);
              }).map(([type, count]) => {
                const cfg = DOC_TYPES[type as DocType];
                if (!cfg) return null;
                return (
                  <Badge key={type} size="sm" color={cfg.color} variant="light" leftSection={getTypeIcon(type as DocType, 12)}>
                    {cfg.action} ({count})
                  </Badge>
                );
              })}
            </Group>
          </Paper>

          <Group justify="flex-end">
            <Button size="xs" variant="subtle" onClick={resetAll}>Cancel</Button>
            <Button
              size="xs"
              onClick={runProcessing}
              leftSection={<IconPlayerPlay size={14} />}
              disabled={needsContractor && !selectedContractor}
            >
              Process {files.length} Files
            </Button>
          </Group>
        </>
      )}

      {/* ═══════════════ STEP 3: PROCESSING ═══════════════ */}
      {step === 'process' && (
        <Paper p="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Text fw={700} size="sm">Processing Files...</Text>
                <Text size="xs" c="dimmed">{processMessage}</Text>
              </div>
              <Button
                size="xs"
                variant="subtle"
                color="var(--color-error)"
                onClick={() => { abortRef.current = true; }}
                leftSection={<IconX size={14} />}
              >
                Cancel
              </Button>
            </Group>

            <Progress value={processProgress} size="lg" color="#4B3FFF" />

            <Text size="xs" c="dimmed" style={{ textAlign: 'center' }}>
              {processProgress}% complete
            </Text>

            {/* Live status per file */}
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              <Stack gap={4}>
                {files.map((cf, idx) => {
                  const effType = getEffType(cf);
                  const config = DOC_TYPES[effType] || DOC_TYPES.unknown;
                  // Check if this file has a result yet
                  const fileResult = results.find(r => r.file === cf.file.name);
                  return (
                    <Group key={idx} gap="sm" wrap="nowrap" style={{ padding: '4px 8px', borderRadius: 6, background: '#f8fafc' }}>
                      <div style={{ width: 18, textAlign: 'center' }}>
                        {fileResult?.error
                          ? <IconX size={14} color="var(--color-error)" />
                          : fileResult
                            ? <IconCheck size={14} color="var(--color-success)" />
                            : <div style={{
                                width: 8, height: 8, borderRadius: 4,
                                background: '#e2e8f0',
                              }} />
                        }
                      </div>
                      <Text size="xs" truncate style={{ flex: 1, maxWidth: 300 }}>{cf.file.name}</Text>
                      <Badge size="xs" color={config.color} variant="light">{config.label}</Badge>
                    </Group>
                  );
                })}
              </Stack>
            </div>
          </Stack>
        </Paper>
      )}

      {/* ═══════════════ STEP 4: BULK REVIEW ═══════════════ */}
      {step === 'review' && reviewQueue.length > 0 && mode === 'bulk' && (
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <div>
              <Title order={4} c="black">Bulk Review</Title>
              <Text size="sm" c="dimmed">
                {reviewQueue.filter(i => i.status === 'approved').length} approved ·{' '}
                {reviewQueue.filter(i => i.status === 'rejected').length} rejected ·{' '}
                {reviewQueue.filter(i => i.status === 'pending').length} pending of {reviewQueue.length}
              </Text>
            </div>
            <Group gap={8}>
              <Button size="xs" variant="light" onClick={() => {
                setReviewQueue(prev => {
                  const next = prev.map(item => {
                    const autoApprove = item.qualityScore === 'complete'
                      && (item.extractionScore ?? 0) >= 80
                      && item.warnings.length === 0;
                    return autoApprove ? { ...item, status: 'approved' as const } : item;
                  });
                  persistQueue(incidentId, next);
                  return next;
                });
              }}>
                Auto-Approve High Confidence
              </Button>
              <Button size="xs" variant="subtle" onClick={resetAll} leftSection={<IconRefresh size={14} />}>
                Start Over
              </Button>
              <Button size="xs" onClick={commitApproved}
                disabled={reviewQueue.every(i => i.status !== 'approved')}
                leftSection={<IconCheck size={14} />}>
                Commit {reviewQueue.filter(i => i.status === 'approved').length} Approved
              </Button>
            </Group>
          </Group>

          <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
                  <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                    {['Document', 'Vendor / Summary', 'Amount / Records', 'OCR', 'Confidence', 'Flags', 'Status', 'Actions'].map(h => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reviewQueue.map((item, i) => {
                    const isHighConf = item.qualityScore === 'complete' && (item.extractionScore ?? 0) >= 80 && item.warnings.length === 0;
                    const confColor = isHighConf ? 'var(--color-success)' : item.qualityScore === 'reject' ? 'var(--color-error)' : '#d97706';
                    const rowBg = item.status === 'approved' ? '#f0fdf4' : item.status === 'rejected' ? '#fef2f2' : '#fff';
                    return (
                      <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9', background: rowBg }}>
                        <td style={{ ...tdStyle, maxWidth: 200 }}>
                          <Text size="xs" fw={500} truncate style={{ maxWidth: 190 }}>{item.file?.name ?? item.fileName ?? '—'}</Text>
                          <Badge size="xs" variant="light" style={{ marginTop: 2 }}>
                            {item.docType === 'contractor_invoice' ? 'Invoice' : item.docType === 'sub_invoice' ? 'Sub-Invoice' : item.docType === 'force_account' ? 'Force Account' : item.docType === 'sow_work_order' ? 'SOW' : item.docType === 'inspection_report' ? 'Inspection' : 'Supporting'}
                          </Badge>
                        </td>
                        <td style={{ ...tdStyle, maxWidth: 180 }}>
                          <Text size="xs" truncate style={{ maxWidth: 170 }}>{item.vendor || item.summary || '—'}</Text>
                        </td>
                        <td style={{ ...tdStyle, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                          {(item.docType === 'contractor_invoice' || item.docType === 'sub_invoice')
                            ? fmtMoney(parseFloat(item.totalAmount || '0'))
                            : item.docType === 'supporting_evidence'
                              ? '—'
                              : `${item.laborEntries.length}L + ${item.equipEntries.length}E`}
                        </td>
                        <td style={tdStyle}>
                          <Badge size="xs" variant="outline" color={
                            item.ocrConfidence === 'high' ? 'var(--color-success)' :
                            item.ocrConfidence === 'low' ? 'var(--color-error)' : '#C68700'
                          }>
                            {item.extractionMethod === 'pdf-text' ? 'PDF' : 'Vision'} · {item.ocrConfidence ?? '?'}
                          </Badge>
                        </td>
                        <td style={{ ...tdStyle, minWidth: 80 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 44, height: 4, background: '#e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
                              <div style={{ width: `${item.extractionScore ?? 0}%`, height: '100%', background: confColor, borderRadius: 2 }} />
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, color: confColor }}>{item.extractionScore != null ? `${item.extractionScore}%` : '—'}</span>
                          </div>
                        </td>
                        <td style={tdStyle}>
                          {item.warnings.length > 0
                            ? <Badge size="xs" color="#d97706" variant="light" title={item.warnings.join('\n')}>{item.warnings.length} flag{item.warnings.length !== 1 ? 's' : ''}</Badge>
                            : <Text size="xs" c="dimmed">—</Text>}
                        </td>
                        <td style={tdStyle}>
                          <Badge size="xs" style={{
                            background: item.status === 'approved' ? '#dcfce7' : item.status === 'rejected' ? '#fee2e2' : '#f1f5f9',
                            color: item.status === 'approved' ? '#16a34a' : item.status === 'rejected' ? '#dc2626' : '#64748b',
                            border: 'none',
                          }}>{item.status}</Badge>
                        </td>
                        <td style={tdStyle}>
                          <Group gap={4} wrap="nowrap">
                            {item.status === 'pending' ? (<>
                              <button onClick={() => updateReviewItem(item.id, { status: 'approved' })}
                                style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', color: '#16a34a', fontSize: 11, fontWeight: 600 }}>✓</button>
                              <button onClick={() => updateReviewItem(item.id, { status: 'rejected' })}
                                style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', color: '#dc2626', fontSize: 11, fontWeight: 600 }}>✕</button>
                            </>) : (
                              <button onClick={() => updateReviewItem(item.id, { status: 'pending' })}
                                style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', color: '#64748b', fontSize: 11 }}>Undo</button>
                            )}
                            <button onClick={() => { setReviewIdx(i); setMode('individual'); }}
                              style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', color: '#4B3FFF', fontSize: 11 }}>Detail</button>
                          </Group>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Paper>
        </Stack>
      )}

      {/* ═══════════════ STEP 4: REVIEW (INDIVIDUAL) ═══════════════ */}
      {step === 'review' && reviewQueue.length > 0 && mode === 'individual' && (
        <Stack gap="md">
          {/* Header */}
          <Group justify="space-between" align="center">
            <div>
              <Title order={4} c="black">Review Extracted Data</Title>
              <Text size="sm" c="dimmed">
                {reviewQueue.filter(i => i.status === 'approved').length} approved ·{' '}
                {reviewQueue.filter(i => i.status === 'rejected').length} rejected ·{' '}
                {reviewQueue.filter(i => i.status === 'pending').length} pending of {reviewQueue.length}
              </Text>
            </div>
            <Group gap={8}>
              {reviewQueue.length >= 3 && (
                <Button size="xs" variant="subtle" onClick={() => setMode('bulk')}>
                  Bulk View
                </Button>
              )}
              <Button size="xs" variant="subtle" onClick={resetAll} leftSection={<IconRefresh size={14} />}>
                Start Over
              </Button>
              <Button
                size="xs"
                onClick={commitApproved}
                disabled={reviewQueue.every(i => i.status !== 'approved')}
                leftSection={<IconCheck size={14} />}
              >
                Commit {reviewQueue.filter(i => i.status === 'approved').length} Approved
              </Button>
            </Group>
          </Group>

          {/* Side-by-side: doc list + detail */}
          <div style={{ display: 'grid', gridTemplateColumns: '210px 1fr', gap: 12, alignItems: 'start' }}>
            {/* Left: document list */}
            <Paper withBorder radius="md" p={0} style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
              {reviewQueue.map((it, i) => (
                <div
                  key={it.id}
                  onClick={() => setReviewIdx(i)}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    background: i === reviewIdx ? '#f0f4ff' : undefined,
                    borderLeft: `3px solid ${it.status === 'approved' ? 'var(--color-success)' : it.status === 'rejected' ? 'var(--color-error)' : '#e2e8f0'}`,
                    borderBottom: '1px solid #f1f5f9',
                  }}
                >
                  <Text size="xs" fw={500} truncate style={{ maxWidth: 175 }}>{it.file.name}</Text>
                  <Text size="xs" c="dimmed" truncate style={{ maxWidth: 175 }}>{it.vendor || '—'}</Text>
                  <Badge size="xs" style={{ marginTop: 4,
                    backgroundColor: it.status === 'approved' ? '#03773018' : it.status === 'rejected' ? '#D3342318' : '#64748b18',
                    color: it.status === 'approved' ? 'var(--color-success)' : it.status === 'rejected' ? 'var(--color-error)' : '#64748b',
                    border: `1px solid ${it.status === 'approved' ? '#03773040' : it.status === 'rejected' ? '#D3342340' : '#64748b30'}`,
                    textTransform: 'none',
                  }}>
                    {it.status}
                  </Badge>
                </div>
              ))}
            </Paper>

            {/* Right: detail panel */}
            {reviewQueue[reviewIdx] && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'start' }}>
                {/* Left column — PDF viewer */}
                <Paper withBorder radius="md" style={{ height: 'calc(100vh - 290px)', background: '#f8fafc', position: 'sticky', top: 0 }}>
                  {reviewPdfUrl ? (
                    <iframe src={reviewPdfUrl} style={{ width: '100%', height: '100%', border: 0 }} title="Invoice preview" />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Text size="sm" c="dimmed">No preview available</Text>
                    </div>
                  )}
                </Paper>

                {/* Right column — badges + fields + actions */}
                <Stack gap={12} style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 290px)', paddingRight: 2 }}>
                {/* Low extraction confidence warning */}
                {((reviewQueue[reviewIdx].extractionScore ?? 100) < 60 || reviewQueue[reviewIdx].ocrConfidence === 'low') && (
                  <Paper p="sm" radius="md" withBorder style={{ background: '#fffbeb', borderColor: '#fbbf24' }}>
                    <Group gap="xs" wrap="nowrap">
                      <IconAlertTriangle size={14} color="#d97706" style={{ flexShrink: 0 }} />
                      <Text size="xs" fw={600} style={{ color: '#92400e' }}>
                        Low extraction confidence — verify all fields carefully before approving.
                        {reviewQueue[reviewIdx].extractionScore != null ? ` (${reviewQueue[reviewIdx].extractionScore}% confidence)` : ''}
                      </Text>
                    </Group>
                  </Paper>
                )}

                {/* Extraction method + quality badges */}
                <Group gap={8} wrap="wrap">
                  <Badge size="xs" style={{
                    backgroundColor: reviewQueue[reviewIdx].extractionMethod === 'pdf-text' ? '#0285C418' : '#A627FF18',
                    color: reviewQueue[reviewIdx].extractionMethod === 'pdf-text' ? '#0285C4' : '#A627FF',
                    border: `1px solid ${reviewQueue[reviewIdx].extractionMethod === 'pdf-text' ? '#0285C440' : '#A627FF40'}`,
                  }}>
                    {reviewQueue[reviewIdx].extractionMethod === 'pdf-text' ? 'PDF Text (digital)' : 'Claude Vision (scanned)'}
                  </Badge>
                  <Badge size="xs" style={{
                    backgroundColor: reviewQueue[reviewIdx].qualityScore === 'complete' ? '#03773018' : reviewQueue[reviewIdx].qualityScore === 'reject' ? '#D3342318' : '#88560418',
                    color: reviewQueue[reviewIdx].qualityScore === 'complete' ? 'var(--color-success)' : reviewQueue[reviewIdx].qualityScore === 'reject' ? 'var(--color-error)' : 'var(--color-warning)',
                    border: `1px solid ${reviewQueue[reviewIdx].qualityScore === 'complete' ? '#03773040' : reviewQueue[reviewIdx].qualityScore === 'reject' ? '#D3342340' : '#88560440'}`,
                  }}>
                    Quality: {reviewQueue[reviewIdx].qualityScore}
                  </Badge>
                  {reviewQueue[reviewIdx].ocrConfidence && (
                    <Badge size="xs" style={{
                      backgroundColor: reviewQueue[reviewIdx].ocrConfidence === 'high' ? '#03773018' : reviewQueue[reviewIdx].ocrConfidence === 'low' ? '#D3342318' : '#88560418',
                      color: reviewQueue[reviewIdx].ocrConfidence === 'high' ? 'var(--color-success)' : reviewQueue[reviewIdx].ocrConfidence === 'low' ? 'var(--color-error)' : 'var(--color-warning)',
                      border: `1px solid ${reviewQueue[reviewIdx].ocrConfidence === 'high' ? '#03773040' : reviewQueue[reviewIdx].ocrConfidence === 'low' ? '#D3342340' : '#88560440'}`,
                    }}>
                      OCR: {reviewQueue[reviewIdx].ocrConfidence}
                    </Badge>
                  )}
                  {reviewQueue[reviewIdx].extractionScore != null && (
                    <Badge size="xs" style={{
                      backgroundColor: (reviewQueue[reviewIdx].extractionScore ?? 0) >= 80 ? '#03773018' : (reviewQueue[reviewIdx].extractionScore ?? 0) >= 50 ? '#88560418' : '#D3342318',
                      color: (reviewQueue[reviewIdx].extractionScore ?? 0) >= 80 ? 'var(--color-success)' : (reviewQueue[reviewIdx].extractionScore ?? 0) >= 50 ? 'var(--color-warning)' : 'var(--color-error)',
                      border: 'none',
                    }}>
                      Confidence: {reviewQueue[reviewIdx].extractionScore}%
                    </Badge>
                  )}
                  {reviewQueue[reviewIdx].warnings.map((w, wi) => (
                    <Badge key={wi} size="xs" style={{ backgroundColor: '#88560410', color: '#885604', border: '1px solid #88560430' }}>
                      <Group gap={4} wrap="nowrap"><IconAlertTriangle size={10} />{w}</Group>
                    </Badge>
                  ))}
                </Group>

                {/* ── Invoice fields + line items ── */}
                {(reviewQueue[reviewIdx].docType === 'contractor_invoice' || reviewQueue[reviewIdx].docType === 'sub_invoice') && (<>
                  <SimpleGrid cols={2} spacing={10}>
                    <TextInput
                      label="Vendor / Company"
                      size="xs"
                      value={reviewQueue[reviewIdx].vendor}
                      onChange={e => updateReviewItem(reviewQueue[reviewIdx].id, { vendor: e?.currentTarget?.value ?? e?.target?.value ?? '' })}
                    />
                    <TextInput
                      label="Invoice Number"
                      size="xs"
                      value={reviewQueue[reviewIdx].invoiceNumber}
                      onChange={e => updateReviewItem(reviewQueue[reviewIdx].id, { invoiceNumber: e?.currentTarget?.value ?? e?.target?.value ?? '' })}
                    />
                    <TextInput
                      label="Invoice Date"
                      type="date"
                      size="xs"
                      value={reviewQueue[reviewIdx].invoiceDate}
                      onChange={e => updateReviewItem(reviewQueue[reviewIdx].id, { invoiceDate: e?.currentTarget?.value ?? e?.target?.value ?? '' })}
                    />
                    <div>
                      <TextInput
                        label="Total Amount"
                        size="xs"
                        value={reviewQueue[reviewIdx].totalAmount}
                        onChange={e => {
                          const raw = (e?.currentTarget?.value ?? e?.target?.value ?? '').replace(/[$,]/g, '');
                          updateReviewItem(reviewQueue[reviewIdx].id, { totalAmount: raw });
                        }}
                      />
                      {reviewQueue[reviewIdx].totalAmount && parseFloat(reviewQueue[reviewIdx].totalAmount) > 0 && (
                        <Text size="xs" c="dimmed" mt={2} style={{ fontFamily: 'monospace' }}>
                          = {fmtMoney(parseFloat(reviewQueue[reviewIdx].totalAmount) || 0)}
                        </Text>
                      )}
                    </div>
                  </SimpleGrid>

                  {/* Line Items Editor */}
                  <div>
                    <Group justify="space-between" mb={6}>
                      <Text size="xs" fw={600}>
                        Line Items{reviewQueue[reviewIdx].lineItems?.length ? ` (${reviewQueue[reviewIdx].lineItems.length})` : ''}
                      </Text>
                      <button
                        onClick={() => updateReviewItem(reviewQueue[reviewIdx].id, {
                          lineItems: [...(reviewQueue[reviewIdx].lineItems || []), { description: '', quantity: 1, unit: '', rate: '', amount: '' }],
                        })}
                        style={{ fontSize: 11, color: '#4B3FFF', background: 'none', border: '1px solid #4B3FFF40', borderRadius: 4, padding: '2px 10px', cursor: 'pointer' }}
                      >
                        + Add Row
                      </button>
                    </Group>
                    <Paper withBorder radius="sm" p={0} style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                          <tr style={{ background: '#f8fafc' }}>
                            {['Description', 'Qty', 'Unit', 'Rate ($)', 'Amount ($)', ''].map((h, hi) => (
                              <th key={hi} style={{
                                padding: '6px 8px', fontWeight: 600, color: '#64748b',
                                borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap',
                                textAlign: hi >= 3 ? 'right' : 'left',
                              }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {!(reviewQueue[reviewIdx].lineItems?.length) ? (
                            <tr>
                              <td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 11 }}>
                                No line items extracted — add rows manually or check AI quality
                              </td>
                            </tr>
                          ) : (reviewQueue[reviewIdx].lineItems as any[]).map((li_item: any, li: number) => {
                            const cellInput = (field: string, val: any, opts?: { right?: boolean; currency?: boolean }) => (
                              <input
                                value={opts?.currency ? String(val ?? '').replace(/[$,]/g, '') : String(val ?? '')}
                                onChange={e => {
                                  const items = [...(reviewQueue[reviewIdx].lineItems as any[])];
                                  const v = opts?.currency ? e.target.value.replace(/[$,]/g, '') : e.target.value;
                                  items[li] = { ...items[li], [field]: v };
                                  updateReviewItem(reviewQueue[reviewIdx].id, { lineItems: items });
                                }}
                                style={{
                                  width: '100%', border: '1px solid transparent', borderRadius: 3,
                                  padding: '3px 6px', fontSize: 11, background: 'transparent', outline: 'none',
                                  textAlign: opts?.right ? 'right' : 'left',
                                }}
                                onFocus={e => (e.target.style.borderColor = '#4B3FFF')}
                                onBlur={e => (e.target.style.borderColor = 'transparent')}
                              />
                            );
                            return (
                              <tr key={li} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '2px 4px', minWidth: 180 }}>{cellInput('description', li_item.description)}</td>
                                <td style={{ padding: '2px 4px', width: 52 }}>{cellInput('quantity', li_item.quantity, { right: true })}</td>
                                <td style={{ padding: '2px 4px', width: 56 }}>{cellInput('unit', li_item.unit)}</td>
                                <td style={{ padding: '2px 4px', width: 100 }}>{cellInput('rate', li_item.rate, { right: true, currency: true })}</td>
                                <td style={{ padding: '2px 4px', width: 110 }}>
                                  <input
                                    value={String(li_item.amount ?? '').replace(/[$,]/g, '')}
                                    onChange={e => {
                                      const items = [...(reviewQueue[reviewIdx].lineItems as any[])];
                                      items[li] = { ...items[li], amount: e.target.value.replace(/[$,]/g, '') };
                                      updateReviewItem(reviewQueue[reviewIdx].id, { lineItems: items });
                                    }}
                                    style={{ width: '100%', border: '1px solid transparent', borderRadius: 3, padding: '3px 6px', fontSize: 11, textAlign: 'right', fontWeight: 600, background: 'transparent', outline: 'none' }}
                                    onFocus={e => (e.target.style.borderColor = '#4B3FFF')}
                                    onBlur={e => (e.target.style.borderColor = 'transparent')}
                                  />
                                </td>
                                <td style={{ padding: '2px 4px', width: 24, textAlign: 'center' }}>
                                  <button
                                    onClick={() => {
                                      const items = (reviewQueue[reviewIdx].lineItems as any[]).filter((_: any, i: number) => i !== li);
                                      updateReviewItem(reviewQueue[reviewIdx].id, { lineItems: items });
                                    }}
                                    title="Delete row"
                                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: 16, lineHeight: 1, padding: '0 2px' }}
                                    onMouseEnter={e => (e.currentTarget.style.color = 'var(--color-error)')}
                                    onMouseLeave={e => (e.currentTarget.style.color = '#cbd5e1')}
                                  >×</button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        {(reviewQueue[reviewIdx].lineItems?.length ?? 0) > 0 && (() => {
                          const lineTotal = (reviewQueue[reviewIdx].lineItems as any[]).reduce((s: number, it: any) =>
                            s + (parseFloat(String(it.amount ?? '0').replace(/[$,]/g, '')) || 0), 0);
                          const headerTotal = parseFloat(String(reviewQueue[reviewIdx].totalAmount).replace(/[$,]/g, '')) || 0;
                          const diff = Math.abs(lineTotal - headerTotal);
                          const mismatch = diff > 0.01 && headerTotal > 0;
                          return (
                            <tfoot>
                              <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                                <td colSpan={4} style={{ padding: '6px 8px', textAlign: 'right', fontSize: 11, color: '#64748b', fontWeight: 500 }}>
                                  Line items total
                                </td>
                                <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: mismatch ? 'var(--color-error)' : '#1e293b' }}>
                                  {fmtMoney(lineTotal)}
                                </td>
                                <td />
                              </tr>
                              {mismatch && (
                                <tr>
                                  <td colSpan={6} style={{ padding: '3px 8px 6px', fontSize: 10, color: 'var(--color-error)', textAlign: 'right' }}>
                                    ⚠ Header total {fmtMoney(headerTotal)} doesn't match line items {fmtMoney(lineTotal)} — discrepancy of {fmtMoney(diff)}
                                  </td>
                                </tr>
                              )}
                            </tfoot>
                          );
                        })()}
                      </table>
                    </Paper>
                  </div>

                  {/* Sub-invoice: link to prime contractor invoice */}
                  {reviewQueue[reviewIdx].docType === 'sub_invoice' && (
                    <div>
                      <Text size="xs" fw={600} mb={4}>Prime Contractor Invoice</Text>
                      <select
                        value={reviewQueue[reviewIdx].primeInvoiceId ?? ''}
                        onChange={e => updateReviewItem(reviewQueue[reviewIdx].id, { primeInvoiceId: e.target.value })}
                        style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 6 }}
                      >
                        <option value="">— Select prime invoice —</option>
                        {incidentInvoices.map(inv => (
                          <option key={inv.id} value={inv.id}>
                            #{inv.number} · {inv.vendor} · {fmtMoney(inv.amount)}
                          </option>
                        ))}
                      </select>
                      {!reviewQueue[reviewIdx].primeInvoiceId && (
                        <Text size="xs" c="orange" mt={4}>Select the prime contractor invoice this sub-invoice belongs to.</Text>
                      )}
                    </div>
                  )}
                </>)}

                {/* ── Supporting Evidence: invoice link + sub-type selector ── */}
                {reviewQueue[reviewIdx].docType === 'supporting_evidence' && (
                  <Stack gap={10}>
                    {reviewQueue[reviewIdx].evidenceSummary && (
                      <Paper withBorder p="xs" radius="sm" style={{ background: '#f0f9ff' }}>
                        <Text size="xs" c="dimmed" fw={500} mb={2}>AI Summary</Text>
                        <Text size="xs">{reviewQueue[reviewIdx].evidenceSummary}</Text>
                      </Paper>
                    )}
                    <div>
                      <Text size="xs" fw={600} mb={4}>Which invoice does this document support?</Text>
                      <select
                        value={reviewQueue[reviewIdx].selectedInvoiceId ?? ''}
                        onChange={e => updateReviewItem(reviewQueue[reviewIdx].id, { selectedInvoiceId: e.target.value })}
                        style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 6 }}
                      >
                        <option value="">— No specific invoice —</option>
                        {incidentInvoices.map(inv => {
                          const cName = contractors.find(c => String(c.id) === inv.contractorId)?.companyName || inv.vendor;
                          const period = inv.periodStart ? `${inv.periodStart} — ${inv.periodEnd}` : '';
                          return (
                            <option key={inv.id} value={inv.id}>
                              #{inv.number} · {cName}{period ? ` · ${period}` : ''} · {fmtMoney(inv.amount)}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div>
                      <Text size="xs" fw={600} mb={4}>Document sub-type</Text>
                      <select
                        value={reviewQueue[reviewIdx].selectedSubType ?? ''}
                        onChange={e => updateReviewItem(reviewQueue[reviewIdx].id, { selectedSubType: e.target.value })}
                        style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 6 }}
                      >
                        <option value="">— Select type —</option>
                        <option value="photo">Field Photo</option>
                        <option value="monitoring_log">Monitor Daily Report</option>
                        <option value="scale_ticket">Scale Ticket</option>
                        <option value="work_order">Work Order</option>
                        <option value="receipt">Receipt</option>
                        <option value="permit">Permit / Authorization</option>
                        <option value="roster">Personnel Roster</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </Stack>
                )}

                {/* ── SOW / Inspection review: invoice picker ── */}
                {(reviewQueue[reviewIdx].docType === 'sow_work_order' || reviewQueue[reviewIdx].docType === 'inspection_report') && (
                  <Stack gap={10}>
                    <Paper withBorder p="xs" radius="sm" style={{ background: reviewQueue[reviewIdx].docType === 'sow_work_order' ? '#faf5ff' : '#ecfdf5' }}>
                      <Text size="xs" c="dimmed" fw={500} mb={2}>
                        {reviewQueue[reviewIdx].docType === 'sow_work_order' ? 'Scope of Work / Work Order' : 'Inspection / Completion Sign-Off'}
                      </Text>
                      <Text size="xs">{reviewQueue[reviewIdx].fileName}</Text>
                      {reviewQueue[reviewIdx].vendor && (
                        <Text size="xs" fw={600} mt={4}>Matched contractor: {reviewQueue[reviewIdx].vendor}</Text>
                      )}
                      {reviewQueue[reviewIdx].invoiceDate && (
                        <Text size="xs" mt={2} c="#4B3FFF">Date extracted from filename: {reviewQueue[reviewIdx].invoiceDate}</Text>
                      )}
                    </Paper>
                    <div>
                      <Text size="xs" fw={600} mb={4}>Link to invoice (auto-satisfies checklist item)</Text>
                      <select
                        value={reviewQueue[reviewIdx].selectedInvoiceId ?? ''}
                        onChange={e => updateReviewItem(reviewQueue[reviewIdx].id, { selectedInvoiceId: e.target.value })}
                        style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 6 }}
                      >
                        <option value="">— Select invoice —</option>
                        {incidentInvoices.map(inv => {
                          const cName = contractors.find(c => String(c.id) === inv.contractorId)?.companyName || inv.vendor;
                          const period = inv.periodStart ? `${inv.periodStart} — ${inv.periodEnd}` : '';
                          return (
                            <option key={inv.id} value={inv.id}>
                              #{inv.number} · {cName}{period ? ` · ${period}` : ''} · {fmtMoney(inv.amount)}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </Stack>
                )}

                {/* ── Force Account fields: labor + equipment entry tables ── */}
                {reviewQueue[reviewIdx].docType === 'force_account' && (() => {
                  const curItem = reviewQueue[reviewIdx];
                  const faCell = (entries: any[], rowIdx: number, field: string, updateFn: (entries: any[]) => void, opts?: { right?: boolean; width?: number }) => (
                    <input
                      value={String(entries[rowIdx][field] ?? '')}
                      onChange={e => {
                        const updated = [...entries];
                        updated[rowIdx] = { ...updated[rowIdx], [field]: e.target.value };
                        updateFn(updated);
                      }}
                      style={{
                        width: '100%', border: '1px solid transparent', borderRadius: 3,
                        padding: '3px 6px', fontSize: 11, background: 'transparent', outline: 'none',
                        textAlign: opts?.right ? 'right' : 'left',
                        minWidth: opts?.width ?? undefined,
                      }}
                      onFocus={e => (e.target.style.borderColor = '#4B3FFF')}
                      onBlur={e => (e.target.style.borderColor = 'transparent')}
                    />
                  );
                  const setLabor = (entries: any[]) => updateReviewItem(curItem.id, { laborEntries: entries });
                  const setEquip = (entries: any[]) => updateReviewItem(curItem.id, { equipEntries: entries });
                  return (
                    <Stack gap={12}>
                      {/* Labor table */}
                      <div>
                        <Group justify="space-between" mb={6}>
                          <Text size="xs" fw={600}>Labor Entries ({curItem.laborEntries.length})</Text>
                          <button
                            onClick={() => setLabor([...curItem.laborEntries, { employeeName: '', employeeId: '', ercDistrict: '', department: '', classification: '', dateWorked: '', hoursWorked: '', hoursOT: '' }])}
                            style={{ fontSize: 11, color: '#0E7490', background: 'none', border: '1px solid #0E749040', borderRadius: 4, padding: '2px 10px', cursor: 'pointer' }}
                          >
                            + Add Labor Row
                          </button>
                        </Group>
                        <Paper withBorder radius="sm" p={0} style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                            <thead>
                              <tr style={{ background: '#f8fafc' }}>
                                {['Date', 'ERC/District', 'Employee Name', 'Department', 'Classification', 'Reg Hrs', 'OT Hrs', 'Docs', ''].map((h, hi) => (
                                  <th key={hi} style={{
                                    padding: '6px 8px', fontWeight: 600, color: '#64748b',
                                    borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap',
                                    textAlign: hi >= 5 && hi <= 6 ? 'right' : 'left',
                                  }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {curItem.laborEntries.length === 0 ? (
                                <tr>
                                  <td colSpan={9} style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 11 }}>
                                    No labor entries extracted — add rows manually or check AI quality
                                  </td>
                                </tr>
                              ) : curItem.laborEntries.map((row: any, ri: number) => (
                                <tr key={ri} style={{ borderBottom: '1px solid #f1f5f9', background: ri % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                  <td style={{ padding: '2px 4px', width: 90 }}>{faCell(curItem.laborEntries, ri, 'dateWorked', setLabor)}</td>
                                  <td style={{ padding: '2px 4px', width: 90 }}>{faCell(curItem.laborEntries, ri, 'ercDistrict', setLabor)}</td>
                                  <td style={{ padding: '2px 4px', minWidth: 140 }}>{faCell(curItem.laborEntries, ri, 'employeeName', setLabor, { width: 140 })}</td>
                                  <td style={{ padding: '2px 4px', width: 100 }}>{faCell(curItem.laborEntries, ri, 'department', setLabor)}</td>
                                  <td style={{ padding: '2px 4px', width: 110 }}>{faCell(curItem.laborEntries, ri, 'classification', setLabor)}</td>
                                  <td style={{ padding: '2px 4px', width: 64 }}>{faCell(curItem.laborEntries, ri, 'hoursWorked', setLabor, { right: true })}</td>
                                  <td style={{ padding: '2px 4px', width: 64 }}>{faCell(curItem.laborEntries, ri, 'hoursOT', setLabor, { right: true })}</td>
                                  <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>
                                    <Group gap={3}>
                                      {(['timesheetDocId', 'payrollDocId'] as const).map(slot => {
                                        const label = slot === 'timesheetDocId' ? 'TS' : 'PR';
                                        const hasDoc = !!row[slot];
                                        const saveKey = `${ri}:${slot}`;
                                        return (
                                          <label key={slot} title={slot === 'timesheetDocId' ? 'Timesheet' : 'Payroll doc'} style={{ cursor: 'pointer' }}>
                                            <input type="file" accept=".pdf,image/*" style={{ display: 'none' }}
                                              onChange={async e => {
                                                const f = e.target.files?.[0]; if (!f) return;
                                                setFaLaborDocSaving(saveKey);
                                                try {
                                                  await saveFaDocToEntry(curItem.id, curItem.laborEntries, ri, slot, f, setLabor);
                                                  notifications.show({ message: `${label} attached`, color: 'var(--color-success)' });
                                                } catch {
                                                  notifications.show({ message: 'Upload failed', color: 'var(--color-error)' });
                                                } finally {
                                                  setFaLaborDocSaving(null);
                                                  e.target.value = '';
                                                }
                                              }} />
                                            <Badge size="xs" style={{
                                              background: hasDoc ? '#dcfce7' : '#fef3c7',
                                              color: hasDoc ? '#166534' : '#92400e',
                                              border: `1px solid ${hasDoc ? '#bbf7d0' : '#fde68a'}`,
                                              cursor: 'pointer',
                                              fontWeight: 600,
                                            }}>
                                              {faLaborDocSaving === saveKey ? '…' : (hasDoc ? <><IconPaperclip size={9} style={{ verticalAlign: 'middle' }} /> {label}</> : label)}
                                            </Badge>
                                          </label>
                                        );
                                      })}
                                    </Group>
                                  </td>
                                  <td style={{ padding: '2px 4px', width: 24, textAlign: 'center' }}>
                                    <button
                                      onClick={() => setLabor(curItem.laborEntries.filter((_: any, i: number) => i !== ri))}
                                      title="Delete row"
                                      style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: 16, lineHeight: 1, padding: '0 2px' }}
                                      onMouseEnter={e => (e.currentTarget.style.color = 'var(--color-error)')}
                                      onMouseLeave={e => (e.currentTarget.style.color = '#cbd5e1')}
                                    >×</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Paper>
                      </div>

                      {/* Equipment table */}
                      <div>
                        <Group justify="space-between" mb={6}>
                          <Text size="xs" fw={600}>Equipment Entries ({curItem.equipEntries.length})</Text>
                          <button
                            onClick={() => setEquip([...curItem.equipEntries, { fleetNumber: '', description: '', dateWorked: '', hoursUsed: '', ercDistrict: '' }])}
                            style={{ fontSize: 11, color: '#0E7490', background: 'none', border: '1px solid #0E749040', borderRadius: 4, padding: '2px 10px', cursor: 'pointer' }}
                          >
                            + Add Equipment Row
                          </button>
                        </Group>
                        <Paper withBorder radius="sm" p={0} style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                            <thead>
                              <tr style={{ background: '#f8fafc' }}>
                                {['Fleet #', 'Description', 'ERC/District', 'Date', 'Hours', 'Rate Doc', ''].map((h, hi) => (
                                  <th key={hi} style={{
                                    padding: '6px 8px', fontWeight: 600, color: '#64748b',
                                    borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap',
                                    textAlign: hi === 4 ? 'right' : 'left',
                                  }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {curItem.equipEntries.length === 0 ? (
                                <tr>
                                  <td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 11 }}>
                                    No equipment entries extracted — add rows manually or check AI quality
                                  </td>
                                </tr>
                              ) : curItem.equipEntries.map((row: any, ri: number) => (
                                <tr key={ri} style={{ borderBottom: '1px solid #f1f5f9', background: ri % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                  <td style={{ padding: '2px 4px', width: 80 }}>{faCell(curItem.equipEntries, ri, 'fleetNumber', setEquip)}</td>
                                  <td style={{ padding: '2px 4px', minWidth: 160 }}>{faCell(curItem.equipEntries, ri, 'description', setEquip, { width: 160 })}</td>
                                  <td style={{ padding: '2px 4px', width: 90 }}>{faCell(curItem.equipEntries, ri, 'ercDistrict', setEquip)}</td>
                                  <td style={{ padding: '2px 4px', width: 90 }}>{faCell(curItem.equipEntries, ri, 'dateWorked', setEquip)}</td>
                                  <td style={{ padding: '2px 4px', width: 64 }}>{faCell(curItem.equipEntries, ri, 'hoursUsed', setEquip, { right: true })}</td>
                                  <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>
                                    {(() => {
                                      const hasDoc = !!row.femaRateDocId;
                                      const saveKey = `equip-${ri}:femaRateDocId`;
                                      return (
                                        <label title="FEMA rate schedule doc" style={{ cursor: 'pointer' }}>
                                          <input type="file" accept=".pdf,image/*" style={{ display: 'none' }}
                                            onChange={async e => {
                                              const f = e.target.files?.[0]; if (!f) return;
                                              setFaLaborDocSaving(saveKey);
                                              try {
                                                await saveFaDocToEntry(curItem.id, curItem.equipEntries, ri, 'femaRateDocId', f, setEquip);
                                                notifications.show({ message: 'Rate doc attached', color: 'var(--color-success)' });
                                              } catch {
                                                notifications.show({ message: 'Upload failed', color: 'var(--color-error)' });
                                              } finally {
                                                setFaLaborDocSaving(null);
                                                e.target.value = '';
                                              }
                                            }} />
                                          <Badge size="xs" style={{
                                            background: hasDoc ? '#dcfce7' : '#fef3c7',
                                            color: hasDoc ? '#166534' : '#92400e',
                                            border: `1px solid ${hasDoc ? '#bbf7d0' : '#fde68a'}`,
                                            cursor: 'pointer',
                                            fontWeight: 600,
                                          }}>
                                            {faLaborDocSaving === saveKey ? '…' : (hasDoc ? <><IconPaperclip size={9} style={{ verticalAlign: 'middle' }} /> Rate</> : 'Rate')}
                                          </Badge>
                                        </label>
                                      );
                                    })()}
                                  </td>
                                  <td style={{ padding: '2px 4px', width: 24, textAlign: 'center' }}>
                                    <button
                                      onClick={() => setEquip(curItem.equipEntries.filter((_: any, i: number) => i !== ri))}
                                      title="Delete row"
                                      style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: 16, lineHeight: 1, padding: '0 2px' }}
                                      onMouseEnter={e => (e.currentTarget.style.color = 'var(--color-error)')}
                                      onMouseLeave={e => (e.currentTarget.style.color = '#cbd5e1')}
                                    >×</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Paper>
                      </div>
                    </Stack>
                  );
                })()}

                {reviewQueue[reviewIdx].summary && (
                  <Paper withBorder p="xs" radius="sm" style={{ background: '#f8fafc' }}>
                    <Text size="xs" c="dimmed">{reviewQueue[reviewIdx].summary}</Text>
                  </Paper>
                )}

                {/* Approve / Reject / Navigate */}
                <Group gap={8}>
                  <Button
                    size="xs"
                    leftSection={<IconCheck size={14} />}
                    style={reviewQueue[reviewIdx].status === 'approved'
                      ? { backgroundColor: 'var(--color-success)', color: '#fff' }
                      : { backgroundColor: '#03773018', color: 'var(--color-success)', border: '1px solid #03773040' }
                    }
                    onClick={() => {
                      updateReviewItem(reviewQueue[reviewIdx].id, { status: 'approved' });
                      if (reviewIdx < reviewQueue.length - 1) setReviewIdx(prev => prev + 1);
                    }}
                  >
                    {reviewQueue[reviewIdx].status === 'approved' ? 'Approved ✓' : 'Approve'}
                  </Button>
                  <Button
                    size="xs"
                    leftSection={<IconX size={14} />}
                    style={reviewQueue[reviewIdx].status === 'rejected'
                      ? { backgroundColor: 'var(--color-error)', color: '#fff' }
                      : { backgroundColor: '#D3342318', color: 'var(--color-error)', border: '1px solid #D3342340' }
                    }
                    onClick={() => {
                      updateReviewItem(reviewQueue[reviewIdx].id, { status: 'rejected' });
                      if (reviewIdx < reviewQueue.length - 1) setReviewIdx(prev => prev + 1);
                    }}
                  >
                    {reviewQueue[reviewIdx].status === 'rejected' ? 'Rejected ✗' : 'Reject'}
                  </Button>
                  <div style={{ flex: 1 }} />
                  {reviewIdx > 0 && (
                    <Button size="xs" variant="subtle" leftSection={<IconChevronLeft size={14} />} onClick={() => setReviewIdx(prev => prev - 1)}>
                      Prev
                    </Button>
                  )}
                  {reviewIdx < reviewQueue.length - 1 && (
                    <Button size="xs" variant="subtle" rightSection={<IconChevronRight size={14} />} onClick={() => setReviewIdx(prev => prev + 1)}>
                      Next
                    </Button>
                  )}
                </Group>
                </Stack>
              </div>
            )}
          </div>
        </Stack>
      )}

      {/* ═══════════════ STEP 5: RESULTS ═══════════════ */}
      {step === 'results' && (
        <>
          <Paper p="lg" radius="md" withBorder style={{ borderColor: 'var(--color-success)', borderWidth: 2 }}>
            <Stack gap="md">
              <Group gap="sm">
                <IconCheck size={28} color="var(--color-success)" />
                <div>
                  <Text fw={700} size="lg">Processing Complete</Text>
                  <Text size="sm" c="dimmed">
                    {results.filter(r => r.recordsCreated > 0).length} files processed,{' '}
                    {results.reduce((s, r) => s + r.recordsCreated, 0)} records created
                    {results.some(r => r.error) && `, ${results.filter(r => r.error).length} errors`}
                  </Text>
                </div>
              </Group>

              {/* Results table */}
              <Paper withBorder radius="md">
                <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 380px)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                        <th style={thStyle}>File</th>
                        <th style={thStyle}>Type</th>
                        <th style={thStyle}>Result</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Records</th>
                        <th style={thStyle}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((r, idx) => {
                        const config = DOC_TYPES[r.type] || DOC_TYPES.unknown;
                        return (
                          <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={tdStyle}>
                              <Group gap={6} wrap="nowrap">
                                {getTypeIcon(r.type, 14)}
                                <Text size="xs" truncate style={{ maxWidth: 250 }}>{r.file}</Text>
                              </Group>
                            </td>
                            <td style={tdStyle}>
                              <Badge size="xs" color={config.color} variant="light">{config.label}</Badge>
                            </td>
                            <td style={tdStyle}>
                              <Text size="xs" c={r.error ? 'red' : undefined}>{r.action}</Text>
                              {r.error && <Text size="xs" c="red">{r.error}</Text>}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                              <Text size="xs" fw={500}>{r.recordsCreated}</Text>
                            </td>
                            <td style={tdStyle}>
                              {r.error
                                ? <Badge size="xs" color="var(--color-error)" variant="light">Error</Badge>
                                : r.recordsCreated > 0
                                  ? <Badge size="xs" color="var(--color-success)" variant="light">Done</Badge>
                                  : <Badge size="xs" color="#94a3b8" variant="light">Skipped</Badge>
                              }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Paper>

              <Divider />

              {/* Summary by type */}
              <div>
                <Text fw={600} size="sm" mb={8}>Summary by Type</Text>
                <Group gap="sm" wrap="wrap">
                  {Object.entries(
                    results.reduce<Record<string, { count: number; records: number; errors: number }>>((acc, r) => {
                      if (!acc[r.type]) acc[r.type] = { count: 0, records: 0, errors: 0 };
                      acc[r.type].count++;
                      acc[r.type].records += r.recordsCreated;
                      if (r.error) acc[r.type].errors++;
                      return acc;
                    }, {})
                  ).map(([type, stats]) => {
                    const cfg = DOC_TYPES[type as DocType] || DOC_TYPES.unknown;
                    return (
                      <Paper key={type} p="sm" radius="sm" withBorder style={{ minWidth: 150 }}>
                        <Group gap={6} mb={4}>
                          {getTypeIcon(type as DocType, 14)}
                          <Text size="xs" fw={600}>{cfg.label}</Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          {stats.count} file{stats.count !== 1 ? 's' : ''} — {stats.records} record{stats.records !== 1 ? 's' : ''}
                          {stats.errors > 0 && <span style={{ color: 'var(--color-error)' }}> ({stats.errors} error{stats.errors !== 1 ? 's' : ''})</span>}
                        </Text>
                      </Paper>
                    );
                  })}
                </Group>
              </div>

              <Divider />

              {/* Navigation */}
              <Text fw={600} size="sm">Next Steps</Text>
              <Group gap="sm" wrap="wrap">
                <Button size="xs" variant="light" onClick={() => navigate('/debris/reconciliation')} leftSection={<IconScale size={14} />}>
                  Reconciliation
                </Button>
                <Button size="xs" variant="light" onClick={() => navigate('/debris/invoices')} leftSection={<IconFileInvoice size={14} />}>
                  Invoices
                </Button>
                <Button size="xs" variant="light" onClick={() => navigate('/debris/evidence')} leftSection={<IconArchive size={14} />}>
                  Documentation Archive
                </Button>
                <Button size="xs" variant="light" onClick={() => navigate('/debris/trucks')} leftSection={<IconTruck size={14} />}>
                  Truck Registry
                </Button>
                {results.some(r => r.type === 'force_account') && (
                  <Button size="xs" variant="light" color="#0E7490" onClick={() => navigate('/debris/forceaccount')} leftSection={<IconUsers size={14} />}>
                    Force Account
                  </Button>
                )}
              </Group>
            </Stack>
          </Paper>
        </>
      )}

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Stack>
  );
}

// ── Utility ─────────────────────────────────────────────────

function readFileAsArray(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(new Uint8Array(e.target?.result as ArrayBuffer));
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}
